// Opt-in CRUD configuration for ModuleListView.  Only module keys present in
// MODULE_CRUD get the New / Edit / Delete / Audit controls; every other module
// keeps rendering as a pure read-only grid (zero behavioural change).
//
// Adding write support to a list screen = add one entry here + a matching
// backend controller (POST/PUT/DELETE) + reader/writer.  See the "set-methods"
// (Settings → Method) implementation for the reference pattern.

export type CrudFieldType =
  | "text"
  | "number"
  | "textarea"
  | "richtext"
  /** An RTF DOCUMENT edited in the G5 (DevExpress 20.1) Word-style ribbon editor —
   *  the shared <DxRichEditor>. The value is RTF and is posted untouched (never
   *  trimmed, so an unedited document compares equal). VIEW mode shows the HTML held
   *  in `htmlKey` instead, the read-only rendering. */
  | "richdoc"
  | "select"
  /** A radio GROUP — every option visible at once, for a short fixed choice
   *  (e.g. Message Library's three Action Types). Reads its options exactly like
   *  'select' (static `options`, or `optionsUrl`), so switching between the two
   *  is a one-word change. Prefer 'select' for anything that can grow. */
  | "radio"
  | "multiselect"
  | "checkbox"
  | "date"
  | "readonly"
  | "file"
  | "assocgrid"
  /** Merge-token panel (legacy XAF "Data Source" side panel — Email Content
   *  Template). Renders `options` as a checklist; the checked set is stored as the
   *  field's comma-joined value, and an "Add" button appends the checked tokens
   *  into the rich-text field named by `insertIntoKey`. Opt-in per form. */
  | "datasource"
  | "lookup"
  /** Full-width sub-section heading between field groups (e.g. the Qualifier
   *  detail's "Sample Automation" / "QC Automation" / "Surrogate Automation").
   *  The field's `label` is the heading text. No input, never required, never in
   *  the payload. Best used with `singleColumn`. */
  | "section"
  /** Empty grid cell — occupies one column-major slot so following fields keep
   *  their intended column/row. No label, no input, never in the payload. */
  | "spacer";

export interface CrudField {
  /** Payload key sent to the API AND the key read back from GET {id}. */
  key: string;
  label: string;
  type: CrudFieldType;
  required?: boolean;
  /** OPTIONAL — show the red "*" required marker on the label WITHOUT enforcing it:
   *  the field is NOT added to the save-blocking required check, so it can be left
   *  empty and still saved. For a field the legacy form marks required but whose
   *  existing records are all empty (so hard-enforcing it would make them un-saveable)
   *  — e.g. Document Creator's "Renewal Date" (owner decision 2026-10-15, "star only").
   *  Ignored when `required` is already set (that one both shows the "*" and enforces). */
  requiredStar?: boolean;
  maxLength?: number;
  /** OPTIONAL — the stored value is legacy RTF ("{\rtf…}", e.g.
   *  dbo.Accrediation.Text). Only meaningful on a `textarea` field: the VIEW
   *  renders the flattened, readable text (same conversion the report cover page
   *  uses), while EDIT keeps the raw RTF textarea so a save never alters the
   *  XAF-shared value. */
  rtf?: boolean;
  /** OPTIONAL — for a `textarea`, keep it HALF-WIDTH (one grid column) instead of
   *  the default full-width span. Lets a short remark/description match the other
   *  fields' width rather than stretching across the whole form. */
  halfWidth?: boolean;
  /** OPTIONAL — initial value for a NEW record (legacy default-value parity, e.g.
   *  Default='Yes', Sort='0'). Ignored on Edit (the loaded record wins). Use a
   *  string for text/select, string[] for multiselect, boolean for checkbox. */
  defaultValue?: string | boolean | string[];
  /** OPTIONAL — preselect the FIRST option on a NEW record; leave it empty when
   *  the list is empty.
   *
   *  `defaultValue` cannot do this job for an `optionsUrl` select: it is applied
   *  when the blank form is built, long before the options arrive, and the value
   *  a select stores is a row id — which differs per database, so it cannot be
   *  written into the config at all. Taking the first option instead means the
   *  DATA decides the default: whatever the endpoint lists first wins, and a
   *  list that is empty (or that drops its first entry) simply leaves the field
   *  on its None entry rather than pointing at something that no longer exists.
   *
   *  Fills only an EMPTY field on a NEW record: it must never overwrite what the
   *  user picked, nor quietly populate an existing record that was deliberately
   *  left blank.
   *
   *  Distinct from `autoSelectFirst`, which does the same for a CASCADING field
   *  once its parent narrows the list. This one is for a plain, unfiltered
   *  `optionsUrl` select. */
  defaultFirstOption?: boolean;
  /** POPUP ONLY, opt-in: a `required` field drawn WITHOUT the red star. The rule itself is
   *  unchanged — the popup and the server still refuse an empty value. For a form whose legacy
   *  screen does not mark the field (Permit Library ▸ Permits, owner request 2026-10-13). */
  hideRequiredMark?: boolean;
  /** OPTIONAL — show this field ONLY when editing an existing record; hide it on
   *  New. For a field the server defaults sensibly on create (e.g. Active, which
   *  the DB defaults to 1), so the New form stays as short as the record needs.
   *  A hidden field is not rendered, not required-checked, and NOT posted — so
   *  the server-side default applies. */
  editOnly?: boolean;
  /** OPTIONAL — keep the field in the record's values, payload and defaults, but
   *  do NOT render it in the header field layout. For a value owned/rendered by a
   *  bespoke child area instead (e.g. SDMS Sequence Setup's "Is Closing QC", shown
   *  inside the Closing QC tab) while still persisting with the header save. */
  hideInLayout?: boolean;
  /** OPTIONAL — show this field only when the predicate (given the current form
   *  values) is true; otherwise it is dropped from the detail layout entirely, and
   *  the 2-column split rebalances around the fields that remain. Used for the
   *  Document Creator's "Is Retire" (existing records only) and "Reason for Retiring"
   *  (shown once Is Retire is ticked), mirroring the legacy Manual_DetailView
   *  [IsNew]/[IsRetire] appearance rules. The field still persists normally when
   *  shown; the API stays authoritative for any conditional-required rule, since this
   *  gate is layout-only and the shared required-check is not visibleWhen-aware. */
  visibleWhen?: (values: Record<string, unknown>) => boolean;
  /** OPTIONAL — editable on a NEW record, READ-ONLY once the record exists. For a
   *  natural key other data points at by value, where a later change would
   *  silently orphan those references. UI convenience only — the API must still
   *  refuse the change. */
  lockOnEdit?: boolean;
  /** OPTIONAL — render the field ALWAYS read-only (never editable, New or Edit),
   *  keeping its declared `type` for value handling. For a system-generated code
   *  shown for reference (e.g. an auto "Sample Matrix Code"). Same effect as
   *  `type: 'readonly'` but without changing the field's value type. */
  readOnly?: boolean;
  /** For type 'select' — GET this URL for options ({ data: [{ id, name }] }).
   *  For type 'lookup' — the same URL, but each row also carries `cols` (the
   *  extra grid columns): `{ data: [{ id, name, cols: [c1, c2, …] }] }`. */
  optionsUrl?: string;
  /** OPTIONAL — for type 'select', the payload key under which the picked
   *  option's id is sent to the API when it differs from `key` (e.g. the
   *  display field is `vendor` but the stored FK is `vendorOid`). */
  idKey?: string;
  /** For type 'lookup' — the grid-lookup column captions (legacy ASPxGridLookup).
   *  Column 0 is the display name; the rest align to each option's `cols`. */
  lookupColumns?: string[];
  /** For type 'lookup' — auto-fill OTHER fields from the picked row (legacy
   *  dependent-field fill, e.g. Client → Address). Maps a target field key to the
   *  picked row's cell index (0 = display name, 1 = first `cols` entry, …). The
   *  filled fields stay editable so the user can still override. */
  lookupFill?: Record<string, number>;
  /** For type 'number' — bounds and step passed to the input. `min: 0` is the
   *  usual choice for a count or a number of days, where a negative is nonsense. */
  min?: number;
  max?: number;
  step?: number;
  /** For type 'number' — render the value to a FIXED number of decimal places,
   *  the equivalent of legacy XAF's `[ModelDefault("DisplayFormat", "{0:n2}")]`
   *  (Items' Unit Price). A stored 0 then shows as "0.00" rather than "0", and a
   *  freshly typed value settles to the same shape on blur. Display only — the
   *  payload still posts a real JSON number. */
  decimals?: number;
  /** OPTIONAL — on a NEW record, GET this URL and show the returned value in
   *  this (read-only) field, e.g. a preview of the id the record will get.
   *  The response is `{ data: { <this field's key>: value } }`.
   *
   *  ADVISORY ONLY. Whatever actually persists is decided by the server at save
   *  time; never treat the previewed value as reserved. */
  previewUrl?: string;
  /** For type 'select' — do NOT render the leading blank option.
   *
   *  A select normally offers a blank ("— None —") so the value can be cleared.
   *  Set this when the option list ALREADY covers every valid value and the
   *  column has a real default — otherwise the field shows two ways to say the
   *  same thing (a blank AND an explicit 'None'), and picking the blank writes
   *  null instead of the value the user meant. */
  hideBlankOption?: boolean;
  /** For type 'select' in the POPUP form (<ModuleCrudDialog>): show the options as a radio
   *  group instead of a dropdown — for a short fixed choice the legacy popup shows as radios
   *  (Template Formula ▸ New Formula ▸ Kind: Fixed / Script). Opt-in per field; options,
   *  stored value and save payload are exactly the select's. */
  radioInDialog?: boolean;
  /** For type 'textarea' on the full-page detail: show this many text rows instead of the
   *  one-row standard height — for a field that holds a block of text the user must read
   *  whole (SDMS Formulas ▸ Script, a C# body). Opt-in per field; every other textarea keeps
   *  the standard height (standing rule 9). */
  textRows?: number;
  /** For type 'select' whose option ids are NUMBERS (a stored enum int, not an FK
   *  Oid — e.g. Sample Status Color 0..9): post the picked value as a real JSON
   *  number so it binds to an `int?` server-side, exactly like a `number` field.
   *  Without it a select posts the option id as a STRING, which fails to bind to an
   *  int column. Blank/non-numeric posts null. */
  numericValue?: boolean;
  /** For a cascading 'select' — when the reloaded list holds EXACTLY ONE option
   *  and the field is still empty, select it automatically. Saves a pointless
   *  click on the common case (111 of 255 analytes have a single method) while
   *  never overriding a value the user or the record already set. */
  autoSelectSingle?: boolean;
  /** For a cascading 'select' — select the FIRST option whenever the field is
   *  empty, however many there are. Stronger than {@link autoSelectSingle}: use
   *  it where any option is a reasonable starting point and the user is expected
   *  to override when it is not. Same empty-field guard, so it never replaces a
   *  value already chosen or loaded. */
  autoSelectFirst?: boolean;
  /** When set, auto-selection (autoSelectFirst / autoSelectSingle) only fires
   *  once ALL of these dependency keys carry a non-empty value.  Use this when
   *  one dep is a context value that is always present (e.g. sampleMatrixId
   *  passed down from the parent permit) and should NOT alone trigger an
   *  arbitrary first-option pick.  Omit to keep the default behaviour: fire
   *  when ANY dep is non-empty. */
  autoSelectRequires?: string[];
  /** For a cascading 'select' — keep the field DISABLED, empty, and its options
   *  UNFETCHED until every listed key carries a value. Enforces a strict order
   *  down a chain: Method is inert until a Parameter is chosen, Units until a
   *  Method is. Without it a dependent still renders its (unnarrowed or
   *  matrix-only) list, inviting a pick the parent would immediately clear. */
  enabledWhen?: string[];
  /** For type 'select' — a STATIC option list, used instead of `optionsUrl` (no
   *  request is made). For choices the client already knows: a fixed vocabulary
   *  (Active / Disabled) or options derived from the record's own parent, which
   *  no endpoint could serve independently. `optionsUrl` still wins if both are
   *  set, so this is additive for every existing config. */
  options?: { id: string; name: string }[];
  /** For type 'datasource' — the rich-text field key the "Add" button appends the
   *  checked merge tokens into (e.g. Email Content Template's Body). */
  insertIntoKey?: string;
  /** Default a NEW record's value to the CURRENT USER's email (legacy "From" on the
   *  Email compose form — the signed-in user's address, read-only). Applied only on a
   *  new record and only when the field is otherwise empty. */
  defaultCurrentUserEmail?: boolean;
  /** Default a NEW record's value to the CURRENT USER's display name (the linked
   *  Employee.DisplayName — the SAME value a reader resolves an Author/CreatedBy FK to).
   *  A read-only preview of what the server stamps on save (e.g. EDD Builder "Author").
   *  New record only. */
  defaultCurrentUserName?: boolean;
  /** Default a NEW record's value to TODAY (yyyy-mm-dd) — a read-only preview of the
   *  server's GETDATE() stamp on save (e.g. EDD Builder "Date Created"). New record only. */
  defaultNow?: boolean;
  /** For type 'assocgrid' — custom grid/picker columns (each read from the row object
   *  by `key`). Omitted = the default Test-Method column set (name/methodName/
   *  formulaCount/isFormulaActive). Used by the Email To/CC/Bcc recipient grids. */
  assocColumns?: AssocColumn[];
  /** For type 'assocgrid' — DIFFERENT, usually narrower columns for the Link PICKER
   *  popup (vs {@link assocColumns} on the linked-rows grid). The legacy Contact
   *  picker shows only FullName · Street1 · Phone · Email while the recipient grid
   *  shows the full Contact set, so the two column lists differ. Omitted = the picker
   *  reuses {@link assocColumns}. */
  assocPickerColumns?: AssocColumn[];
  /** For type 'assocgrid' — assocgrid fields sharing the SAME group render as ONE
   *  tabbed panel (a tab per field), the legacy XAF TabbedGroup look (e.g. Email
   *  To / CC / Bcc). Omitted = the field renders as its own single-tab panel. */
  assocTabGroup?: string;
  /** For type 'assocgrid' — the Link picker's dialog title (default: the field label,
   *  singularised loosely). Data Package uses "Test Method"; Email uses "Contact". */
  assocPickerTitle?: string;
  /** For type 'assocgrid' — legacy XAF grid-lookup parity: the picker shows ALL
   *  candidate rows INCLUDING the ones already linked (pre-checked), and OK REPLACES
   *  the whole membership with the checked set (add + remove in one go). Omitted =
   *  the default add-only picker that hides already-linked rows and OK only appends.
   *  Data Package Review Checklist's Test Methods uses this so its count + behaviour
   *  match the old NPTest ASPxGridLookup (owner report 2026-10-15). */
  assocPickerIncludesLinked?: boolean;
  /** For type 'assocgrid' — the Link picker renders a per-row EDIT PENCIL next to the
   *  checkbox that opens the row's Test Method record in the shared TestMethodDetailDialog
   *  (G5 TestMethod_DetailView). Only valid where each picker row's `id` is a TestMethod
   *  Oid (Data Package Review Checklist's Test Methods). Owner request 2026-10-15. */
  assocPickerTestMethodEdit?: boolean;
  /** For type 'assocgrid' — the Link picker renders a per-row EDIT PENCIL next to the
   *  checkbox that opens the row's Contact in the shared ContactEditDialog (legacy XAF
   *  Contact_DetailView). Only valid where each picker row's `id` is a Contact Oid (the
   *  Email To/CC/Bcc recipient picker). Owner request 2026-10-15, image 638. */
  assocPickerContactEdit?: boolean;
  /** For type 'select' — label of the empty/none option (default "— None —"). */
  noneLabel?: string;
  /** OPTIONAL — a `select` that ALSO accepts a typed value: a legacy combo with a
   *  free-text editor (e.g. the EAS Reference Library's Source / Storage, 2026-09-30).
   *  Renders a text input with the options offered as suggestions, and saves the
   *  TEXT — so the options' `id` should be the value itself. Opt-in; a plain
   *  `select` is unchanged. */
  allowCustom?: boolean;
  /** OPTIONAL — render a `select` as the shared `<ComboSelect>` instead of the
   *  native browser dropdown: its list is capped (~8 rows) and scrolls, and it
   *  filters as you type — the legacy lookup combo. A native `<select>` popup
   *  cannot be height-limited, so a long option list covers the form. Opt-in;
   *  not combined with `quickAdd` / `allowCustom`. */
  comboList?: boolean;
  /** OPTIONAL — for a `select` (optionsUrl) field: an inline "add new" affordance,
   *  mirroring the legacy XAF grid-lookup "New" button (e.g. Task Checklist →
   *  Category / Ownership). Renders a "+" beside the select; clicking it opens a
   *  small popup with `fields`, POSTs to `createUrl` on OK (the lookup's OWN master
   *  endpoint — no new backend), then re-reads the parent `optionsUrl` and selects
   *  the freshly-created row. */
  quickAdd?: {
    /** POST endpoint that creates the lookup record (the master's own controller). */
    createUrl: string;
    /** Popup header title (e.g. "Category", "Task Ownership"). */
    title: string;
    /** Optional `ti-*` icon for the popup header. */
    icon?: string;
    /** The field(s) shown in the popup (payload keys must match the create API). */
    fields: CrudField[];
    /** The popup field whose typed value equals the new option's display NAME, used
     *  to find the created row in the refreshed option list. Defaults to the first
     *  field's key. */
    nameKey?: string;
    /** OPT-IN (owner report 2026-10-12, image 441): render the field as the shared
     *  <AiCombo> with the ＋ "New" button WELDED to the dropdown caret as ONE combined
     *  control (the Payment Terms "Terms" style), instead of a native <select> plus a
     *  separate ＋ box. On a squareControls form the pair is squared and the ＋ is
     *  green (caret stays orange). Off by default, so other quickAdd forms are
     *  unchanged. */
    welded?: boolean;
    /** OPT-IN (first use: MSR Data Import Config ▸ System, owner request 2026-10-15): the popup
     *  standard footer — OK first, then Cancel. Off by default, so other quickAdd popups keep
     *  Cancel · OK. */
    okFirst?: boolean;
  };
  /** OPTIONAL — on a `textarea` field, the legacy Reagent "Result Correction Formula"
   *  builder (RegentPrepCalculationEditor "Formula" action). Renders a "Formula" button
   *  beside the box that opens a builder popup: it seeds `{target}({unit})=` from the
   *  Calculation Approach (target = the Approach's last word), lists the NON-target
   *  variables with their chosen unit and the dbo.ReagentOperator operator tokens to
   *  append, and writes the built string back. The button is gated exactly as legacy
   *  Formula_Execute: C1/C2/V2 units set AND ((Approach contains "V1" AND V1 set) OR
   *  (Approach contains "W1" AND W1 set)), else "Select the all units." */
  formulaBuilder?: {
    /** Sibling `select` key holding the Calculation Approach (its label drives target). */
    approachKey: string;
    /** The five variable → unit-select-key pairs (C1/C2/V1/V2/W1). */
    units: { variable: string; key: string }[];
    /** Endpoint returning the operator tokens as a `string[]` under `{ data }`. */
    operatorsUrl: string;
  };
  /** For type 'richdoc' — the key of the record value holding the document as HTML,
   *  shown in VIEW mode (the editor itself appears only in edit mode, as in G5). */
  htmlKey?: string;
  /** OPTIONAL — greyed placeholder text shown when the field is empty. For a
   *  'readonly' field it replaces the default "(auto)" filler (e.g. the legacy
   *  Sharing Code shows "Autogenerated"). */
  placeholder?: string;
  /** For 'select' / 'multiselect' — CASCADE: the key(s) of the PARENT field(s)
   *  whose value(s) filter this field's options. Options are (re)loaded from
   *  `optionsUrl?{parent}={value}` — one query parameter per parent — whenever
   *  any parent changes, and this field is CLEARED when a parent changes.
   *  Give an ARRAY when a list narrows by more than one field: Units depends on
   *  Parameter AND Method, and only the pair pins the unit down.
   *  Additive: fields without `dependsOn` load their full option list once. */
  dependsOn?: string | string[];
  /** For type 'multiselect' — paginate the checked-list at this many rows per
   *  page (numbered pager, legacy CheckedListBoxEditor parity). Omit for a plain
   *  scrolling list. */
  pageSize?: number;
  /** For type 'multiselect' — when set, the field box lists EVERY selected value
   *  joined by this separator (e.g. "; ") instead of "N selected". Legacy XAF
   *  editor parity. */
  summarySeparator?: string;
  /** For type 'multiselect' — hide the "OK" button in the picker popup (it still
   *  closes on click-away / Escape). Legacy checked-list parity. */
  hideOk?: boolean;
  /** Optional helper text under the field. */
  hint?: string;
  /** OPTIONAL — override the renderer for INLINE row edit only (dialog still
   *  uses `type`). LIMS QcRole is the canonical case: stored as a FK
   *  (Guid) and rendered as a dropdown in the dialog, but rendered as a
   *  plain text input INLINE (per the LIMS XAFML override). When set to
   *  'text' on a select field, the inline editor sends the typed string
   *  as `{key}Name` (the server resolves to Oid via sub-SELECT). */
  inlineType?: CrudFieldType;
  /** OPTIONAL — LIMS-verbatim message shown inline when the field is
   *  required and empty. Falls back to "{label} Should not be empty."
   *  if not supplied. Use the EXACT wording from the legacy entity's
   *  [RuleRequiredField] attribute so the client message matches what
   *  the server would emit (CLAUDE.md "LIMS exact parity"). */
  requiredMessage?: string;
  /** For type 'file' — the sibling form key holding the uploaded file's
   *  display name (returned by GET {basePath}/{id} alongside the FK Oid).
   *  The field's own `key` holds the FileData Oid sent back on Save. */
  fileNameKey?: string;
  /** For type 'file' — POST endpoint that accepts a multipart `file` and
   *  returns { data: { fileDataOid, fileName } } (two-phase upload). */
  uploadUrl?: string;
  /** For type 'file' — GET base; the field downloads `${downloadUrlBase}/${oid}/download`. */
  downloadUrlBase?: string;
  /** For a mutually-exclusive checkbox group (e.g. ICP-MS Select Mass 1..4):
   *  the sibling checkbox keys to clear (set false) when THIS one is checked, so
   *  at most one in the group is ever selected. */
  exclusiveWith?: string[];
}

/** A READ-ONLY reference table rendered below the fields on the detail view.
 *
 *  The content is DECLARED HERE, not loaded from the database - it documents
 *  behaviour (which events fire, what triggers them) rather than storing
 *  records, so it needs no table and no endpoint. Use it for a fixed matrix the
 *  user should see while editing; anything that can be added to or edited by a
 *  user is a real table with a real grid instead. */
export interface CrudStaticSection {
  /** Heading above the table. */
  title: string;
  /** Column captions, left to right. */
  columns: string[];
  /** Rows, each the same length as `columns`. */
  rows: string[][];
  /** Cell values to render bold wherever they appear (e.g. 'Required'). */
  strong?: string[];
  /** Optional note under the table; `label` renders bold before `text`. */
  note?: { label?: string; text: string };
}

/** A CHILD COLLECTION grid rendered below the fields on the detail view: the
 *  records that belong to the one being edited (e.g. a permit's limits).
 *
 *  Unlike {@link CrudStaticSection} this is real, editable data - it has its own
 *  New / Edit / Delete, backed by the child's own REST controller. The parent id
 *  is injected into every write, so a child can never be orphaned.
 *
 *  A child needs a saved parent to belong to, so the actions stay DISABLED while
 *  the parent is unsaved (a New that has not been saved yet). */
export interface CrudChildGrid {
  /** Heading above the grid. */
  title: string;
  /** GET returning `{ data: [...] }`; `{id}` is replaced with the parent id. */
  listUrl: string;
  /** Grid columns, in order. `key` indexes a row of the list response.
   *  `type: 'date'` renders through the shared fmtLocalDate helper - dates must
   *  never be formatted inline (14b.6 / C2). `type: 'file'` renders the cell
   *  (the filename in `key`) as a download link to
   *  `downloadUrlBase/{row[fileOidKey]}/download` — legacy attachment preview. */
  columns: {
    key: string;
    label: string;
    width?: number;
    type?: "date" | "file" | "checkbox";
    downloadUrlBase?: string;
    fileOidKey?: string;
    /** OPTIONAL — clamp this cell to its `width` on ONE line, truncating overflow
     *  with an ellipsis (full value on hover via title). Keeps a long free-text / SQL
     *  cell (EDD Builder's QueryBuilder) from blowing the row out, so rows stay
     *  compact and the column widths stay stable. Opt-in per column. */
    ellipsis?: boolean;
  }[];
  /** MODULE_CRUD key whose config drives the child's New/Edit dialog. */
  childKey: string;
  /** OPTIONAL — set when `listUrl` returns the SAME shape as the child's
   *  `GET basePath/{id}`, i.e. every editable field, not just the display
   *  columns. Edit then opens from the row already in the grid and skips that
   *  fetch entirely — one round trip (~300 ms on a remote database) and the
   *  "Loading…" panel with it.
   *
   *  OFF BY DEFAULT, and must stay that way unless the shapes really do match:
   *  a row missing an editable field would prefill it BLANK and the next save
   *  would persist that blank. Permit Limits qualifies because
   *  ListByPermitAsync and GetByIdAsync both return PermitSetupDetail. */
  listReturnsFullRecord?: boolean;
  /** OPTIONAL — parent field keys to make readable inside the child's dialog, for
   *  cascades that need context the child row does not itself carry. A Permit
   *  Limit's Units narrows by the OWNING PERMIT's Sample Matrix, which lives on
   *  the parent, not the limit. Read-only context: these are never rendered and
   *  never sent in the child's payload, since that is built from `fields`. */
  parentContext?: string[];
  /** Field key on the CHILD that holds the parent id. Injected on save. */
  parentField: string;
  /** Optional note rendered at the right of the grid footer. */
  note?: string;
  /** OPTIONAL — page this grid client-side behind the ONE shared `<GridPager>`
   *  footer, the same bar every list grid has (rule 7 / §18.6): `Page X of N
   *  (TOTAL items)` left, the numbered pages centre, `Page size:` right. The
   *  default `Total Records: n` footer stays for every grid that does not ask
   *  for it, so no existing form's footer changes.
   *
   *  Client-side because a child grid already holds its whole collection (it is
   *  read in one GET by parent id, and unsaved rows are buffered in the same
   *  list) — paging is presentation, not a second query. Sort and the column
   *  filters apply BEFORE the slice, so page 1 is the first page of what the
   *  user filtered, and Export still takes the whole filtered set, not the
   *  visible page. First form: Permit Library ▸ Permit Limits (owner request
   *  2026-09-28). */
  paged?: boolean;
  /** Rows per page when {@link paged}. Defaults to 20 — the same default the
   *  shared list grids use — and the user can change it in the footer. */
  pageSize?: number;
  /** OPTIONAL — legacy XAF association Link/Unlink toolbar. When set, the grid shows
   *  Link (pick an existing UNLINKED record from `linkListUrl` and attach it to this
   *  parent via POST `${childBasePath}/{id}/link?labwareId=<parent>`) and Unlink
   *  (POST `${childBasePath}/{id}/unlink`, keeping the row). Off by default. */
  linkable?: boolean;
  /** GET returning `{ data: [full records] }` of rows NOT linked to any parent —
   *  the Link picker's candidate list. Required when `linkable`. */
  linkListUrl?: string;
  /** OPTIONAL — query param name the shared Link handler posts the parent id
   *  under (`${childBasePath}/{id}/link?<name>=<parentId>`), and that Unlink now
   *  ALSO sends (`${childBasePath}/{id}/unlink?<name>=<parentId>`) — needed for
   *  a genuine many-to-many child (a Sub Item can be linked to several Items, so
   *  Unlink must say WHICH one to drop), unlike the original one-to-one
   *  Instrument Certificate/Attachment case where the child's own single FK made
   *  that unambiguous. Defaults to "labwareId" (those two forms' own historical
   *  name) so they're unaffected; a NEW linkable child grid sets its own instead
   *  of reusing that name for an unrelated parent (Sub Item → "itemId"). The
   *  extra query param on Unlink is harmless for a backend that doesn't read it
   *  (ASP.NET Core ignores an unbound query param). */
  linkQueryParam?: string;
  /** OPTIONAL — a narrow pencil column beside the selection box, opening that
   *  row's edit popup directly (legacy XAF's in-row edit affordance). Purely a
   *  shortcut: it opens the same dialog the Edit button does, so a grid without
   *  it loses nothing but the click. Follows the record's mode and is disabled on
   *  an unsaved pending row, exactly like Edit. */
  rowEditIcon?: boolean;
  /** OPTIONAL — a narrow download column next to {@link rowEditIcon}, downloading
   *  THAT row's file without selecting it first (legacy XAF shows this beside the
   *  pencil on a file grid). Requires {@link downloadAction}, which supplies the
   *  route and the row key holding the dbo.FileData Oid. Read-only safe, like the
   *  Download toolbar button: taking a copy changes nothing. */
  rowDownloadIcon?: boolean;
  /** OPTIONAL — select rows with CHECKBOXES and a select-all in the header,
   *  instead of the default single-select radio. This is the legacy XAF grid
   *  (and the app's own `<AppDataGrid>` standard, rule 7): several rows can be
   *  ticked, and the bulk-capable actions — Delete, Unlink — act on the whole
   *  selection, while the single-row ones — Edit, Download — need exactly one.
   *
   *  OPT-IN so the existing child grids (Permit Limits, Sub Item, Instrument
   *  Certificate/Attachment, Help Center) keep their radio and their
   *  one-row-at-a-time toolbars until each is migrated as its own change.
   *  First form: Vendors (user request 2026-09-23). */
  multiSelect?: boolean;
  /** OPTIONAL — drop **New** from this grid's toolbar. XAF hides it on a
   *  non-aggregated many-to-many collection, where Link takes its place: you
   *  attach an existing row rather than create one (Vendors → Evaluation).
   *
   *  Note the consequence before setting it: with no New, the grid can only ever
   *  offer what the Link picker already holds. */
  hideNew?: boolean;
  /** OPTIONAL — drop **Edit** from this grid's toolbar. For a purely read-only
   *  child collection (e.g. the Retired Document Attachments grid), pair with
   *  {@link hideNew} + {@link hideDelete} to leave only Export. */
  hideEdit?: boolean;
  /** OPTIONAL — drop **Delete** from this grid's toolbar. Pairs with
   *  {@link hideNew} on an M:N collection, where Unlink is the destructive action
   *  the user wants and deleting the shared row outright is not on offer. */
  hideDelete?: boolean;
  /** OPTIONAL — add a **Download** button that downloads the SELECTED row's file
   *  (legacy XAF FileAttachment toolbar, e.g. Vendors → Upload). Independent of
   *  the `type: 'file'` COLUMN, which links the filename in the cell: legacy
   *  offers both, and the button is what works when the grid is keyboard-driven.
   *  `fileOidKey` indexes the row for the dbo.FileData Oid; `urlBase` is the
   *  controller's file route (`{urlBase}/{oid}/download`). */
  downloadAction?: { urlBase: string; fileOidKey: string };
  /** OPTIONAL — show Export as a labelled **Export to ▾** button on the grid's own
   *  toolbar, right after Delete, instead of the icon at the right of the header —
   *  the legacy XAF child-grid bar New · Edit · Delete · Export to (Technical Writer,
   *  owner request 2026-10-13). Same export, same data; Maximize stays where it is. */
  exportButton?: boolean;
  /** @deprecated Vestigial — every CrudChildGrid now ALWAYS shows Export + Maximize
   *  (BO UI Standard, user request 2026-09-23: "in the detail view if there is a
   *  grid they must have the export to and maximus actions"), so this flag no
   *  longer gates anything. Left in the type only so configs that still set it
   *  keep compiling; do not set it on a NEW config. */
  exportable?: boolean;
  /** OPTIONAL — Edit swaps the CHECKED rows' own cells for editors in place,
   *  rather than opening the child dialog: each cell keeps its column, so a
   *  row still reads as a row (owner request 2026-09-23, "edit the data and
   *  update in the row cell itself"). Works on any number of checked rows at
   *  once; each row keeps its own draft and its own cascaded option lists.
   *  New is unaffected and still opens the dialog — there is no existing row
   *  to edit in place for a record that does not exist yet. Off by default.
   *  Naming matches ModuleCrudConfig.pencilRowEdit, the same idea for the
   *  list view. */
  pencilRowEdit?: boolean;
  /** OPTIONAL — hide this child grid's "Export to" + Maximize header icons. They
   *  are ON by default (BO UI Standard §18.6); set this only where the owner asked
   *  for a plain embedded grid with no header tools (Document Creator Attachments,
   *  owner request 2026-09-30). Use {@link hideToolbar} instead when the grid also
   *  has no New/Edit/Delete and the whole bar should disappear. */
  hideHeaderTools?: boolean;
  /** OPTIONAL — hide the ENTIRE toolbar row (New/Edit/Delete/Link + Export +
   *  Maximize). For a purely view-only child collection whose bar would otherwise
   *  render empty (Retired Document Attachments — New/Edit/Delete already hidden),
   *  so no blank strip is left above the grid (owner request 2026-09-30). */
  hideToolbar?: boolean;
  /** OPTIONAL — draw a card border + radius around the whole child-grid section
   *  (title, toolbar, grid, footer). Off by default (the section is borderless and
   *  blends into the detail page); set it where the owner wants the collection
   *  visually boxed (Document Creator / Retired Document Attachments, 2026-09-30). */
  bordered?: boolean;
  /** OPTIONAL — clicking a saved row opens its New/Edit dialog (the same one the
   *  Edit button opens), mirroring the legacy XAF "click the row → popup" flow
   *  (EDD Builder's Query Details → EDDQuery Builder popup). The selection radio /
   *  checkbox still selects without opening. Off by default. */
  rowOpensDialog?: boolean;
  /** OPTIONAL — auto-number a field on NEW from the existing rows (legacy XAF
   *  "Rev Number" on a document's Attachments: Rev1 → Rev2, read-only + filled in
   *  automatically). On New the grid parses `prefix<n>` out of every existing (and
   *  pending) row's `key`, takes the max and seeds `prefix<max+1>` into the dialog
   *  as that field's value. Pair it with a `readOnly: true` field of the same key,
   *  which still POSTs its (seeded) value. Edit shows the stored value, never a
   *  recomputed one. */
  seqField?: { key: string; prefix: string };
}

/**
 * A record-scoped action on the detail view's action bar - something done TO the
 * saved record that is neither Save nor Delete (Renew Permit, and whatever comes
 * next).
 *
 * Declared in config rather than coded into <SettingsDetailPage> so the shared
 * page stays module-agnostic: a second module gets its own action by adding one
 * entry, not by editing the shared component.
 */
/** A cascading field's parent key(s), normalised. `dependsOn` accepts a single
 *  key or several (Units narrows by Parameter AND Method), so every consumer
 *  goes through this rather than assuming one. */
export function depsOf(f: { dependsOn?: string | string[] }): string[] {
  return f.dependsOn == null ? [] : Array.isArray(f.dependsOn) ? f.dependsOn : [f.dependsOn];
}

/** Key under which a PENDING child row carries its display labels.
 *
 *  A child added before its parent exists is buffered client-side and written
 *  once the parent is created. That buffered row holds what the API wants — FK
 *  Oids (`parameterId`, `unitsId`, …) — but the grid renders display columns
 *  (`parameterName`, `unitsName`, `operatorDisplay`), so every FK-backed cell
 *  came up EMPTY until the parent was saved and the row re-read from the server.
 *
 *  The labels the user just picked are already on screen in the dialog, so they
 *  are captured here rather than guessed. Kept under ONE reserved key so the
 *  POST can drop it whole and the payload the server sees is unchanged. */
export const PENDING_LABELS_KEY = "__labels";

/** True while a field's `enabledWhen` prerequisites are not all filled — the
 *  control renders disabled and its options are not requested. */
export function isFieldLocked(f: { enabledWhen?: string[] }, values: Record<string, unknown>): boolean {
  return (f.enabledWhen ?? []).some((k) => String(values[k] ?? "").trim() === "");
}

/** `optionsUrl` plus one query parameter per parent that currently has a value.
 *  Parents that are still empty are OMITTED, so a partly-filled form narrows by
 *  what it knows instead of asking for nothing. */
export function cascadeUrl(
  optionsUrl: string,
  f: { dependsOn?: string | string[] },
  values: Record<string, unknown>,
): string {
  const qs = depsOf(f)
    .map((k) => [k, String(values[k] ?? "").trim()] as const)
    .filter(([, v]) => v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");
  return qs ? `${optionsUrl}?${qs}` : optionsUrl;
}

/** A form-specific LIST toolbar action over the ticked rows (Technical Writer
 *  "Release"). Confirmed through the shared dialog, then
 *  `POST ${basePath}/${path}` with `{ ids }`, then the shared success message and
 *  a list refresh. */
export interface CrudListAction {
  key: string;
  label: string;
  /** Emoji glyph, matching the other toolbar buttons. */
  icon?: string;
  path: string;
  /** OPTIONAL — query string appended to the POST, e.g. "big=true". */
  query?: string;
  /** Confirm dialog wording. Not used by a {@link download} action, which changes nothing. */
  confirmTitle?: string;
  confirmMessage?: string;
  successMessage: string;
  /** Shown (as info) when nothing is ticked. Unset = the button is disabled instead. */
  noSelectionMessage?: string;
  /** OPTIONAL — the action RETURNS A FILE (e.g. a barcode PDF): no confirm, the response
   *  is downloaded (server file name, else this one) and the list is left as it is. */
  download?: string;
  /** OPTIONAL — where the button sits: "left" (default, before the standard buttons) or
   *  "beforeExport" (after Refresh, before Export to — the G5 Instrument barcode buttons). */
  placement?: "left" | "beforeExport";
}

export interface CrudRecordAction {
  /** Stable key - also the React key. */
  key: string;
  label: string;
  /** Emoji glyph, matching the other action-bar buttons. */
  icon?: string;
  /** POSTed as `${basePath}/${id}/${path}` — or, with {@link sendFormValues},
   *  as `${basePath}/${path}` carrying the current form payload. */
  path: string;
  /** OPTIONAL — POST the CURRENT form values (not an id-scoped empty body) to
   *  `${basePath}/${path}`, and show even on a NEW (unsaved) record. For a
   *  read-only probe of the entered values that must work before Save — e.g. DB
   *  Connection's "Test Connection" tests the typed Server/Database/User/Password
   *  without saving (legacy tests View.CurrentObject). No record is changed, so
   *  the list is not refreshed and nothing is navigated. */
  sendFormValues?: boolean;
  /** Shared confirm dialog copy (standing rule 7 - EVERY state-changing action is
   *  confirmed through the shared centred dialog). */
  confirmTitle: string;
  confirmMessage: string;
  /** Shared success acknowledgement. `{id}` is replaced by the returned id. */
  successMessage: string;
  /** Hide the button unless this holds for the loaded record. Receives the raw
   *  loaded values. Omitted = always shown on a saved record. */
  visibleWhen?: (values: Record<string, unknown>) => boolean;
  /** Field on the response `data` holding an id to navigate to afterwards. */
  navigateToField?: string;
  /** Renders in the orange primary style. */
  primary?: boolean;
  /** OPTIONAL — PERSIST the form's current values before POSTing the action.
   *
   *  A record action runs SERVER-side against the STORED row, so without this an
   *  action that depends on a field the user has just typed reads the old value:
   *  Activate Renewal refused a successor whose Effective Date was filled in on
   *  screen but still NULL in the database, and its message ("Set an Effective
   *  Date…") read as a bug because the date was plainly visible.
   *
   *  Set it on any action whose OUTCOME depends on this record's field values.
   *  The save and the action then run inside ONE shared save flow — one reason
   *  gate, one confirm, one blocking loader, one acknowledgement — so the button
   *  still costs the user a single click. */
  saveFirst?: boolean;
  /** OPTIONAL — the action must be EXPLAINED (e.g. retiring a record that other
   *  records still point at). The shared prompt dialog then takes the place of
   *  the plain confirm — same title and message, plus a required reason box —
   *  and the reason is POSTed as `{ reason }` for the handler to audit. The
   *  server must refuse a blank one; this only saves the round trip. Not
   *  combined with {@link saveFirst} or {@link sendFormValues}. */
  reasonPrompt?: { placeholder?: string };
}

/** One column of an `assocgrid` grid + Link picker. The value is read from each
 *  row object by `key`; `date` runs it through fmtLocalDate, `bool` renders Yes/No. */
export interface AssocColumn {
  key: string;
  label: string;
  width?: number;
  date?: boolean;
  bool?: boolean;
  /** Render the cell as a read-only checkbox (legacy XAF bit column) instead of the
   *  bool "Yes/No" TEXT. Keep `bool` alongside it so the Export still writes Yes/No. */
  checkbox?: boolean;
}

export interface ModuleCrudConfig {
  /** REST base, e.g. '/api/v1/methods'.  POST base, PUT/DELETE base/{id}.
   *  OPTIONAL: a `detailReadOnly` reference list with no controller of its own
   *  omits it — the shared static-list adapter then renders the detail straight
   *  from the loaded list row (see ModuleListView staticListAdapter). */
  basePath?: string;
  /** Row property holding the record's primary key (Oid) for edit/delete. */
  idField: string;
  /** Audit FormName + GET base/{id} prefill source. */
  formName: string;
  /** OPTIONAL — Project Notes (BO UI Standard §8, mandatory on every BO screen).
   *  The note routes' base with `{id}` standing in for the record's Oid, e.g.
   *  `/api/v1/documents/{id}/project-notes`. Set it and `<SettingsDetailPage>`
   *  renders the shared `<ProjectNotesButton>` on the action bar, gated on the
   *  record existing (a new, unsaved record has nothing to attach a note to).
   *
   *  The form's side is ONE line; what it needs from the API is the four standard
   *  routes over the shared `dbo.Notes` — `GET|POST {base}` and
   *  `PUT|DELETE {base}/{noteOid}` — with the author-only rule (§14a Rule C).
   *  `INoteReader`'s update / delete are already source-agnostic, so a module
   *  adds only a list + an add. */
  projectNotesBase?: string;
  /** With `projectNotesBase`: render the popup VIEW-ONLY (no New / Delete). For a
   *  read-only screen such as Retired Document, where the notes are history. */
  projectNotesReadOnly?: boolean;
  /** OPTIONAL — the FormName the Audit Trail chip QUERIES, when that differs from this
   *  module's own `formName`. Two cases it exists for:
   *   • a screen whose actions are audited under the OWNING record's form, so the trail
   *     reads as one lifecycle (Retired Document → "Document Creator": a document's
   *     retire / reactivate history belongs with the document, not split in two);
   *   • a list with no `basePath` (static adapter) that nevertheless HAS an audit trail —
   *     the default suppresses the chip for genuinely static reference lists (Operators),
   *     and this opts a real one back in (standing rule 11).
   *  Defaults to `formName` when a `basePath` exists, else no chip. */
  auditFormName?: string;
  /** Singular noun for button/dialog text ("Method"). */
  entityLabel: string;
  fields: CrudField[];
  /** OPTIONAL — a URL returning MANY option lists at once, keyed by the exact
   *  `optionsUrl?…` each cascading field would otherwise request:
   *  `{ data: { "<url>": [{id, name}], … } }`. Fetched once when the form opens
   *  and poured straight into the same per-URL cache the cascade already uses.
   *
   *  WHY. A cascade costs one round trip per LEVEL, serially — picking a Permit
   *  Limit's Parameter waits for Method, then for Units. Against a remote
   *  database that is the whole delay: a bare `SELECT 1` measures ~276 ms there
   *  while the lookups themselves add under ~110 ms each. Sending the lists that
   *  a form could possibly need in ONE request (410 rows for the busiest Sample
   *  Matrix) turns every later change into a memory read.
   *
   *  Purely an optimisation, and safe by construction: a key that doesn't match
   *  simply misses and fetches as before. `cachePrimeDependsOn` names the value
   *  keys that build its query string, exactly like a field's `dependsOn`; the
   *  prime is skipped while any of them is empty. */
  cachePrimeUrl?: string;
  /** Value keys forming `cachePrimeUrl`'s query string (see above). */
  cachePrimeDependsOn?: string[];
  /** OPTIONAL — maps a list-view column key to the editable CRUD field key
   *  when they differ. Needed for FK columns where the list shows the
   *  human-readable name (e.g. `qcSource`) but the editor needs the FK
   *  Oid (e.g. `qcSourceId`). Identity mapping is used when omitted. */
  columnFieldMap?: Record<string, string>;
  /** OPTIONAL — set to false to hide the per-row inline-edit pencil. The
   *  full-dialog edit (external-link icon) still shows. Use for SIMPLE
   *  master-data lists (Container / Preservative / Temperature) where
   *  cells are too few to benefit from inline edit; only QC Type and
   *  other multi-column forms keep inline on. Default: true. */
  inlineRowEdit?: boolean;
  /** OPTIONAL — a DOUBLE-click on a row opens its full-page detail (read-only
   *  view), the AppDataGrid convention; a single click just selects. Opt-in and
   *  self-contained: it also turns OFF per-cell inline editing on this list (so a
   *  cell double-click opens the detail instead of editing in place) and leaves
   *  single-click selection + the pencil / toolbar Edit untouched. Every other
   *  list keeps its current behaviour. (Method, owner request 2026-10-02.) */
  rowDoubleClickView?: boolean;
  /** OPTIONAL — hide ONLY the per-row Edit-pencil column on this module's list.
   *  The select-checkbox and the toolbar Edit/Delete stay, so editing works via the
   *  checkbox + toolbar Edit (or by clicking a row → view → Edit). Scoped opt-in —
   *  every other grid keeps the pencil. Turn Around Time uses this to match the
   *  ALPACA list, which has a checkbox but no pencil (user request 2026-09-17). */
  hideEditPencil?: boolean;
  /** OPTIONAL — render the LIST as a read-only VIEW (legacy XAF ListView with no
   *  New/Edit/Delete — just Refresh/Export/Search). Row-click still opens the
   *  full-page detail (read-only, with its own Edit button) and the shared CRUD
   *  config still drives the child dialogs. Used by the global Instrument
   *  Certificate view, where certificates are managed from the Instrument detail. */
  readOnlyList?: boolean;
  /** OPTIONAL — the LIST offers Edit only: New and Delete are left off the toolbar
   *  (legacy XAF ListView AllowNew="False" AllowDelete="False"). The row checkbox,
   *  the Edit pencil, the toolbar Edit and the row-click detail all stay. */
  editOnlyList?: boolean;
  /** OPTIONAL — hide the toolbar New button only (Delete, Edit and the checkboxes stay). */
  hideNewButton?: boolean;
  /** OPTIONAL — add a second per-row icon that opens the full detail form (G5 Method list). */
  detailIcon?: boolean;
  /** OPTIONAL — draw the {@link detailIcon} in its OWN column right after the Edit pencil
   *  (G5 Instrument list: row-edit pencil | open-detail icon, two separate columns). */
  detailIconColumn?: boolean;
  /** OPTIONAL — hide the toolbar Edit button only (New, Delete and the checkboxes stay). */
  hideEditButton?: boolean;
  /** OPTIONAL — hide the toolbar Delete button only (New, Edit and the checkboxes stay). */
  hideDeleteButton?: boolean;
  /** OPTIONAL — the row-click / New / Edit opens the full-page detail as a READ-ONLY
   *  VIEW (no Save trio, greyed fields). The LIST keeps its toolbar; only the detail
   *  is non-editable. Distinct from readOnlyList (which strips the LIST toolbar). */
  detailReadOnly?: boolean;
  /** OPTIONAL — pure ALPACA list-view parity: hides checkboxes, edit-pencil column,
   *  and the List/Detail view-toggle, leaving only Refresh/Export/Search.
   *  Use when the ALPACA original is a bare non-editable list with no row selection. */
  pureListView?: boolean;
  /** OPTIONAL — fit EVERY column in the viewport with no horizontal scroll (legacy
   *  XAF ListView): the grid uses table-layout:fixed at width:100% so the configured
   *  column widths act as proportions, and long headers/cells truncate with an
   *  ellipsis instead of forcing the column wider. For wide many-column read views
   *  (the Instrument Certificate list). Without it, columns size to their content. */
  fitAllColumns?: boolean;
  /** OPTIONAL — legacy XAF row-inline editor. When true, the per-row Edit pencil
   *  puts the WHOLE ROW into editors at once (every inline-editable cell — text /
   *  date / inlineType:'select') with a Save / Cancel pair in the pencil column,
   *  instead of opening the full detail; per-cell double-click is suppressed. The
   *  wider field set (and non-inline columns like a multiselect) is still reached
   *  via check-row → toolbar Edit → detail. Opt-in per module so grids that use
   *  the plain double-click cell edit (e.g. eNotification) are unaffected.
   *  Requires inlineRowEdit !== false so the cells resolve an editor type. */
  pencilRowEdit?: boolean;
  /** OPTIONAL — initial page-size for this module's list (one of PAGE_SIZES:
   *  10 / 20 / 50 / 100).  Overrides the global DEFAULT_PAGE_SIZE for THIS form
   *  only.  Omit to use the shared default. */
  defaultPageSize?: number;
  /** OPTIONAL — set true for lookups that support bulk CSV load (BO UI
   *  Standard §6).  Adds the Import + Template toolbar buttons: Template
   *  downloads a blank CSV of the non-readonly field labels; Import uploads a
   *  filled CSV to `${basePath}/import`.  Only Method supports this today. */
  bulkLoad?: boolean;
  /** OPTIONAL — the settings FAMILY this form belongs to (BO UI Standard §4
   *  <RelatedLookups>).  Renders a strip under the toolbar to hop between the
   *  sibling lookups; the entry whose `id` matches this module is the active
   *  (orange) one.  A sibling whose form isn't built yet shows a gentle notice
   *  on click instead of navigating. */
  relatedLookups?: { id: string; label: string }[];
  /** OPTIONAL — detail view: hide the Edit button (view + delete only, no edit
   *  of existing records from the detail). Default: Edit shown. */
  detailHideEdit?: boolean;
  /** OPTIONAL — detail view: show a Delete button on the toolbar (ALPACA
   *  detail-toolbar parity), for existing records. Default: off (delete stays a
   *  list-view action). Runs the shared useDeleteFlow → DELETE basePath/{id}. */
  detailShowDelete?: boolean;
  /** OPTIONAL — detail VIEW mode: a New button on the action bar (legacy XAF detail
   *  toolbar parity, e.g. Technical Writer). Opens a blank record in the SAME page;
   *  leaving unsaved edits goes through the shared unsaved-changes guard. */
  detailShowNew?: boolean;
  /** OPTIONAL — detail VIEW mode: a Refresh button that re-reads the open record
   *  (legacy XAF detail toolbar parity, e.g. Technical Writer). */
  detailShowRefresh?: boolean;
  /** OPTIONAL — detail view: move some fields onto their OWN top-level tab beside
   *  "General Information" — the legacy XAF TabbedGroup that puts a large editor on
   *  a page of its own (Technical Writer's "Article" tab). Each listed field renders
   *  full-width with no label (the tab caption names it); the rest of the page —
   *  fields, child grids — stays on the first tab. Unset = no tab strip. */
  fieldTabs?: { title: string; fields: string[] }[];
  /** OPTIONAL — form-specific LIST toolbar actions over the ticked rows, rendered in
   *  the AppToolbar `left` slot (form-specific actions come first, rule 7). See
   *  {@link CrudListAction}. */
  listActions?: CrudListAction[];
  /** OPTIONAL — detail view: open an EXISTING record straight into editable mode
   *  (Save · Save & Close · Save & New · Cancel + Delete), skipping the read-only
   *  view + separate Edit toggle. Mirrors the legacy XAF flow where List → Edit
   *  opens an editable DetailView. Default: open read-only (Edit to modify). */
  detailOpenInEdit?: boolean;
  /** OPTIONAL — detail view: no "Save and New" button (nor its Ctrl+Shift+S key),
   *  for a form whose legacy DetailView has AllowNew="False" — its records are only
   *  ever edited here, never created. */
  detailHideSaveNew?: boolean;
  /** OPTIONAL — detail view: stack every field in ONE vertical column instead of
   *  the default two-column grid. Use when the legacy XAF DetailView is a single
   *  stacked column (e.g. Bottle Sharing) so the field order reads strictly
   *  top-to-bottom. Additive — default (unset) keeps the two-column layout. */
  singleColumn?: boolean;
  /** OPTIONAL — detail view: hide the "General Information" section caption above
   *  the field grid. Default (unset) shows it. */
  hideSectionHeader?: boolean;
  /** OPTIONAL — detail view: fill the two-column grid COLUMN-major (straight down
   *  the left column, then down the right) instead of the default row-major
   *  (left, right, left, right).
   *
   *  Use it when the form is designed as two independent stacks of fields rather
   *  than as label/value pairs — `fields` is then simply "everything in the left
   *  column, then everything in the right". Ignored when `singleColumn` is set. */
  columnMajor?: boolean;
  /** OPTIONAL — detail view: label BESIDE its field (fixed-width label column,
   *  LEFT-aligned, immediately left of the field — the alignment was right until
   *  2026-09-23, corrected to match G5 on user report), full-width equal 50/50 split
   *  (no max-width cap), every control a uniform 26px tall (incl. textarea, which
   *  the stacked layout below sizes taller at 36px), and a tighter 6px row-gap —
   *  instead of the older stacked label-above-field row, taller textarea, and
   *  940px-capped grid. The legacy XAF DetailView look (ALPACA "G5" parity).
   *
   *  STANDARD FOR NEW DETAIL FORMS from 2026-09-23 (user: "make this standard for
   *  the Fields in the Detail view, from here after we should use this standard")
   *  — set `sideLabels: true` on every NEW moduleCrud detail entry. Existing forms
   *  built before this date keep the stacked BO UI Standard layout (§18.6, standing
   *  rule 12) unchanged; they are not retroactively migrated by this flag alone —
   *  do that as its own reviewed change per form, not silently as a side effect of
   *  touching something else. First form: Items (`set-inventory-items`). */
  sideLabels?: boolean;
  /** OPT-IN — the detail-view standard from the 2026-10-06 review: controls with
   *  SQUARE corners, a 24px height and NO colour at rest (editable boxes plain white
   *  instead of the editable yellow, carets grey instead of orange), instead of the
   *  shared 5px radius / 26px / yellow. Read-only stays grey and a required field left
   *  empty after a save attempt still turns yellow. (Owner feedback, Calculation
   *  Editor: "Box without curve, control box height set 24 px", "no need colour also".) Applies to every control in the
   *  field grid — inputs, selects, combos, multi-selects, lookups and the in-grid
   *  buttons beside them; a tall textarea keeps its two-row proportion at the new
   *  height. Default off — no other form changes. */
  squareControls?: boolean;
  /** OPTIONAL — the G5 compact control size on the full-page detail: single-line boxes 20 px tall on a
   *  5 px row pitch (the 26 px shared standard above is the default for every other form). Used with
   *  `horizontalLabelWidth` and `singleColumn` to match the legacy New form's label column and field
   *  width (owner request 2026-10-14, Method). Default off — no other form changes. */
  compactControls?: boolean;
  /** LIST: show the Search box on the action bar, right after Export to, instead of on its own
   *  row under the bar (legacy XAF puts Search on the toolbar). Opt-in per module — every other
   *  list keeps the search row. First use: Settings ▸ SDMS ▸ Template Types (owner 2026-10-09). */
  searchInToolbar?: boolean;
  /** LIST: pass `crudFirst` to the shared <AppToolbar> — New · Edit · Delete render BEFORE
   *  the form-specific actions (crud.listActions / the `left` slot). Only for a form whose
   *  LEGACY bar puts its own action after Delete (Technical Writer: New · Edit · Delete ·
   *  Release · Refresh · Export to). Default off keeps the canonical order (standing rule 7). */
  crudFirst?: boolean;
  /** LIST: the legacy "Import File" + "Template Download" pair, drawn between Refresh and
   *  Export to. Import File opens the one-field upload popup (<ImportFileDialog>); Template
   *  Download saves the workbook the import reads. Opt-in per module. First use: Settings ▸
   *  Parameter ▸ Parameter Library (owner 2026-10-13). */
  listImport?: { importUrl: string; templateUrl: string; templateName: string; dialogTitle: string };
  /** OPTIONAL — with `columnMajor`, force the LEFT-column height (number of field
   *  rows) instead of the default ceil(fieldCount / 2). Use it to split on a
   *  SECTION boundary rather than the exact middle — e.g. Qualifier puts
   *  Definition + Sample Automation (12 rows) in the left column and QC +
   *  Surrogate Automation in the right. */
  columnRows?: number;
  /** OPTIONAL — with `columnMajor`, let the two columns use the FULL page width and
   *  sit at the left/right edges (a wide gap between) instead of the default ≤940px
   *  centred-left block. For a two-section form where the right column should hug
   *  the right side of the page (Qualifier: Definition left, QC + Surrogate right). */
  wideLayout?: boolean;
  /** OPTIONAL — detail view: put each field's LABEL to the LEFT of its control
   *  (same row) instead of above it, matching the legacy XAF detail (Qualifier).
   *  The label takes a fixed left gutter; the control fills the rest. */
  horizontalLabels?: boolean;
  /** OPTIONAL — override the label column width when horizontalLabels is true
   *  (default 150 px). Use a smaller value when the labels are short and 150 px
   *  creates visible dead-space in the label gutter (e.g. 90 for "Name / Comment"). */
  horizontalLabelWidth?: number;
  /** OPTIONAL — detail view: override the default max-width (940px) of the field
   *  grid for this form only (px value, e.g. 600). Use when the legacy XAF form
   *  is narrower than the default. */
  detailMaxWidth?: number;
  /** OPTIONAL — detail view: render the bespoke Maintenance Setup "Task Checklist"
   *  scheduling grid (Link/Unlink over dbo.MaintenanceTaskCheckList) full-width
   *  below the form. Only set on `set-maintenance-setup`; the feature is specific
   *  enough that it is its own component rather than a generic child grid. */
  taskChecklistGrid?: boolean;
  /** OPTIONAL — detail view: render the bespoke Assign Tests To Item "Tests" tab
   *  (Add · Delete · row-inline Matrix / Amount / Units over dbo.ItemTestMethodLink).
   *  Its edits are BUFFERED and go out with the header Save, as the legacy detail
   *  commits the whole collection on Save. Only set on `set-assign-tests-to-item`. */
  itemTestsGrid?: boolean;
  /** OPTIONAL — detail view: render the bespoke Daily QC Log Book Builder "Field
   *  Configuration" dual list (Available fields from dbo.DailyQcDataTable | add /
   *  remove | the template's own dbo.DailyQCLogBookBuilderSelected rows, with the
   *  Selected grid inline-editable). Only set on `set-dailyqc-logbook`. Needs a
   *  SAVED header — a selected row is keyed to the template's Oid. */
  dailyQcFieldConfig?: boolean;
  /** OPTIONAL — detail view: render every `date` field as the shared
   *  <UsDateInput> (text shown and typed as MM/DD/YYYY, native calendar behind a
   *  button) instead of a bare `<input type="date">`.
   *
   *  A native date input renders its text in the BROWSER's locale, which cannot be
   *  overridden, so a viewer on a non-US locale sees `21-12-2024` where standing
   *  rule 3 requires `12/21/2024` (user report 2026-09-24). Opt-in per form so the
   *  ~37 date fields on other settings forms are untouched until each is migrated
   *  as its own reviewed change. */
  usDateFormat?: boolean;
  /** OPTIONAL — record-scoped actions on the detail action bar (Renew Permit).
   *  Shown only on a SAVED record: they act on something that must already exist. */
  recordActions?: CrudRecordAction[];
  /** OPTIONAL — render the recordActions BEFORE Edit/Delete (view mode) instead of
   *  after. Opt-in per form for a legacy bar that places its form-specific action
   *  ahead of the standard ones — e.g. DB Connection's "Test Connection" sits before
   *  Edit (owner request 2026-10-02). Default (false) keeps them after Edit/Delete. */
  recordActionsFirst?: boolean;
  /** OPTIONAL — detail view: read-only reference tables rendered full-width below
   *  the fields (and below any assocgrid). See {@link CrudStaticSection}. */
  staticSections?: CrudStaticSection[];
  /** OPTIONAL — detail view: editable child-collection grids. See {@link CrudChildGrid}. */
  childGrids?: CrudChildGrid[];
  /** OPTIONAL — render `childGrids` as ONE tabbed panel (a tab strip, one grid
   *  visible at a time) instead of the default: every grid stacked down the page.
   *
   *  This is the legacy XAF `TabbedGroup` look — several child collections sharing
   *  one grid area at the bottom of the detail. Reuses the same `.sd-assoc-tab*`
   *  tab styling the assocgrid field already uses, so a tab strip looks the same
   *  wherever it appears (standing rule 9).
   *
   *  Applies from ONE grid upward — legacy uses a TabbedGroup even for a single
   *  collection (Items' lone "Sub Item" tab), and the tab is what visually binds
   *  the grid to the form instead of leaving it floating under a bare heading.
   *  The tab replaces the grid's own `<h3>` heading (hidden by the CSS), so the
   *  title is never shown twice.
   *
   *  OPT-IN, and deliberately so: Instrument (Certificates + Attachments), Help
   *  Center (Attachments + Version Log) and the others render stacked today, and
   *  their legacy screens are tabbed too — but flipping them is a visible change
   *  to each of those forms and belongs to its own reviewed change, not to this
   *  one. First form: Vendors (user request 2026-09-23, "need a single grid G5").
   *
   */
  childGridsTabbed?: boolean;
  /** OPTIONAL — open New/Edit in the shared <ModuleCrudDialog> POPUP instead of
   *  the full-page <SettingsDetailPage>.
   *
   *  Standing rule 12 makes the full-page detail the default for every CRUD
   *  form, and it stays the default — this is an explicit per-form opt-out for
   *  a screen whose approved design is a compact modal (Settings ▸ Permit
   *  Library New ▸ Permit Type New, owner-specified 2026-08-27). Set it ONLY
   *  when the form owner has signed off on the popup; a two-or-three-field
   *  lookup is the only shape it suits. */
  dialogEdit?: boolean;
  /** OPTIONAL — fixed title for the <ModuleCrudDialog> popup, used VERBATIM for
   *  New / Edit / View (no "New "/"Edit " prefix). Matches a legacy XAF DetailView
   *  whose caption is the same in every mode (Document Creator → Attachments shows
   *  "Document Attachment"). Omitted, the title is `New/Edit {entityLabel}`. */
  dialogTitle?: string;
  /** POPUP ONLY, all opt-in (first use: Permit Library ▸ Permits, owner 2026-10-13 — "standard and
   *  captions like G5"). `dialogHeading` prints a heading above the fields; `dialogSaveLabel` renames
   *  the Save button; `dialogStandard` draws the fields to the detail-view standard — captions end in
   *  a colon, and controls are square, 24px tall and plain white. Unset = the popup is unchanged. */
  dialogHeading?: string;
  dialogSaveLabel?: string;
  dialogStandard?: boolean;
  /** POPUP ONLY, opt-in (first use: Permit Library ▸ Permits, owner request 2026-10-15): the shared popup
   *  standard — solid orange title bar, white title, a white round close button, and the footer as
   *  OK · Cancel. Unset = the popup is unchanged. */
  dialogAccent?: boolean;
  /** OPTIONAL — hide the shared List/Detail segmented switch (`ViewToggle`) on
   *  BOTH the list and the detail. The toggle is the BO UI Standard default on
   *  every CRUD module; set this only where the form owner wants it gone (New /
   *  the row Edit pencil still open the full-page detail, and Cancel / Save &
   *  Close / breadcrumb return to the list). */
  hideViewToggle?: boolean;
  /** OPTIONAL — legacy "plain list" chrome (ALPACA XAF parity, e.g. Qualifier).
   *  Bundles the behaviours a legacy list has and a modern CRUD list does not:
   *  shows the breadcrumb path (Home ▸ Group ▸ Form) INSTEAD of the List/Detail
   *  segmented toggle; a bare row-click does NOT open the detail (only the row
   *  Edit-pencil, or checkbox + toolbar Edit, opens it); and the keyboard grid-nav
   *  focus ring (the orange active-row outline) is off. New / Edit / Delete +
   *  the checkbox + Edit-pencil all stay. Opt-in per module. */
  plainList?: boolean;
  /** OPTIONAL — detail view: the field key whose value captions the record in the
   *  header/breadcrumb ("{entityLabel} – {value}"), mirroring the legacy XAF
   *  <c>[DefaultProperty]</c>. For a 'select' field the option label is shown
   *  (not the raw id), and the caption appears even on a NEW record once the
   *  field has a value. Default (unset) uses the first field. */
  titleField?: string;
}

/** Email To/CC/Bcc recipient grid columns — the legacy XAF Email recipient list over
 *  dbo.Contact (Photo omitted: a binary blob cannot render in a text grid). Order matches
 *  the ALPACA recipient grid; served by GET /api/v1/emails/contact-rows. */
const EMAIL_RECIPIENT_COLS: AssocColumn[] = [
  { key: "fullName", label: "FullName", width: 160 },
  { key: "firstName", label: "FirstName", width: 110 },
  { key: "middleName", label: "MiddleName", width: 100 },
  { key: "lastName", label: "LastName", width: 110 },
  { key: "gender", label: "Gender", width: 80 },
  { key: "birthday", label: "Birthday", width: 110, date: true },
  { key: "customer", label: "Customer", width: 170 },
  { key: "position", label: "Position", width: 130 },
  { key: "account", label: "Account", width: 120 },
  { key: "email", label: "Email", width: 210 },
  { key: "officePhone", label: "OfficePhone", width: 120 },
  { key: "homePhone", label: "HomePhone", width: 120 },
  { key: "mobilePhone", label: "MobilePhone", width: 120 },
  { key: "otherPhone", label: "OtherPhone", width: 120 },
  // Full Contact-column parity with the legacy Email_To/CC/Bcc_ListView (owner request
  // 2026-10-12, image 468) — FK/enum resolved server-side; bits render Yes/No; dates
  // via fmtLocalDate. (The PICKER keeps the narrow 4-column set below.)
  { key: "fax", label: "Fax", width: 120 },
  { key: "street1", label: "Street1", width: 160 },
  { key: "street2", label: "Street2", width: 160 },
  { key: "country", label: "Country", width: 120 },
  { key: "state", label: "State", width: 120 },
  { key: "city", label: "City", width: 120 },
  { key: "zip", label: "Zip", width: 90 },
  { key: "maritalStatus", label: "MaritalStatus", width: 120 },
  { key: "spouseName", label: "SpouseName", width: 140 },
  { key: "anniversary", label: "Anniversary", width: 110, date: true },
  { key: "prospect", label: "Prospect", width: 90, bool: true },
  { key: "prospectClient", label: "ProspectClient", width: 160 },
  { key: "jobTitle", label: "JobTitle", width: 140 },
  { key: "prospects", label: "Prospects", width: 140 },
  { key: "comment", label: "Comment", width: 200 },
  { key: "terms", label: "Terms", width: 120 },
  { key: "sampleReceiptNotification", label: "SampleReceiptNotification", width: 190, bool: true },
  { key: "reportDelivery", label: "ReportDelivery", width: 120, bool: true },
  { key: "invoiceDelivery", label: "InvoiceDelivery", width: 120, bool: true },
  { key: "type", label: "Type", width: 130 },
  { key: "retiredBy", label: "RetiredBy", width: 150 },
  { key: "retiredDate", label: "RetiredDate", width: 110, date: true },
];

// The Contact Link PICKER shows ONLY these 4 columns (legacy Contact_LookupListView:
// FullName · Street1 · Phone(=OfficePhone) · Email) — narrower than the recipient grid
// above, which keeps the full Contact set. (owner request 2026-10-12, image 466.)
const EMAIL_PICKER_COLS: AssocColumn[] = [
  { key: "fullName", label: "FullName", width: 180 },
  { key: "street1", label: "Street1", width: 200 },
  { key: "officePhone", label: "Phone", width: 140 },
  { key: "email", label: "Email", width: 240 },
];

/** Permit Library: show the renewal workflow (Expiration Date, Expired Permit Action, Renewal Due
 *  Days, Renewal Workflow State, Renewed From / To, Display Status, and the Renew Permit / Activate
 *  Renewal actions). OFF since 2026-10-13 — the detail shows exactly the legacy field set (owner
 *  decision). The fields stay in the record either way; this only decides whether they are drawn. */
const PERMIT_RENEWAL_UI = false;

export const MODULE_CRUD: Record<string, ModuleCrudConfig> = {
  // Settings → Method.  Mirrors the ALPACA "Method" screen toolbar
  // (New / Delete) plus per-row Edit, with audit on update/delete.
  "set-methods": {
    basePath: "/api/v1/methods",
    idField: "oid",
    formName: "Method",
    entityLabel: "Method",
    // G5 shows two edit icons per row, in the same column: the row editor (yellow pencil, Save/Cancel on
    // the row) and the full detail form (blue pencil). pencilRowEdit makes the first one the row editor.
    pencilRowEdit: true,
    detailIcon: true,
    // G5 draws the two icons in SEPARATE columns (owner request 2026-10-14): row editor | open detail.
    detailIconColumn: true,
    // G5 Method_DetailView is an editable XAF view (no read-only setting in the model), so an
    // existing record opens in edit mode with the Save trio — not a read-only view with Edit/Close.
    detailOpenInEdit: true,
    // G5 New Method form: 20 px single-line boxes and a 544 px field. The label column is 124 px so the
    // longest caption ("Method Number:*") is not cut to "Method Numbe...".
    compactControls: true,
    horizontalLabelWidth: 124,
    // Shared opt-in: square control corners + plain white editable boxes (no yellow tint).
    squareControls: true,
    sideLabels: true,   // AI Standard side-label layout (owner request 2026-10-02)
    singleColumn: true, // one control per row; caps field width (incl. Comment/Requirement) to the column
    // Method supports bulk CSV load (Import + Template toolbar buttons).
    bulkLoad: true,
    // Settings family strip (BO UI Standard §4).  Method is the active entry;
    // the three siblings aren't built as forms yet (they notify on click).
    relatedLookups: [
      { id: "set-method-category", label: "Method Category" },
      { id: "set-method-number", label: "Method Number" },
      { id: "set-standard-clause", label: "Standard Clause" },
      { id: "set-methods", label: "Method" },
    ],
    // Field order mirrors the ALPACA "Method" detail form.
    fields: [
      // Same height as every other box on the form (owner request 2026-10-15) — no taller memo box.
      { key: "methodName", label: "Method", type: "textarea", required: true },
      { key: "methodNumber", label: "Method Number", type: "text", required: true, maxLength: 200 },
      {
        // Keyed by the list column "category" (the name) and posted as its id "categoryId" (idKey), so the
        // row editor's Category dropdown lines up with the list column (G5 row edit shows the combo).
        key: "category",
        idKey: "categoryId",
        label: "Category",
        type: "select",
        inlineType: "select",
        optionsUrl: "/api/v1/methods/categories",
        noneLabel: "N/A",
        // Legacy XAF grid-lookup "New" button parity (owner request 2026-10-02): a "+"
        // beside the dropdown opens a popup to add a Method Category inline, POSTing to
        // the Method Category master's own endpoint, then selecting the new row.
        quickAdd: {
          createUrl: "/api/v1/method-categories",
          // Welded ＋ combo — the same control as Payment Terms "Terms" (owner request 2026-10-14, image 14).
          welded: true,
          title: "Method Category",
          icon: "ti-category",
          nameKey: "category",
          fields: [
            { key: "category", label: "Method Category", type: "text", required: true, maxLength: 100, requiredMessage: "Method Category is required." },
            { key: "description", label: "Description", type: "text", maxLength: 1000 },
          ],
        },
      },
      { key: "standardClause", label: "Standard Clause", type: "text" },
      { key: "clauseSubject", label: "Clause Subject", type: "text" },
      { key: "activeDate", label: "Active Date", type: "date" },
      { key: "dateIssued", label: "Date Issued", type: "date" },
      // G5 shows the code read-only with no note (Method.MethodCode is AllowEdit=False in the model).
      { key: "methodCode", label: "Method Code", type: "readonly" },
      // Requirement / Comment: same one-row height as the other boxes (owner request 2026-10-15).
      { key: "requirement", label: "Requirement", type: "textarea" },
      { key: "comment", label: "Comment", type: "textarea" },
      { key: "active", label: "Active", type: "checkbox" },
    ],
  },

  // Settings → Test Code.  Mirrors the ALPACA "Test Code" screen toolbar
  // (New / Edit / Delete + per-row pencil for inline-edit & open-detail) with
  // audit on update/delete (CLAUDE.md §14a Rule A).  Legacy entity is
  // Modules.BusinessObjects.Setting.TestMethod; this slice covers the General
  // tab fields. TestCode is server-generated (race-safe ISequentialIdGenerator,
  // CLAUDE.md §14a Rule B — replaces the legacy GetTestuqID sproc) and shown
  // read-only on edit. Tabs 2-17 (Surrogates, Internal Standards, Components,
  // Test Guides, Prep Methods, QC Types, Instruments, Department Chains,
  // Sample Prep Chains, Preservatives, Items) land in follow-up slices.
  "set-testcode": {
    basePath: "/api/v1/test-methods",
    idField: "oid",
    formName: "Test Code",
    entityLabel: "Test Code",
    sideLabels: true,   // AI Standard side-label layout (owner request 2026-10-02)
    singleColumn: true, // one control per row; caps field width (incl. Comment) to the column
    // Test Code uses the bespoke TestCodeFormDialog (tabs + pickers,
    // not the generic ModuleCrudDialog). Inline cell editing doesn't
    // make sense here — the row has too many fields and several use
    // pickers/lookups (Method/QC Types/Test Guides) that can't be
    // edited in a single grid row. Dialog edit only.
    inlineRowEdit: false,
    // Field order mirrors the LIMS TestMethod_DetailView General tab.
    fields: [
      { key: "testName", label: "Test", type: "text", required: true },
      {
        key: "matrixId",
        label: "Matrix",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/test-methods/matrices",
        noneLabel: "— Select —",
      },
      {
        key: "methodId",
        label: "Method",
        type: "select",
        optionsUrl: "/api/v1/test-methods/methods",
        noneLabel: "— Select —",
        hint: "Required unless this is a group test.",
      },
      { key: "category", label: "Category", type: "text" },
      {
        key: "departmentId",
        label: "Department",
        type: "select",
        optionsUrl: "/api/v1/test-methods/departments",
        noneLabel: "— None —",
      },
      { key: "isGroup", label: "Is Group", type: "checkbox" },
      { key: "isFieldTest", label: "Is Field Test", type: "checkbox" },
      // Derived in legacy (TestMethod.IsPLMTest, TestMethod.cs:1478) from the
      // single 'Asbestos_PLM' row of dbo.DataEntry — there is no column on the
      // test, and the legacy property has no setter, so legacy shows it only as
      // a read-only ListView column.  Editable here: saving writes back to that
      // ONE config row (AI_TestMethod_SetPlmTest_SP), so legacy XAF and Alpaca
      // Intelligence always agree about whether a batch routes to PLM or SDMS.
      // Name-centric like the legacy picker: it marks every test sharing this
      // test's name.
      {
        key: "isPlmTest",
        label: "Is PLM Test",
        type: "checkbox",
        hint: "Routes this test's batches to PLM instead of SDMS. Applies to every test with the same name.",
      },
      { key: "retireDate", label: "Retire Date", type: "date" },
      { key: "comment", label: "Comment", type: "textarea" },
      { key: "testCode", label: "Test Code", type: "readonly", hint: "Auto-generated on save" },
    ],
  },

  // Settings → SDMS → Formula Library.  Mirrors the ALPACA "SDMS Formulas"
  // detail form fields verbatim (screenshot 2026-07-28). The SHARED formula
  // DEFINITIONS used by both SDMS templates and Result Entry Formula — New /
  // Edit / Delete + audit; Delete is blocked server-side while a Result Entry
  // Formula mapping still references the row.
  formulalib: {
    basePath: "/api/v1/formula-library",
    idField: "uqId",
    formName: "Formula Library",
    entityLabel: "Formula",
    sideLabels: true,   // AI Standard side-label layout (owner request 2026-10-02)
    singleColumn: true, // one control per row; caps field width (incl. Script/Description) to the column
    // De-yellow the editable controls (plain white) + square corners (owner request
    // 2026-10-13, this form only). Read-only grey + required-empty yellow cues kept.
    squareControls: true,
    fields: [
      // Helper hint sub-labels removed (owner request 2026-10-16).
      { key: "formulaID", label: "Formula ID", type: "readonly" },
      { key: "formulaName", label: "Formula Name", type: "text", required: true, maxLength: 100 },
      { key: "kind", label: "Kind", type: "select", required: true, optionsUrl: "/api/v1/formula-library/kinds" },
      { key: "apiFunction", label: "Api Function", type: "text" },
      { key: "targetField", label: "Target Field", type: "text", required: true },
      { key: "series", label: "Series", type: "text", required: true },
      { key: "caption", label: "Caption", type: "text" },
      { key: "isActive", label: "Is Active", type: "checkbox" },
      { key: "script", label: "Script", type: "textarea" },
      { key: "description", label: "Description", type: "textarea" },
    ],
  },

  // Settings → Holidays.  Mirrors the ALPACA "Holidays" screen (HolidayName +
  // HolidayDate) with New / Edit / Delete + audit.
  "set-holidays": {
    basePath: "/api/v1/holidays",
    idField: "oid",
    formName: "Holidays",
    entityLabel: "Holiday",
    sideLabels: true,    // AI Standard Form Layout — label beside field (migrated 2026-10-02)
    singleColumn: true,  // one control per row (owner request 2026-10-02)
    rowDoubleClickView: true, // double-click a list row → open its detail (owner request 2026-10-02)
    // De-yellow the editable controls (plain white) + square corners (owner request
    // 2026-10-13, this form only). Read-only grey + required-empty yellow cues kept.
    squareControls: true,
    fields: [
      { key: "holidayName", label: "Holiday Name", type: "text", required: true },
      { key: "holidayDate", label: "Holiday Date", type: "date", required: true },
    ],
  },

  // Settings → Utility → Test → Container.  Mirrors the ALPACA
  // "Container" screen (entity Modules.BusinessObjects.Setting.Container).
  // Fields per the LIMS Container_DetailView: ContainerName (required,
  // unique), ContainerCode (optional), Comment (optional). The XAF
  // [RuleUniqueValue] on ContainerName is enforced server-side via the
  // ExistsByName check; client surfaces the 400 message as a toast.
  "set-container": {
    basePath: "/api/v1/containers",
    idField: "oid",
    formName: "Container",
    entityLabel: "Container",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      { key: "containerName", label: "Container", type: "text", required: true },
      { key: "containerCode", label: "Container Code", type: "text" },
      { key: "comment", label: "Comment", type: "textarea" },
    ],
  },

  // Settings → Utility → Test → Container Settings.  Full CRUD for
  // dbo.ContainerSettings — the per-Matrix/Test/Method container checklist.
  // Fields mirror the legacy ContainerSettings_DetailView: CSCode, Matrix,
  // Test (cascades from Matrix), Method (cascades from Test), Container
  // (multi-select, required), Default Container (filtered to selected
  // containers), Preservative (multi-select), HT Before Prep, HT Before
  // Analysis, Set Pre Time As Analysis Time (checkbox), Comment.
  "set-container-settings": {
    basePath: "/api/v1/container-settings",
    idField: "oid",
    formName: "Container Settings",
    entityLabel: "Container Setting",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      { key: "cpCode", label: "CS Code", type: "text" },
      { key: "matrixId", label: "Matrix", type: "select", optionsUrl: "/api/v1/container-settings/matrices" },
      {
        key: "testId",
        label: "Test",
        type: "select",
        optionsUrl: "/api/v1/container-settings/tests",
        dependsOn: "matrixId",
      },
      {
        key: "methodId",
        label: "Method",
        type: "select",
        optionsUrl: "/api/v1/container-settings/methods",
        dependsOn: "testId",
      },
      {
        key: "containerIds",
        label: "Container",
        type: "multiselect",
        optionsUrl: "/api/v1/container-settings/containers",
        required: true,
      },
      {
        key: "defaultContainerId",
        label: "Default Container",
        type: "select",
        optionsUrl: "/api/v1/container-settings/default-container-options",
        dependsOn: "containerIds",
      },
      {
        key: "preservativeIds",
        label: "Preservative",
        type: "multiselect",
        optionsUrl: "/api/v1/container-settings/preservatives",
      },
      {
        key: "htBeforePrepId",
        label: "HT Before Prep",
        type: "select",
        optionsUrl: "/api/v1/container-settings/holding-times",
      },
      {
        key: "htBeforeAnalysisId",
        label: "HT Before Analysis",
        type: "select",
        optionsUrl: "/api/v1/container-settings/holding-times",
      },
      { key: "setPreTimeAsAnalysisTime", label: "Set Pre Time As Analysis Time", type: "checkbox" },
      { key: "comment", label: "Comment", type: "textarea" },
    ],
  },

  // Settings → Data Package Review Checklist.  Mirrors the ALPACA
  // "DataPackageCheckList" screen (entity
  // Modules.BusinessObjects.DataPackage.DataPackageCheckList).
  // Fields per the legacy DataPackageCheckList_DetailView: CheckPoint (required,
  // unique), Department (text), Tests (multi-select TestMethod), QcRoles,
  // QcRootRoles, Default (Yes/No/NA), Sort, Comment.
  "data-package-checklist": {
    basePath: "/api/v1/data-package-checklists",
    idField: "oid",
    formName: "Data Package Review Checklist",
    entityLabel: "Check Point",
    inlineRowEdit: false,
    defaultPageSize: 20,
    detailShowDelete: true,
    // Per-row Edit pencil column next to the checkbox (owner request 2026-10-15,
    // reversing the 2026-09-29 removal): the pencil opens the record detail in EDIT
    // mode; a plain row click still opens the detail in VIEW mode (inlineRowEdit:false).
    // Detail-view standard (2026-09-29, standing rule 12 / BO UI Standard §4): label
    // BESIDE field via sideLabels; singleColumn keeps the fields in ONE stacked column
    // (the legacy DataPackageCheckList_DetailView order). The Test Methods assocgrid
    // renders full-width below the field grid.
    singleColumn: true,
    sideLabels: true,
    // De-yellow the editable controls (plain white) + square corners (owner request
    // 2026-10-12), matching the Bottle Sharing / Contract Lab / Reference Library
    // treatment; read-only grey and required-empty yellow cues are kept. This also
    // squares the QC Roles / QC Root Roles quick-add "+" box and turns it green.
    squareControls: true,
    // Hide the "General Information" section caption (user request 2026-08-26).
    hideSectionHeader: true,
    fields: [
      // Auto-generated on save (3-digit zero-padded) — display only; shows the
      // greyed "Autogenerate" placeholder like the legacy DetailView.
      { key: "checkPointId", label: "Check Point ID", type: "readonly", placeholder: "Autogenerate" },
      {
        key: "checkPoint",
        label: "Check Point",
        type: "text",
        required: true,
        requiredMessage: "'Check Point' must not be empty.",
      },
      // Department is a multi-select of dbo.Department (legacy CheckedListBox),
      // stored as the "; "-joined Department-Oid string (grid resolves to names).
      {
        key: "departments",
        label: "Department",
        type: "multiselect",
        optionsUrl: "/api/v1/data-package-checklists/departments",
        pageSize: 10,
        summarySeparator: "; ",
        hideOk: true,
      },
      // Test Methods — legacy Link/Unlink association grid (TestName · MethodName
      // · FormulaCount · IsFormulaActive). Stores the same "; "-joined
      // TestMethod-Oid list; rendered as a full-width grid section below the form.
      {
        key: "tests",
        label: "Test Methods",
        type: "assocgrid",
        optionsUrl: "/api/v1/data-package-checklists/test-method-rows",
        // Test · Method · FormulaCount · IsFormulaActive (owner request 2026-10-15:
        // removed the Matrix column, dropped the spaces in the Formula headers, and
        // render IsFormulaActive as a read-only checkbox via AssocColumn.checkbox;
        // `bool` is kept so the Export still writes Yes/No). Keys match the row JSON
        // (name/methodName/formulaCount/isFormulaActive); overrides the default set.
        assocColumns: [
          { key: "name", label: "TestName", width: 200 },
          { key: "methodName", label: "MethodName", width: 240 },
          { key: "formulaCount", label: "FormulaCount", width: 110 },
          { key: "isFormulaActive", label: "IsFormulaActive", width: 130, bool: true, checkbox: true },
        ],
        // The LINK picker shows only Matrix · Test · Method (legacy
        // TestMethod_LookupListView_CMDP parity) — Formula Count + Is Formula
        // Active belong to the linked-rows grid, not the lookup picker, so they
        // are dropped here (owner request 2026-10-12, image 481: those two
        // columns were showing additionally in the picker).
        assocPickerColumns: [
          { key: "matrix", label: "Matrix", width: 120 },
          { key: "name", label: "Test", width: 230 },
          { key: "methodName", label: "Method", width: 300 },
        ],
        // Legacy NPTest ASPxGridLookup parity (owner report 2026-10-15): the old
        // picker lists every candidate test with the already-linked ones pre-checked
        // and OK applies the whole selection, so the item count matches the old form
        // (it was short by the already-linked rows the add-only picker hid).
        assocPickerIncludesLinked: true,
        // Per-row edit pencil in the picker → opens the Test Method record in the
        // shared TestMethodDetailDialog (owner request 2026-10-15; the row id is the
        // TestMethod Oid). The old NPTest picker had no such column — this is an AI
        // addition the owner asked for.
        assocPickerTestMethodEdit: true,
      },
      {
        key: "qcRoles",
        label: "QC Roles",
        type: "select",
        optionsUrl: "/api/v1/data-package-checklists/qc-roles",
        noneLabel: "N/A",
        // Legacy XAF grid-lookup "New" button parity (owner request 2026-09-29): add a
        // QC Role inline via a popup, POSTing to the QC Role master's own endpoint.
        quickAdd: {
          createUrl: "/api/v1/qc-roles",
          title: "QC Role",
          icon: "ti-user-check",
          nameKey: "qcRoleName",
          welded: true,   // Payment Terms welded ＋ combo (owner report 2026-10-12, image 441)
          fields: [
            {
              key: "qcRoleName",
              label: "QC Role",
              type: "text",
              required: true,
              maxLength: 100,
              requiredMessage: "QC Role must not be empty.",
            },
          ],
        },
      },
      {
        key: "qcRootRoles",
        label: "QC Root Roles",
        type: "select",
        optionsUrl: "/api/v1/data-package-checklists/qc-root-roles",
        noneLabel: "N/A",
        // Legacy XAF grid-lookup "New" button parity (owner request 2026-09-29): add a
        // QC Root Role inline via a popup, POSTing to the QC Root Role master endpoint.
        quickAdd: {
          createUrl: "/api/v1/qc-root-roles",
          title: "QC Root Role",
          icon: "ti-user-check",
          nameKey: "qcRootRole",
          welded: true,   // Payment Terms welded ＋ combo (owner report 2026-10-12, image 441)
          fields: [
            {
              key: "qcRootRole",
              label: "QC Root Role",
              type: "text",
              required: true,
              maxLength: 100,
              requiredMessage: "QC Root Role must not be empty.",
            },
          ],
        },
      },
      // Legacy default: Sort = 0.
      { key: "sort", label: "Sort", type: "text", defaultValue: "0" },
      // Legacy DefaultOption enum default = Yes.
      {
        key: "defaultOption",
        label: "Default",
        type: "select",
        options: [
          { id: "Yes", name: "Yes" },
          { id: "No", name: "No" },
          { id: "NA", name: "N/A" },
        ],
        defaultValue: "Yes",
        noneLabel: "— None —",
      },
      { key: "comment", label: "Comment", type: "textarea" },
    ],
  },

  // Settings ▸ Data Package ▸ DB Connection — basic port of the ALPACA "DB Connection"
  // screen (entity Modules.BusinessObjects.DataPackage.DBConnection). Master of external
  // DB connections over the legacy XAF-shared dbo.DBConnection (owner request 2026-09-29).
  // Password stored PLAINTEXT to match the shared column (owner decision "match legacy
  // exactly"); rendered as a plain text field for now (a masked 'password' field type is a
  // later shared-component addition). Detail-view standard: label BESIDE field, single column.
  // Settings ▸ E-mail ▸ Email — the email outbox/log (dbo.Email). Basic port
  // (owner request 2026-09-30). Status is system-managed (shown in the list, not
  // edited here; preserved on save). Body is the shared rich-text editor.
  // Settings ▸ E-mail ▸ Email — legacy XAF Email_DetailView (owner report 2026-09-30,
  // images 270/271): From · Subject · Attachment (file) · Status (read-only radio,
  // system-set on Send) · rich Body. The To/CC/Bcc recipient grid + Send are a later
  // increment (pending the legacy source).
  "set-email": {
    basePath: "/api/v1/emails",
    idField: "oid",
    formName: "Email",
    entityLabel: "Email",
    inlineRowEdit: false,
    detailShowDelete: true,
    // De-yellow + square-corner the detail inputs (owner request 2026-10-12, this form only).
    squareControls: true,
    singleColumn: true,
    sideLabels: true,
    fields: [
      // From = the signed-in user's email, read-only (legacy Email_DetailView, image 276).
      { key: "from", label: "From", type: "text", maxLength: 100, readOnly: true, defaultCurrentUserEmail: true },
      { key: "subject", label: "Subject", type: "text", required: true, maxLength: 100, requiredMessage: "Subject must not be empty." },
      {
        key: "attachment", label: "Attachment", type: "file", fileNameKey: "attachmentFileName",
        uploadUrl: "/api/v1/emails/file/upload", downloadUrlBase: "/api/v1/emails/file",
      },
      // Status is system-set on Send (None / Success / Failed) — read-only radio, as legacy.
      {
        key: "status", label: "Status", type: "radio", readOnly: true, defaultValue: "0",
        options: [{ id: "0", name: "None" }, { id: "1", name: "Success" }, { id: "2", name: "Failed" }],
      },
      { key: "body", label: "Body", type: "richtext" },
      // Recipients — To / CC / Bcc, one tabbed panel (legacy Email recipient tabs, many-to-many
      // over dbo.Contact). Link opens the Contact picker; the whole set is saved on Save. The
      // picker has a per-row Edit pencil (assocPickerContactEdit) that opens the Contact for
      // edit (owner request 2026-10-15, image 638).
      { key: "to", label: "To", type: "assocgrid", assocTabGroup: "recipients", assocPickerTitle: "Contact", optionsUrl: "/api/v1/emails/contact-rows", assocColumns: EMAIL_RECIPIENT_COLS, assocPickerColumns: EMAIL_PICKER_COLS, assocPickerContactEdit: true },
      { key: "cc", label: "CC", type: "assocgrid", assocTabGroup: "recipients", assocPickerTitle: "Contact", optionsUrl: "/api/v1/emails/contact-rows", assocColumns: EMAIL_RECIPIENT_COLS, assocPickerColumns: EMAIL_PICKER_COLS, assocPickerContactEdit: true },
      { key: "bcc", label: "Bcc", type: "assocgrid", assocTabGroup: "recipients", assocPickerTitle: "Contact", optionsUrl: "/api/v1/emails/contact-rows", assocColumns: EMAIL_RECIPIENT_COLS, assocPickerColumns: EMAIL_PICKER_COLS, assocPickerContactEdit: true },
    ],
    // Send — dispatch through the Company-SMTP transport, then Status flips (legacy Email "Send").
    recordActions: [
      {
        key: "send", label: "Send", icon: "📤", path: "send", primary: true,
        confirmTitle: "Send Email", confirmMessage: "Send this email to its recipients?",
        successMessage: "Email sent successfully.",
      },
    ],
  },

  // Settings ▸ E-mail ▸ Email Content Template — Subject + rich Body + category flags
  // (dbo.EmailContentTemplate). Basic port (owner request 2026-09-30); mirrors the
  // eNotification Content Template shape.
  // Settings ▸ E-mail ▸ Email Content Template — legacy XAF EmailContentTemplate_DetailView
  // (owner report 2026-09-30, images 255-258): Division (dbo.Company) · BO (dbo.DefaultSetting,
  // shown "Module|Item") · Subject · rich Body · the Data Source merge-token panel. The legacy
  // list + detail do NOT show the Samplecheckin/Reporting/Invoice/SubOutSample flags, so they
  // were removed from this form (DB columns preserved). The list columns (BO · Subject · Data
  // Source) come from ModuleRegistry("set-email-content-template").
  "set-email-content-template": {
    basePath: "/api/v1/email-content-templates",
    idField: "oid",
    formName: "Email Content Template",
    entityLabel: "Email Content Template",
    inlineRowEdit: false,
    detailShowDelete: true,
    // De-yellow + square-corner the detail inputs (owner request 2026-10-12, this form only).
    squareControls: true,
    singleColumn: true,
    sideLabels: true,
    fields: [
      { key: "division", label: "Division", type: "select", required: true, optionsUrl: "/api/v1/email-content-templates/divisions", requiredMessage: "Division must not be empty.", defaultFirstOption: true },
      { key: "bo", label: "BO", type: "select", required: true, optionsUrl: "/api/v1/email-content-templates/business-objects", requiredMessage: "BO must not be empty." },
      { key: "subject", label: "Subject", type: "text", required: true, maxLength: 100, requiredMessage: "Subject must not be empty." },
      { key: "body", label: "Body", type: "richtext" },
      {
        key: "datasource", label: "Data Source", type: "datasource", insertIntoKey: "body",
        // Fixed merge-field set (legacy Data Source panel — no backing table).
        options: ["[IMG]", "[Invoice#]", "[JobID]", "[ProjectID]", "[ProjectName]", "[ReceivedDate]", "[ReportID]", "[SuboutID]", "[TAT]"]
          .map((t) => ({ id: t, name: t })),
      },
    ],
  },

  "set-dbconnection": {
    basePath: "/api/v1/db-connections",
    idField: "oid",
    formName: "DB Connection",
    entityLabel: "DB Connection",
    inlineRowEdit: false,
    detailShowDelete: true,
    singleColumn: true,
    sideLabels: true,
    // De-yellow editable controls (plain white) + square corners (owner request
    // 2026-10-12), matching the Bottle Sharing / Contract Lab / Reference Library /
    // Data Package Review Checklist treatment; read-only grey + required-empty yellow
    // cues kept.
    squareControls: true,
    fields: [
      {
        key: "title",
        label: "Title",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Title must not be empty.",
      },
      // All five fields required, matching the legacy DB Connection detail (owner
      // request 2026-09-29, image 194 — every label carries a *). The "Default"
      // checkbox was removed the same day (legacy has no such control); IsDefault
      // stays in the table and defaults to false on insert.
      { key: "serverName", label: "Server Name", type: "text", required: true, maxLength: 100, requiredMessage: "Server Name must not be empty." },
      { key: "dataBaseName", label: "Database Name", type: "text", required: true, maxLength: 100, requiredMessage: "Database Name must not be empty." },
      { key: "userName", label: "User Name", type: "text", required: true, maxLength: 100, requiredMessage: "User Name must not be empty." },
      { key: "password", label: "Password", type: "text", required: true, maxLength: 100, requiredMessage: "Password must not be empty." },
    ],
    // Legacy DBConnectionViewController "Test Connection" action — opens a live SQL
    // connection to the entered Server/Database/User/Password and reports the result
    // ("DB connection testing successfully." / the SQL error). sendFormValues: tests
    // the CURRENT form values (works before Save, like legacy's View.CurrentObject),
    // so it shows in View AND New/Edit, before Edit/Save (owner request 2026-10-02).
    recordActions: [
      {
        key: "test-connection",
        label: "Test Connection",
        icon: "🔌",
        path: "test-connection",
        sendFormValues: true,
        confirmTitle: "Test Connection",
        confirmMessage: "Test this database connection?",
        successMessage: "DB connection testing successfully.",
      },
    ],
    recordActionsFirst: true,   // Test Connection sits before Edit / Save (owner request)
  },

  // Settings ▸ EDD ▸ EDD Builder — full List → Detail CRUD (owner request 2026-10-02:
  // New/Edit/Delete + editable header + manage queries). Standard SettingsDetailPage
  // (editable header, columnMajor + sideLabels = AI Standard layout) + a "Query Details"
  // child grid whose row opens the EDDQuery Builder editor dialog (edd-query childKey).
  // Per-row Edit pencil column shown next to the row checkbox (owner request 2026-10-12,
  // opens the record detail in edit mode); a plain row click still opens the detail.
  // inlineRowEdit:false makes a row click open the Detail View. boClass "B".
  // Field order is column-major: the first 5 fill the LEFT column, the next 5 the RIGHT,
  // reproducing the legacy header layout (EDD ID·Name·Description·Category·Client | Date
  // Created·Author·Active·Retire·Remark).
  "set-eddbuilder": {
    basePath: "/api/v1/edd-builders",
    idField: "oid",
    formName: "EDD Builder",
    entityLabel: "EDD Builder",
    inlineRowEdit: false,
    // De-yellow + square-corner the detail inputs (owner request 2026-10-12, this form only).
    squareControls: true,
    columnMajor: true,
    sideLabels: true,
    fields: [
      { key: "eddId", label: "EDD ID", type: "readonly", placeholder: "Autogenerated" },
      { key: "eddName", label: "EDD Name", type: "text", required: true, maxLength: 100, requiredMessage: "EDD Name must not be empty." },
      { key: "description", label: "Description", type: "text", maxLength: 250 },
      {
        key: "category",
        label: "Category",
        type: "select",
        optionsUrl: "/api/v1/edd-builders/categories",
        // Legacy XAF grid-lookup "New" button parity (owner report 2026-10-13, image 488):
        // the legacy EDDBuilder.Category reference lookup has a New button (no AllowNew=False),
        // so weld a "+" into the combo to add an EDD Category inline — posts to the EDD
        // Category master, then re-reads this dropdown and selects the new row. No new backend.
        quickAdd: {
          createUrl: "/api/v1/edd-categories",
          title: "Category",
          icon: "ti-category",
          nameKey: "categoryName",
          welded: true,
          fields: [
            {
              key: "categoryName",
              label: "Category Name",
              type: "text",
              required: true,
              maxLength: 100,
              requiredMessage: "Category Name must not be empty.",
            },
          ],
        },
      },
      { key: "client", label: "Client", type: "select", optionsUrl: "/api/v1/edd-builders/clients" },
      // Date Created + Author are server-stamped on save (GETDATE() + current Employee).
      // On a NEW record, preview them read-only (today + the signed-in user's display name,
      // = what the reader resolves Author to) so the fields aren't blank (owner report
      // 2026-10-13: "values not loading when adding new data").
      { key: "dateCreated", label: "Date Created", type: "date", readOnly: true, defaultNow: true },
      { key: "author", label: "Author", type: "text", readOnly: true, defaultCurrentUserName: true },
      { key: "active", label: "Active", type: "checkbox" },
      { key: "retire", label: "Retire", type: "checkbox" },
      { key: "remark", label: "Remark", type: "text", maxLength: 500 },
    ],
    childGrids: [
      {
        title: "Query Details",
        listUrl: "/api/v1/edd-builders/{id}/queries",
        childKey: "edd-query",
        parentField: "eddBuilder",
        listReturnsFullRecord: true,
        bordered: true,          // frame the whole Query Details section (owner request 2026-10-02)
        rowOpensDialog: true,    // click a row → open the EDDQuery Builder popup (owner request 2026-10-02)
        rowEditIcon: true,       // per-row Edit pencil next to the selection box (owner request 2026-10-15)
        paged: true,             // standard <GridPager> footer — Page X of N · prev/next · Page size (owner request 2026-10-16, image 737)
        columns: [
          { key: "queryName", label: "QueryName", width: 220 },
          { key: "sheetName", label: "SheetName", width: 180 },
          // QueryBuilder holds the full SQL — clamp it to one line so rows stay
          // compact + the widths stay stable (full SQL on hover / in the popup).
          { key: "queryBuilder", label: "QueryBuilder", width: 420, ellipsis: true },
        ],
      },
    ],
  },

  // Settings ▸ Field Template ▸ Field App Table — simple master CRUD over the int-keyed
  // dbo.tbl_FlutterSDA_FieldEntryColumn_New (owner request 2026-10-02: add List/Detail +
  // New/Edit/Delete). idField "oid" = the INT identity (as a string in URLs). boClass A.
  "set-flutter-fieldapptable": {
    basePath: "/api/v1/field-app-table",
    idField: "oid",
    formName: "Field App Table",
    entityLabel: "Field App Table",
    inlineRowEdit: false,
    hideEditPencil: true,
    // De-yellow + square-corner the detail inputs (owner request 2026-10-12, this form only).
    squareControls: true,
    sideLabels: true,
    singleColumn: true,  // one control per row (owner request 2026-10-02)
    // Field order + required markers mirror the legacy Field App Table detail
    // (owner request 2026-10-02): Field Name* · Type* · Size* · Mapped Property* · Description.
    fields: [
      { key: "fieldName", label: "Field Name", type: "text", required: true, maxLength: 200, requiredMessage: "Field Name must not be empty." },
      { key: "dataType", label: "Type", type: "text", required: true, maxLength: 100, requiredMessage: "Type must not be empty." },
      { key: "size", label: "Size", type: "text", required: true, maxLength: 50, requiredMessage: "Size must not be empty." },
      { key: "mappedProperty", label: "Mapped Property", type: "text", required: true, maxLength: 200, requiredMessage: "Mapped Property must not be empty." },
      { key: "description", label: "Description", type: "text", maxLength: 400 },
    ],
  },

  // Settings ▸ Field Template ▸ Field Template Builder — header CRUD over the int-keyed
  // dbo.SDATemplate (realigned from dbo.SDATemplate_New 2026-10; the list + detail now read
  // the SAME table, see ModuleRegistry — reading different tables caused "Could not load this
  // field template", fixed 2026-10-16). Template ID auto ("001"…); Category is the
  // TemplateCategory enum (one value, Field Entry = 0). singleColumn — one control per row.
  "set-flutter-field-template-builder": {
    basePath: "/api/v1/field-templates",
    idField: "oid",
    formName: "Field Template Builder",
    entityLabel: "Field Template",
    inlineRowEdit: false,
    // Per-row Edit PENCIL column shown next to the row checkbox (owner request 2026-10-13):
    // hideEditPencil removed so the pencil renders; a click opens the record detail in edit mode.
    // De-yellow + square-corner the detail inputs (owner request 2026-10-12, this form only).
    squareControls: true,
    sideLabels: true,
    singleColumn: true,
    fields: [
      { key: "templateId", label: "Template ID", type: "readonly", placeholder: "Autogenerate" },
      { key: "templateName", label: "Template Name", type: "text", required: true, maxLength: 200, requiredMessage: "Template Name must not be empty." },
      { key: "category", label: "Category", type: "select", options: [{ id: "0", name: "Field Entry" }], defaultValue: "0" },
      { key: "isActive", label: "Is Active", type: "checkbox" },
    ],
  },

  // Flutter Template Builder — a read-only orphan duplicate of Field Template Builder,
  // wired up with the SAME full CRUD + List/Detail toggle at owner request 2026-10-16.
  // Same /api/v1/field-templates controller + dbo.SDATemplate data, so edits from either
  // form audit under "Field Template Builder".
  "set-flutter-template-builder": {
    basePath: "/api/v1/field-templates",
    idField: "oid",
    formName: "Field Template Builder",
    entityLabel: "Field Template",
    inlineRowEdit: false,
    squareControls: true,
    sideLabels: true,
    singleColumn: true,
    fields: [
      { key: "templateId", label: "Template ID", type: "readonly", placeholder: "Autogenerate" },
      { key: "templateName", label: "Template Name", type: "text", required: true, maxLength: 200, requiredMessage: "Template Name must not be empty." },
      { key: "category", label: "Category", type: "select", options: [{ id: "0", name: "Field Entry" }], defaultValue: "0" },
      { key: "isActive", label: "Is Active", type: "checkbox" },
    ],
  },

  // EDD Builder → Query Details child dialog (the "EDDQuery Builder" popup). Opened from
  // the set-eddbuilder childGrid; the owning EDD (eddBuilder) is injected via parentField.
  // QueryBuilder is the full SQL (textarea). Audited under FormName "EDD Builder".
  "edd-query": {
    basePath: "/api/v1/edd-queries",
    idField: "oid",
    formName: "EDD Builder",
    entityLabel: "Query",
    inlineRowEdit: false,
    fields: [
      // Labels carry a trailing colon to match the legacy EDDQueryBuilder popup
      // ("Query Name:*", "Sheet Name:*") — owner request 2026-10-15, image 625.
      { key: "queryName", label: "Query Name:", type: "text", required: true, maxLength: 100, requiredMessage: "Query Name must not be empty." },
      { key: "sheetName", label: "Sheet Name:", type: "text", maxLength: 100 },
      // All three are editable, matching the legacy EDDQueryBuilder_DetailView_SheetEDD
      // popup (QueryName · SheetName · QueryBuilder are all editable PropertyEditors,
      // RowCount=20 — no AllowEdit="False"). The SQL IS edited here (owner follow-up
      // 2026-10-02: "not editable only" = the highlighted name/sheet are NOT the only
      // editable controls — the Query Builder is editable too). The writer persists +
      // audits QueryBuilder on update.
      { key: "queryBuilder", label: "Query Builder:", type: "textarea" },
    ],
  },

  // Settings ▸ Document Management ▸ Document Creator — basic port of the ALPACA
  // "Document Creator" screen (entity Modules.BusinessObjects.Setting.Manual). Controlled
  // documents / SDS / manuals over the legacy XAF-shared dbo.Manual (owner request
  // 2026-09-29). Header fields per the legacy DetailView; the Attachments child grid
  // (dbo.DocumentAttachment, a file-upload child collection) is a deferred increment.
  "set-documentcreator": {
    basePath: "/api/v1/documents",
    idField: "oid",
    formName: "Document Creator",
    entityLabel: "Document",
    // Project Notes button REMOVED from the Document Creator detail (owner request
    // 2026-10-15, image 620). The backend routes still exist (DocumentsController over
    // dbo.Notes) and the Retired Document detail still shows notes view-only; this form
    // simply no longer renders the button (projectNotesBase omitted → projectNotes = null
    // in SettingsDetailPage).
    // Renewal Date must read MM/DD/YYYY whatever the viewer's browser locale is
    // (standing rule 3). Verified 2026-10-12: without this a non-US browser showed
    // "15-03-2027" for 03/15/2027 — the stored value was right, the reading was not.
    // The flag is opt-in per form "until each is migrated as its own reviewed
    // change"; this module's forms are that change.
    usDateFormat: true,
    inlineRowEdit: false,
    detailShowDelete: true,
    // Per-row Edit pencil column restored next to the row checkbox (owner request
    // 2026-10-12) — opens the record's detail in edit mode. Supersedes the
    // 2026-09-29 decision to drop it (so `hideEditPencil` is no longer set).
    // De-yellow + square-corner the detail inputs (owner request 2026-10-12, this form only).
    squareControls: true,
    // Detail layout matches the legacy Manual DetailView (owner 2026-09-29): 2-column
    // columnMajor + sideLabels — LEFT = Document ID · Name* · Sub Name · Category* ·
    // Description; RIGHT = Version Number · Rev Number(ro) · Renewal Date* · Alert ·
    // Is Retire (existing-only) · Reason for Retiring (shown when Is Retire is ticked).
    // columnRows is LEFT UNSET (owner request 2026-10-15): the Is Retire / Reason fields
    // are conditionally visible, so the 2-column split must follow the VISIBLE count
    // (ceil(n/2)) — a fixed 5 would spill the 11th cell (the reason) into a 3rd column
    // the moment the box is ticked. 9 visible (new) → 5/4, 10 → 5/5, 11 (retired) → 6/5.
    columnMajor: true,
    sideLabels: true,
    fields: [
      // ── LEFT column ──
      // Document ID is AUTO-GENERATED server-side ("DOC"+N from DOC1001, legacy
      // Manual.OnSaving format, race-safe — §14a Rule B) and permanent thereafter, like
      // the legacy ModelDefault AllowEdit=False. Read-only; the API ignores any client value.
      { key: "documentID", label: "Document ID", type: "readonly", placeholder: "Autogenerated" },
      {
        key: "name",
        label: "Name",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Name must not be empty.",
      },
      { key: "subName", label: "Sub Name", type: "text", maxLength: 100 },
      {
        key: "category",
        label: "Category",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/documents/categories",
        noneLabel: "— None —",
        requiredMessage: "Category must not be empty.",
        // Legacy XAF grid-lookup "New" button parity (owner request 2026-09-29): add a
        // Document Category inline via a popup (Name* + Description), POSTing to the
        // Category master endpoint — no new backend.
        quickAdd: {
          createUrl: "/api/v1/document-categories",
          title: "Category",
          icon: "ti-category",
          nameKey: "name",
          // Weld the + INSIDE the combo ([＋][▼]) instead of a separate button beside
          // it (owner request 2026-10-12) — matches the Payment Terms combo.
          welded: true,
          fields: [
            {
              key: "name",
              label: "Name",
              type: "text",
              required: true,
              maxLength: 100,
              requiredMessage: "Name must not be empty.",
            },
            { key: "description", label: "Description", type: "text", maxLength: 100 },
          ],
        },
      },
      { key: "description", label: "Description", type: "textarea", maxLength: 2000 },
      // ── RIGHT column ──
      { key: "versionNumber", label: "Version Number", type: "text", maxLength: 100 },
      // Read-only: the ACTIVE/latest attachment's RevNumber (resolved server-side), like legacy.
      { key: "revNumber", label: "Rev Number", type: "readonly" },
      // Legacy declares [RuleRequiredField] on Renewal Date. We show the red "*"
      // (owner request 2026-10-15) but do NOT hard-enforce it (`requiredStar`, not
      // `required`): every live document has RenewalDate = NULL (6/6 verified), so
      // blocking save on it would make every existing record un-saveable (hit and
      // reverted once before). Owner picked "star only, no save block". The field is
      // editable; the form's squareControls keep it white rather than yellow.
      { key: "renewalDate", label: "Renewal Date", type: "date", requiredStar: true },
      { key: "alert", label: "Alert", type: "checkbox" },
      // "Is Retire" RESTORED next to Alert (owner request 2026-10-15, reversing the
      // 2026-10-12 removal). Legacy Manual_DetailView shows it ONLY on an EXISTING record
      // (appearance rule `hideIsRetire` hides it when `[IsNew] = True`) and reveals
      // ReasonForRetiring only once it is ticked (`ReasonShow`/`Reasonhide` on
      // `[IsRetire] = True`/`false`). documentID is empty on a NEW record (the id is minted
      // server-side), so it is the "is-existing" signal. The retire WRITE path already
      // exists end-to-end — DocumentInput.IsRetire/ReasonForRetiring, the writer stamping
      // DateRetired/RetiredBy, and DocumentsController R3 (active file required) + R4
      // (reason required) — so this is a frontend-only restore.
      { key: "isRetire", label: "Is Retire", type: "checkbox", visibleWhen: (v) => !!v.documentID },
      // [Size(1000)] in legacy; [RuleRequiredField] there only when IsRetire=True. The
      // shared required-check is NOT visibleWhen-aware (a hidden required field would block
      // every normal save), so this stays optional and the API's R4
      // ("Please provide the reason for retiring.") enforces it when the box is ticked.
      { key: "reasonForRetiring", label: "Reason for Retiring", type: "text", maxLength: 1000, visibleWhen: (v) => v.isRetire === true },
    ],
    // Attachments child grid (owner request 2026-09-29) — the document revisions/files
    // (legacy dbo.DocumentAttachment). New/Edit via the set-documentattachment dialog; files
    // to the shared dbo.FileData store. A true parent-child collection (not linkable).
    childGrids: [
      {
        title: "Attachments",
        listUrl: "/api/v1/document-attachments/by-document/{id}",
        childKey: "set-documentattachment",
        parentField: "manual",
        listReturnsFullRecord: true,
        // Rev Number is derived server-side on create ("Rev" + sibling count + 1, the
        // legacy AfterConstruction rule — DocumentAttachmentReaderWriter). seqField makes
        // the UI SHOW that value up front (owner report 2026-10-15: on a NEW, unsaved
        // document a buffered attachment left the RevNumber column blank). It fills the
        // next "Rev{n}" into the New dialog AND into the buffered row's grid cell, so it
        // reads "Rev1" immediately like legacy; the server still assigns it authoritatively
        // on persist (the readonly field is never sent). No effect once the document is
        // saved (attachments then POST directly and the server value is shown).
        seqField: { key: "revNumber", prefix: "Rev" },
        // Owner request 2026-09-30: no Export/Maximize header icons on this grid, and
        // box the whole Attachments section with a border.
        hideHeaderTools: true,
        bordered: true,
        columns: [
          { key: "revNumber", label: "RevNumber", width: 110 },
          {
            key: "attachmentFileName",
            label: "Attachment",
            width: 200,
            type: "file",
            downloadUrlBase: "/api/v1/document-attachments/file",
            fileOidKey: "attachment",
          },
          { key: "comment", label: "Comment", width: 200 },
          { key: "date", label: "Date", width: 120, type: "date" },
          { key: "author", label: "Author", width: 150 },
          { key: "expireDate", label: "ExpireDate", width: 120, type: "date" },
          // IsActive renders as a read-only checkbox, not the raw "true"/"false" text.
          { key: "isActive", label: "IsActive", width: 90, type: "checkbox" },
        ],
      },
    ],
  },

  // Document Creator → Attachments child dialog (New/Edit one attachment). Opened from the
  // set-documentcreator childGrid; the parent document (manual) is injected via parentField.
  // Popup matches the legacy XAF "Document Attachment" DetailView (owner request 2026-10-12):
  // fixed title "Document Attachment"; field order Rev Number · Attachment · Expire Date ·
  // Comment · Is Active; Rev Number read-only, derived server-side on create ("Rev" +
  // sibling count + 1 — DocumentAttachmentReaderWriter), shown as "Autogenerated".
  "set-documentattachment": {
    basePath: "/api/v1/document-attachments",
    idField: "oid",
    formName: "Document Attachment",
    entityLabel: "Attachment",
    dialogTitle: "Document Attachment",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      // Parent document Oid — injected via the child grid's parentField, kept in the
      // payload but never rendered.
      { key: "manual", label: "Document", type: "text", hideInLayout: true },
      // Rev Number: server-derived on create as "Rev" + (live sibling count + 1) — the
      // legacy DocumentAttachment.AfterConstruction rule, computed race-safely inside the
      // write transaction (DocumentAttachmentReaderWriter); the client value is ignored.
      // Read-only, shows "Autogenerated" on New. Kept first per the owner's 2026-10-12
      // field order (Rev Number · Attachment · Expire Date · Comment · Is Active).
      { key: "revNumber", label: "Rev Number", type: "readonly", placeholder: "Autogenerated" },
      {
        key: "attachment",
        label: "Attachment",
        type: "file",
        fileNameKey: "attachmentFileName",
        required: true,
        uploadUrl: "/api/v1/document-attachments/file/upload",
        downloadUrlBase: "/api/v1/document-attachments/file",
      },
      { key: "expireDate", label: "Expire Date", type: "date" },
      { key: "comment", label: "Comment", type: "textarea", maxLength: 1000 },
      // Ticking this deactivates every other attachment on the document (exclusive, R1) —
      // enforced in the same transaction server-side.
      { key: "isActive", label: "Is Active", type: "checkbox" },
    ],
  },

  // Settings ▸ Document Management ▸ Retired Document — the legacy Manual DetailView
  // as a READ-ONLY view (owner request 2026-09-29; the 2026-09-30 "make it editable"
  // change was REVERTED on owner request). A retired document is never created / edited /
  // deleted here, so: detailReadOnly (no New/Edit/Delete toolbar, no Save trio, greyed
  // fields) + detailHideEdit (no Edit button — pure view) + NO basePath, so the shared
  // static-list adapter renders the detail straight from the already-loaded list row (no
  // extra endpoint). Row-click opens it (inlineRowEdit:false). Layout mirrors image 186:
  // LEFT = Document ID · Name · Sub Name · Description · Category · Remark; RIGHT = Version
  // Number · Rev Number · Is Retire · Date Retired · Retired By · Reason For Retiring.
  // Attachments child grid is view-only (hideToolbar + bordered). Un-retire is the
  // Reactivate action (list toolbar + per-row ↻), not an edit.
  "set-retireddocument": {
    idField: "oid",
    formName: "Retired Document",
    // The Audit chip was MISSING here: this config has no `basePath` (static-list
    // adapter), and the chip defaulted off for that. Retire / Reactivate ARE audited —
    // under "Document Creator", so a document's whole lifecycle stays in ONE trail
    // (the Reporting-lifecycle precedent). Point the chip there. (Standing rule 11.)
    auditFormName: "Document Creator",
    // Project Notes, VIEW-ONLY: a retired document's notes are history to read,
    // not a place to keep writing. Same routes, same dbo.Notes rows as Document
    // Creator — one document, one set of notes.
    projectNotesBase: "/api/v1/documents/{id}/project-notes",
    projectNotesReadOnly: true,
    // Date Retired reads MM/DD/YYYY on every locale (standing rule 3) — same
    // reviewed change as Document Creator above.
    usDateFormat: true,
    entityLabel: "Retired Document",
    detailReadOnly: true,
    detailHideEdit: true,
    inlineRowEdit: false,
    hideEditPencil: true,
    columnMajor: true,
    sideLabels: true,
    columnRows: 6,
    fields: [
      // ── LEFT column ──
      { key: "documentID", label: "Document ID", type: "text" },
      { key: "name", label: "Name", type: "text" },
      { key: "subName", label: "Sub Name", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "category", label: "Category", type: "text" },
      { key: "remark", label: "Remark", type: "textarea" },
      // ── RIGHT column ──
      { key: "versionNumber", label: "Version Number", type: "text" },
      { key: "revNumber", label: "Rev Number", type: "text" },
      { key: "isRetire", label: "Is Retire", type: "checkbox" },
      { key: "dateRetired", label: "Date Retired", type: "date" },
      { key: "retiredBy", label: "Retired By", type: "text" },
      { key: "reasonForRetiring", label: "Reason For Retiring", type: "textarea" },
    ],
    // Attachments — the document's revision files (legacy dbo.DocumentAttachment).
    // Same columns as the Document Creator grid; view-only here (no New/Edit/Delete).
    childGrids: [
      {
        title: "Attachments",
        listUrl: "/api/v1/document-attachments/by-document/{id}",
        childKey: "set-documentattachment",
        parentField: "manual",
        listReturnsFullRecord: true,
        // Read-only view — no New/Edit/Delete. Owner request 2026-09-30: drop the
        // whole (now action-less) toolbar row so no blank strip shows, and box the
        // section with a border to match the Document Creator Attachments grid.
        hideNew: true,
        hideEdit: true,
        hideDelete: true,
        hideToolbar: true,
        bordered: true,
        columns: [
          { key: "revNumber", label: "RevNumber", width: 110 },
          {
            key: "attachmentFileName",
            label: "Attachment",
            width: 200,
            type: "file",
            downloadUrlBase: "/api/v1/document-attachments/file",
            fileOidKey: "attachment",
          },
          { key: "comment", label: "Comment", width: 200 },
          { key: "date", label: "Date", width: 120, type: "date" },
          { key: "author", label: "Author", width: 150 },
          { key: "expireDate", label: "ExpireDate", width: 120, type: "date" },
          // IsActive renders as a read-only checkbox, not the raw "true"/"false" text.
          { key: "isActive", label: "IsActive", width: 90, type: "checkbox" },
        ],
      },
    ],
  },

  // Settings → Analysis → Contract Lab.  Mirrors the ALPACA "Contract Lab"
  // screen (entity Modules.BusinessObjects.SuboutTracking.SubOutContractLab).
  // Fields per the legacy SubOutContractLab_DetailView: ContractLabName
  // (required, unique) + EmailID (required); the rest are optional. The
  // Certificate is a FileData attachment (upload/download via the shared
  // 'file' field type + the certificate upload/download endpoints).
  "set-contractlab": {
    basePath: "/api/v1/contract-labs",
    idField: "oid",
    formName: "Contract Lab",
    entityLabel: "Contract Lab",
    inlineRowEdit: false,
    // Detail flow (legacy parity): a ROW click opens read-only (view + Edit
    // button); the Edit pencil / toolbar Edit opens editable.
    detailShowDelete: true,
    // Detail-view standard (2026-09-29, standing rule 12 / BO UI Standard §4): label
    // BESIDE field via columnMajor + sideLabels, arranged to match the legacy
    // SubOutContractLab_DetailView — LEFT column = lab identity + address, RIGHT column
    // = contact + web + certificate + deactivate + comment (columnRows:8 splits the 15
    // fields 8/7, exactly as the legacy form). Replaces the earlier default 2-column
    // label-above layout.
    columnMajor: true,
    sideLabels: true,
    columnRows: 8,
    // Square, un-coloured controls (owner request 2026-10-12: remove the editable-yellow
    // background + rectangular/square corners, THIS form only). Shared .sd-grid--square
    // variant — editable boxes plain white at rest, all control boxes border-radius 0,
    // read-only grey + required-empty yellow cues preserved.
    squareControls: true,
    fields: [
      // ── LEFT column: lab identity + address ──
      {
        key: "contractLabName",
        label: "Contract Lab",
        type: "text",
        required: true,
        requiredMessage: "'Contract Lab' must not be empty.",
      },
      { key: "accreditationID", label: "Accreditation ID", type: "text" },
      { key: "address1", label: "Address 1", type: "text" },
      { key: "address2", label: "Address 2", type: "text" },
      { key: "city", label: "City", type: "text" },
      { key: "state", label: "State", type: "text" },
      { key: "zip", label: "Zip", type: "text" },
      { key: "country", label: "Country", type: "text" },
      // ── RIGHT column: contact + web + certificate + status ──
      { key: "contact", label: "Contact", type: "text" },
      { key: "phone", label: "Phone", type: "text" },
      { key: "emailID", label: "Email ID", type: "text", required: true, requiredMessage: "'Email ID' must not be empty." },
      { key: "webSite", label: "Web Site", type: "text" },
      // Certificate — file attachment stored in dbo.FileData (FK on
      // SubOutContractLab.Certificate). Two-phase upload: pick a file → POST
      // upload → Oid stored in `certificate` on Save; download via the Oid.
      {
        key: "certificate",
        label: "Certificate",
        type: "file",
        fileNameKey: "certificateFileName",
        uploadUrl: "/api/v1/contract-labs/certificate/upload",
        downloadUrlBase: "/api/v1/contract-labs/certificate",
      },
      { key: "deactivate", label: "Deactivate", type: "checkbox" },
      { key: "comment", label: "Comment", type: "textarea" },
    ],
  },

  // Settings → Analysis → Bottle Sharing.  Mirrors the ALPACA "Bottle Sharing"
  // screen (entity Modules.BusinessObjects.Setting.BottleSharing).  Fields per
  // the legacy BottleSharing_DetailView: SharingCode (auto-generated, read-only),
  // SampleMatrix (VisualMatrix FK), Matrix (FK), Test (multi-select TestMethod,
  // required).  The legacy form cascades SampleMatrix → Matrix → Test; here the
  // three pickers are independent selects (the shared detail renderer has no
  // cascade support) — same columns + same stored data (Tests persists as the
  // "; "-joined TestMethod-Oid string).
  "set-bottlesharing": {
    basePath: "/api/v1/bottle-sharings",
    idField: "oid",
    formName: "Bottle Sharing",
    entityLabel: "Bottle Sharing",
    inlineRowEdit: false,
    // Detail flow (legacy parity): a ROW click opens read-only (view + Edit
    // button); the Edit pencil / toolbar Edit opens editable. Delete shows in
    // both view and edit toolbars.
    detailShowDelete: true,
    // Detail-view standard (2026-09-29, standing rule 12 / BO UI Standard §4): label
    // BESIDE field via sideLabels; singleColumn keeps the four fields in ONE stacked
    // column (Sharing Code → Sample Matrix → Matrix → Test — the cascade order), owner
    // request 2026-09-29 "move the right controls to the left side, one by one".
    singleColumn: true,
    sideLabels: true,
    // Square, un-coloured controls (owner request 2026-10-12: remove the editable-yellow
    // background + use rectangular/square corners, THIS form only). The shared
    // .sd-grid--square variant does both — editable boxes plain white at rest, all control
    // boxes border-radius 0 — while keeping the read-only grey and required-empty cues.
    squareControls: true,
    // Header caption = Sample Matrix (legacy [DefaultProperty("SampleMatrix")] —
    // "Bottle Sharing - Drinking Water").
    titleField: "sampleMatrix",
    fields: [
      // Auto-generated on save (3-digit zero-padded) — display only; shows the
      // greyed "Autogenerated" placeholder like the legacy form.
      { key: "sharingCode", label: "Sharing Code", type: "readonly", placeholder: "Autogenerated" },
      {
        key: "sampleMatrix",
        label: "Sample Matrix",
        type: "select",
        optionsUrl: "/api/v1/bottle-sharings/sample-matrices",
        noneLabel: "N/A",
      },
      // Cascade (legacy BottleSharing): Matrix is filtered by the chosen Sample
      // Matrix; Test is filtered by the chosen Matrix. Changing a parent clears
      // its children and reloads their options.
      {
        key: "matrix",
        label: "Matrix",
        type: "select",
        dependsOn: "sampleMatrix",
        optionsUrl: "/api/v1/bottle-sharings/matrices",
        noneLabel: "N/A",
      },
      {
        key: "tests",
        label: "Test",
        type: "multiselect",
        required: true,
        dependsOn: "matrix",
        pageSize: 10,
        summarySeparator: "; ",
        hideOk: true,
        requiredMessage: "Tests must not be empty.",
        optionsUrl: "/api/v1/bottle-sharings/tests",
      },
    ],
  },

  // Settings → Utility → Test → Preservative.  Mirrors the ALPACA
  // "Preservative" screen (entity Modules.BusinessObjects.Setting.Preservative).
  // Fields per LIMS Preservative_DetailView: PreservativeName (required,
  // unique — XAF [RuleRequiredField] + [RuleUniqueValue]), PreservativeCode
  // (optional), Comment (optional, max 1000). Comment is hidden in LIMS
  // list view but visible in detail.
  "set-preservative": {
    basePath: "/api/v1/preservatives",
    idField: "oid",
    formName: "Preservative",
    entityLabel: "Preservative",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      { key: "preservativeName", label: "Preservative", type: "text", required: true },
      { key: "preservativeCode", label: "Preservative Code", type: "text" },
      { key: "comment", label: "Comment", type: "textarea" },
    ],
  },

  // Settings → Utility → Audit Trail → Reason for Change.  Mirrors the ALPACA
  // "AuditTrailReasontoChange" screen. Topic (required, unique), Global (bool),
  // Module + BO (free-text scope).
  "set-reasonforchange": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: four fields are too few to fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/reason-for-change",
    idField: "oid",
    formName: "Reason for Change",
    entityLabel: "Reason for Change",
    inlineRowEdit: false,
    fields: [
      { key: "topic", label: "Topic", type: "text", required: true, requiredMessage: "Topic must not be empty." },
      { key: "global", label: "Global", type: "checkbox" },
      { key: "module", label: "Module", type: "text" },
      { key: "bo", label: "BO", type: "text" },
    ],
  },

  // Settings → Utility → Client.  Mirrors the ALPACA "Client" screen (simple
  // Setting.Client master). Client Name required + unique.
  "set-client": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column, matching G5, which stacks both fields with the label beside.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/setting-clients",
    idField: "oid",
    formName: "Client",
    entityLabel: "Client",
    inlineRowEdit: false,
    fields: [
      {
        key: "clientName",
        label: "Client Name",
        type: "text",
        required: true,
        requiredMessage: "Client Name must not be empty.",
      },
      // Auto-generated (race-safe MAX+1, §14a Rule B), zero-padded to 3 digits
      // ("001") to match the legacy XAF DisplayFormat "{0:000}" and the list column.
      // READ-ONLY: previewUrl shows the id a New save will most likely mint, and the
      // reader returns existing rows already formatted, so the field shows "001" in
      // view/edit too. The server ignores any posted value and mints its own.
      { key: "clientUniqueId", label: "Client Unique ID", type: "readonly", previewUrl: "/api/v1/setting-clients/next-unique-id" },
    ],
  },

  // Settings → Utility → Client Account Number.  Mirrors the ALPACA
  // "ClientAccountNumber" screen. Customer (FK, required), Account Number
  // (required, unique per customer), Default + Suspend bools.
  "set-clientaccountnumber": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column, matching G5, which stacks all four with the label beside and
    // the two checkboxes under the fields.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/client-account-numbers",
    idField: "oid",
    formName: "Client Account Number",
    entityLabel: "Client Account Number",
    inlineRowEdit: false,
    fields: [
      {
        key: "customer",
        label: "Customer",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/client-account-numbers/customers",
        noneLabel: "— Select —",
        requiredMessage: "'Customer' must not be empty.",
      },
      {
        key: "accountNumber",
        label: "Account Number",
        type: "text",
        required: true,
        maxLength: 50,
        requiredMessage: "'Account Number' must not be empty.",
      },
      { key: "default", label: "Default", type: "checkbox" },
      { key: "suspend", label: "Suspend", type: "checkbox" },
    ],
  },

  // Settings → Utility → Analysis → Asbestos → Fiber Colors.  Mirrors the ALPACA
  // "FiberColors" screen. Single required FiberColor field (legacy caption "FiberColor").
  "set-fibercolors": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-24), which the rule requires over a silent side-effect.
    // ONE column: too few fields to fill a 50/50 split (owner, 2026-09-24).
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/fiber-colors",
    idField: "oid",
    formName: "Fiber Colors",
    entityLabel: "Fiber Color",
    inlineRowEdit: false,
    fields: [
      {
        key: "fiberColor",
        label: "Fiber Color",
        type: "text",
        required: true,
        requiredMessage: "Fiber Color must not be empty.",
      },
    ],
  },

  // Settings → Utility → Analysis → Asbestos → PLM Sample Notes.  Mirrors the
  // ALPACA "PLMSampleComments" screen. Single required Sample Comment field.
  // Settings → Utility → Analysis → Asbestos → PLM Dispersion Oil (2026-10-08).
  // The RI oils the PLM Result Entry header picks from (the lab's DispersionOil
  // list): Name — the refractive index, e.g. "1.550" — and Lot Number, which is
  // what makes a layer read with the oil traceable. Display Name is how the oil
  // reads wherever it is picked ("<name> - <lot number>"); the server works it
  // out, so it is read-only here and fills in once the record is saved. No
  // required field and no duplicate rule — G5's DispersionOil has none (owner
  // 2026-10-17, G5 parity). maxLength 100 is the column size, which G5's XAF
  // editor also caps typing at.
  "set-plm-dispersion-oil": {
    // Standard detail layout (standing rule 12): side labels; ONE column, as its
    // sibling PLM Sample Notes — two fields do not fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/plm-dispersion-oils",
    idField: "oid",
    formName: "PLM Dispersion Oil",
    entityLabel: "PLM Dispersion Oil",
    inlineRowEdit: false,
    fields: [
      { key: "name", label: "Name", type: "text", maxLength: 100 },
      { key: "lotNumber", label: "Lot Number", type: "text", maxLength: 100 },
      { key: "displayName", label: "Display Name", type: "text", readOnly: true },
    ],
  },

  // Settings → Utility → Analysis → Asbestos → PLM Instrument Setup (G5 parity,
  // 2026-10-15). One row per station instrument: Station (the instrument's
  // Location — a drop-down of the Asbestos-PLM instruments) and its four devices.
  // The PLM Result Entry device drop-downs list these rows; picking one fills the
  // other three devices and the layer's station.
  "set-plm-instrument-setup": {
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/plm-instrument-setups",
    idField: "oid",
    formName: "PLM Instrument Setup",
    entityLabel: "PLM Instrument Setup",
    inlineRowEdit: false,
    fields: [
      {
        key: "instrumentOid",
        label: "Station",
        type: "select",
        optionsUrl: "/api/v1/plm-instrument-setups/stations",
        required: true,
        requiredMessage: "Select the Station.",
      },
      { key: "plmMicroscope", label: "PLM Microscope", type: "text", maxLength: 100 },
      { key: "stereoMicroscope", label: "Stereo Microscope", type: "text", maxLength: 100 },
      { key: "hotPlate", label: "Hot Plate", type: "text", maxLength: 100 },
      { key: "thermohygrometer", label: "Thermohygrometer", type: "text", maxLength: 100 },
    ],
  },

  "set-plm-sample-notes": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-24), which the rule requires over a silent side-effect.
    // ONE column: too few fields to fill a 50/50 split (owner, 2026-09-24).
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/plm-sample-notes",
    idField: "oid",
    formName: "PLM Sample Notes",
    entityLabel: "PLM Sample Note",
    inlineRowEdit: false,
    fields: [
      {
        key: "sampleComment",
        label: "Sample Comment",
        type: "textarea",
        required: true,
        requiredMessage: "Sample Comment must not be empty.",
      },
    ],
  },

  // Settings → Utility → Analysis → Daily Qc → Data Table.  Mirrors the ALPACA
  // "DailyQcDataTable" screen. FieldName (required, unique), FieldType / Size /
  // MappedProperty (required), Default (bool), Comment (optional).
  "set-dailyqc-datatable": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column, matching G5, which stacks all six fields with the label beside.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/daily-qc-data-table",
    idField: "oid",
    formName: "Daily QC Data Table",
    entityLabel: "Data Table Field",
    inlineRowEdit: false,
    fields: [
      // Labels match the legacy BO [DisplayName]: FieldName→"Field", FieldType→"Type".
      { key: "fieldName", label: "Field", type: "text", required: true, requiredMessage: "Field must not be empty." },
      // G5 dropdowns (M13, DailyQcDataTableRules): Type + Mapped Property from fixed lists; Size
      // only matters for nvarchar — the API requires it there and clears it for any other Type.
      {
        key: "fieldType", label: "Type", type: "select", required: true, requiredMessage: "Type must not be empty.",
        options: ["bit", "datetime", "int", "nvarchar", "decimal"].map((t) => ({ id: t, name: t })),
      },
      {
        key: "size", label: "Size (nvarchar only)", type: "select",
        options: ["50", "100", "max"].map((s) => ({ id: s, name: s })),
      },
      {
        key: "mappedProperty",
        label: "Mapped Property",
        type: "select",
        required: true,
        requiredMessage: "Mapped Property must not be empty.",
        options: ["Checkbox", "CheckedComboBox", "ComboBox", "Date", "DateTime", "Text", "User Defined Pop-up Column", "Numeric", "Attachment"]
          .map((m) => ({ id: m, name: m })),
      },
      { key: "default", label: "Default", type: "checkbox" },
      { key: "comment", label: "Comment", type: "textarea" },
    ],
  },

  // Settings → Utility → Analysis → Daily Qc → Category.  Mirrors the ALPACA
  // "DailyQCCategory" screen. Category (required, unique), Comment (optional).
  "set-dailyqc-category": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: two fields are too few to fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/daily-qc-categories",
    idField: "oid",
    formName: "Daily QC Category",
    entityLabel: "Daily QC Category",
    inlineRowEdit: false,
    fields: [
      {
        key: "category",
        label: "Category",
        type: "text",
        required: true,
        requiredMessage: "Category must not be empty.",
      },
      { key: "comment", label: "Comment", type: "textarea" },
    ],
  },

  // Settings → Utility → Test → Temperature.  Mirrors the ALPACA
  // "Temperature" screen (entity Modules.BusinessObjects.Setting.Temperature).
  // Single field — TemperatureName (required, unique). The XAF entity has
  // no Comment, no Code, no audit columns — minimal master.
  "set-temperature": {
    basePath: "/api/v1/temperatures",
    idField: "oid",
    formName: "Temperature",
    entityLabel: "Temperature",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [{ key: "temperatureName", label: "Temperature Name", type: "text", required: true }],
  },

  // Settings → Utility → Test → QC Type.  Mirrors the ALPACA "QCType"
  // screen (entity Modules.BusinessObjects.Setting.QCType).  Six fields:
  // QCTypeName (required, unique), Comment (textarea, max 1000), three
  // FK lookups (QCSource, QcRole, QCRootRole), and the ExcludedReport
  // bit. Lookup options come from /api/v1/qc-types/lookups/{kind}.
  "set-qctype": {
    basePath: "/api/v1/qc-types",
    idField: "oid",
    formName: "QC Type",
    entityLabel: "QC Type",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "qcTypeName",
        label: "QC Type",
        type: "text",
        required: true,
        // LIMS QCType.cs:68 — [RuleRequiredField("QCTypeName", … , "QC Type must not be empty")]
        requiredMessage: "QC Type must not be empty.",
      },
      // LIMS uses "N/A" (not "— Select —") as the empty-state label on
      // these three dropdowns — matches the legacy XAF placeholder.
      {
        key: "qcRoleId",
        label: "QC Role",
        type: "select",
        optionsUrl: "/api/v1/qc-types/lookups/qc-role",
        noneLabel: "N/A",
        // LIMS XAFML overrides QcRole to a STRING editor in inline edit
        // (the QCType_ListView grid screenshot confirms QcRole is a text
        // cell, no dropdown chrome). Dialog stays a dropdown.
        inlineType: "text",
      },
      {
        key: "qcSourceId",
        label: "QC Source",
        type: "select",
        optionsUrl: "/api/v1/qc-types/lookups/qc-source",
        noneLabel: "N/A",
      },
      {
        key: "qcRootRoleId",
        label: "QC Root Role",
        type: "select",
        optionsUrl: "/api/v1/qc-types/lookups/qc-root-role",
        noneLabel: "N/A",
      },
      { key: "comment", label: "Comment", type: "textarea" },
      { key: "excludedReport", label: "Excluded Report", type: "checkbox" },
    ],
  },

  // Settings → Utility → Test → QC Role. Mirrors the ALPACA "QcRole"
  // screen (entity Modules.BusinessObjects.Setting.QcRole). The XAFML
  // override hides the four classifier bits from the detail view —
  // LIMS only surfaces QC Role name (verified screenshot 2026-06-30).
  // Backend still accepts the bits (they default to false on Create
  // and stay unchanged on Update when omitted from the payload).
  // Simple master — no inline row edit, dialog only.
  "set-qcrole": {
    basePath: "/api/v1/qc-roles",
    idField: "oid",
    formName: "QC Role",
    entityLabel: "QC Role",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "qcRoleName",
        label: "QC Role",
        type: "text",
        required: true,
        // LIMS QcRole.cs:25 — [RuleRequiredField("Enter the QC Role", …)]
        requiredMessage: "Enter the QC Role",
      },
    ],
  },

  // ── Settings → Reporting → MSR → Water System (merged team package
  // 2026-07-02).  Legacy dbo.MSRWaterSystem — SystemID is server-minted
  // (race-safe, §14a Rule B) and read-only; SystemName is unique (server-
  // enforced inside the mint transaction). ────────────────────────────────
  "msr-water-system": {
    basePath: "/api/v1/msr-water-system",
    idField: "oid",
    formName: "MSR Water System",
    entityLabel: "Water System",
    inlineRowEdit: false,
    // Detail-View standard (standing rule 12) — label BESIDE its field in a
    // fixed-width, left-aligned column, 26px controls (migrated 2026-09-26).
    // Single column so the three controls stack one per row (owner request
    // 2026-09-26): System ID, then System Name, then Description.
    singleColumn: true,
    sideLabels: true,
    // Detail-view standard from the 2026-10-06 review: square 24px boxes, no colour at rest.
    squareControls: true,
    fields: [
      { key: "systemId", label: "System ID", type: "readonly" },
      {
        key: "systemName",
        label: "System Name",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "System Name is required.",
      },
      { key: "description", label: "Description", type: "textarea" },
    ],
  },

  // ── Settings → Reporting → MSR → MSR Data Import Config (follow-up slice to
  // the 2026-07-02 MSR merge).  The New/Edit detail is the BESPOKE FULL-PAGE
  // <MsrDataImportConfigDetail> — five type-to-search multi-select GUID pickers
  // + an ImportMode radio that the generic CrudField types can't express — so
  // ModuleListView special-cases the 'msr-data-import-config' key (same as 'msr')
  // and `fields` stays empty (the generic field editor / inline edit never
  // render).  It renders FULL-PAGE (side-label standard, standing rule 12), NOT a
  // popup — the old <MsrDataImportConfigDialog> was removed 2026-09-26.  This
  // entry still drives the toolbar New/Edit/Delete wiring, the audit FormName, and
  // bulk delete (DELETE {basePath}/{oid} soft-deletes the config; writer sets
  // GCRecord). ─────────────────────────────────────────────────────────────────
  "msr-data-import-config": {
    basePath: "/api/v1/msr-data-import-config",
    idField: "oid",
    formName: "MSR Data Import Config",
    entityLabel: "Data Import Config",
    inlineRowEdit: false,
    fields: [],
  },

  // ── Settings → Dashboard → Assign Dashboard To User (ported from the ALPACA
  // AssignDashboardToUserDepartment_ListView).  New/Edit opens the SHARED
  // full-page <SettingsDetailPage> (Standing rule 12 / BO UI Standard §4 — NOT a
  // popup), exactly like ALPACA's detail view: Dashboard View + Navigation Item
  // dropdowns, Role as a checked-list DROPDOWN ('multiselect', rendered via the
  // shared CheckedCombo — same interaction as the legacy XAF CheckedListBoxEditor),
  // and Is Active.  The Role field stores an array of RoleNavigationPermission
  // Oids (the backend joins them to "; " for the shared table); GET {id} returns
  // roleIds[] and POST/PUT accept roleIds[].  Audit + delete unchanged. ────────
  "set-assign-dashboard": {
    basePath: "/api/v1/assign-dashboard",
    idField: "oid",
    formName: "Assign Dashboard To User",
    entityLabel: "Dashboard Assignment",
    inlineRowEdit: false,
    fields: [
      {
        key: "dashboardViewId",
        label: "Dashboard View",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/assign-dashboard/dashboards",
        noneLabel: "Select a Dashboard View…",
        requiredMessage: "Dashboard View is required.",
      },
      {
        key: "navigationItemId",
        label: "Navigation Item",
        type: "select",
        optionsUrl: "/api/v1/assign-dashboard/navigation-items",
        noneLabel: "— None —",
      },
      {
        key: "roleIds",
        label: "Role",
        type: "multiselect",
        optionsUrl: "/api/v1/assign-dashboard/roles",
        noneLabel: "Select roles…",
      },
      { key: "isActive", label: "Is Active", type: "checkbox" },
    ],
  },

  // ── Reporting → Monthly Summary Repository (merged team package
  // 2026-07-02).  The New/Edit detail is the BESPOKE <MsrDetailDialog>
  // (Year/Month/Category + Retrieve + samples grid) — ModuleListView
  // special-cases the 'msr' key, so `fields` stays empty (the generic
  // field editor / inline edit never render).  This entry still drives the
  // toolbar New/Edit/Delete wiring, the audit FormName, and bulk delete
  // (DELETE {basePath}/{oid} soft-deletes the header + child samples). ───
  msr: {
    basePath: "/api/v1/monthly-summary-repository",
    idField: "oid",
    formName: "Monthly Summary Repository",
    entityLabel: "Monthly Summary",
    inlineRowEdit: false,
    fields: [],
  },

  // Sample Field Configuration (legacy VisualMatrix field config). ModuleListView
  // special-cases this key (INLINE_DETAIL_MODULES) — New/Edit render the bespoke
  // full-page SampleFieldConfigDetail (header + 3 tabs of available↔selected
  // fields), so `fields` stays empty. This entry drives the toolbar New/Edit/
  // Delete wiring + the audit FormName. (2026-08-25)
  "set-samplefieldconfig": {
    basePath: "/api/v1/sample-field-config",
    idField: "oid",
    formName: "Sample Field Configuration",
    entityLabel: "Sample Field Configuration",
    inlineRowEdit: false,
    fields: [],
  },

  // Sample Matrix Field Setup — legacy VisualMatrix_DetailView_FieldSetup over
  // the child dbo.SampleMatrixSetupFields (a SEPARATE table from Sample Field
  // Configuration's dbo.SamplingFieldConfiguration). Edit opens its OWN bespoke
  // detail (SampleMatrixFieldSetupDetail) — a single panel with columns
  // Name·Freeze·Width·Order·Caption — via /api/v1/sample-matrix-field-setup. The
  // earlier "reuse Sample Field Configuration detail" wiring (2026-09-26) showed
  // the wrong 3-tab screen and was corrected 2026-09-29 (owner report). List
  // columns come from the ModuleRegistry entry; Edit-only (no New / Delete),
  // matching the legacy toolbar.
  "set-sample-matrix-field-setup": {
    basePath: "/api/v1/sample-matrix-field-setup",
    idField: "oid",
    formName: "Sample Matrix Field Setup",
    entityLabel: "Sample Matrix Field Setup",
    inlineRowEdit: false,
    fields: [],
  },

  // ══════════════════════════════════════════════════════════════════════════
  //  Settings master-data lookups (merged team package 2026-07-02).
  //  Simple code-list CRUD over existing legacy dbo.* tables — each renders
  //  through the shared ModuleListView + SettingsDetailPage (New/Edit/Delete +
  //  audit on Update/Delete).  Grid list is served by the matching
  //  ModuleRegistry ModuleDef.  QC Role (set-qcrole) + Temperature
  //  (set-temperature) already exist above / from the MSR merge, so the
  //  package's duplicate verticals were skipped.
  // ══════════════════════════════════════════════════════════════════════════

  // Settings ▸ Utility ▸ Test ▸ Sample Category.
  "set-sample-category": {
    basePath: "/api/v1/sample-categories",
    idField: "oid",
    formName: "Sample Category",
    entityLabel: "Sample Category",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      { key: "sampleCategoryName", label: "Sample Category", type: "text", required: true, maxLength: 100 },
      { key: "comment", label: "Comment", type: "textarea", maxLength: 1000 },
    ],
  },

  // Settings ▸ Utility ▸ Test ▸ Units.
  "set-units": {
    basePath: "/api/v1/units",
    idField: "oid",
    formName: "Units",
    entityLabel: "Unit",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      { key: "unitName", label: "Unit", type: "text", required: true, maxLength: 100 },
      { key: "comment", label: "Comment", type: "textarea", maxLength: 1000 },
    ],
  },

  // Settings ▸ Utility ▸ Test ▸ Storage.
  "set-storage": {
    basePath: "/api/v1/storages",
    idField: "oid",
    formName: "Storage",
    entityLabel: "Storage",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      { key: "storageName", label: "Name", type: "text", required: true, maxLength: 200 },
      { key: "storageId", label: "Storage ID", type: "text", maxLength: 200 },
      { key: "comment", label: "Comment", type: "textarea", maxLength: 1000 },
    ],
  },

  // Settings ▸ Utility ▸ Test ▸ Method Category.
  "set-method-category": {
    basePath: "/api/v1/method-categories",
    idField: "oid",
    formName: "Method Category",
    entityLabel: "Method Category",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      { key: "category", label: "Method Category", type: "text", required: true, maxLength: 100 },
      { key: "description", label: "Description", type: "textarea", maxLength: 1000 },
    ],
  },

  // Settings ▸ Utility ▸ Test ▸ QC Source (ALPACA: not required, not unique).
  "set-qc-source": {
    basePath: "/api/v1/qc-sources",
    idField: "oid",
    formName: "QC Source",
    entityLabel: "QC Source",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [{ key: "qcSource", label: "QC Source", type: "text", maxLength: 200 }],
  },

  // Settings ▸ Utility ▸ Test ▸ QC Root Role.
  "set-qc-root-role": {
    basePath: "/api/v1/qc-root-roles",
    idField: "oid",
    formName: "QC Root Role",
    entityLabel: "QC Root Role",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [{ key: "qcRootRole", label: "QC Root Role", type: "text", required: true, maxLength: 100 }],
  },

  // Settings ▸ Utility ▸ EDD Builder ▸ Category (legacy EDDCategory).
  "set-eddcategory": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: a single field cannot fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/edd-categories",
    idField: "oid",
    formName: "EDD Category",
    entityLabel: "EDD Category",
    inlineRowEdit: false,
    fields: [
      {
        key: "categoryName",
        label: "Category Name",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Category Name must not be empty.",
      },
    ],
  },

  // Settings ▸ Utility ▸ Glossary ▸ Category (legacy GlossaryCategory).
  "set-glossarycategory": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: two fields are too few to fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/glossary-categories",
    idField: "oid",
    formName: "Glossary Category",
    entityLabel: "Glossary Category",
    inlineRowEdit: false,
    fields: [
      {
        key: "categoryName",
        label: "Category Name",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Category name must not be empty.",
      },
      { key: "description", label: "Description", type: "textarea", maxLength: 200 },
    ],
  },

  // Settings ▸ Utility ▸ Internal Audit ▸ NCA ▸ Action Category (legacy ActionCategory).
  "set-actioncategory": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: a single field cannot fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/action-categories",
    idField: "oid",
    formName: "Action Category",
    entityLabel: "Action Category",
    inlineRowEdit: false,
    fields: [
      {
        key: "category",
        label: "Action Category",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Action Category must not be empty.",
      },
    ],
  },

  // Settings ▸ Utility ▸ Internal Audit ▸ NCA ▸ Problem Category (legacy ProblemCategory).
  "set-problemcategory": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: a single field cannot fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/problem-categories",
    idField: "oid",
    formName: "Problem Category",
    entityLabel: "Problem Category",
    inlineRowEdit: false,
    fields: [
      {
        key: "category",
        label: "Category",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "'Problem Category' must not be empty.",
      },
    ],
  },

  // Settings ▸ Utility ▸ Internal Audit ▸ NCA ▸ NCA Reason (legacy NCAReason).
  "set-ncareason": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: two fields are too few to fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/nca-reasons",
    idField: "oid",
    formName: "NCA Reason",
    entityLabel: "NCA Reason",
    inlineRowEdit: false,
    fields: [
      {
        key: "reason",
        label: "Reason",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "'Reason' must not be empty.",
      },
      { key: "discription", label: "Discription", type: "textarea" },
    ],
  },

  // ════ Settings ▸ Utility ▸ Inventory Management (ICM) simple masters ════
  "set-delivery-priority": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: three fields, and the legacy ICM detail views likewise stack their
    // fields in a 40%-wide group with the rest of the row left as an empty filler.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/delivery-priorities",
    idField: "oid",
    formName: "Delivery Priority",
    entityLabel: "Delivery Priority",
    inlineRowEdit: false,
    fields: [
      {
        key: "name",
        label: "Name",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Name must not be empty.",
      },
      { key: "sort", label: "Sort", type: "number" },
      { key: "description", label: "Description", type: "textarea", maxLength: 2000 },
    ],
  },
  "set-grade": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: two fields, and the legacy ICM detail views likewise stack their
    // fields in a 40%-wide group with the rest of the row left as an empty filler.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/grades",
    idField: "oid",
    formName: "Grade",
    entityLabel: "Grade",
    inlineRowEdit: false,
    fields: [
      {
        key: "grade",
        label: "Name",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Name must not be empty.",
      },
      { key: "description", label: "Description", type: "textarea", maxLength: 2000 },
    ],
  },
  "set-item-categories": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: two fields, and the legacy ICM detail views likewise stack their
    // fields in a 40%-wide group with the rest of the row left as an empty filler.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/item-categories",
    idField: "oid",
    formName: "Item Categories",
    entityLabel: "Item Category",
    inlineRowEdit: false,
    fields: [
      {
        key: "category",
        label: "Name",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "category must not be empty.",
      },
      { key: "description", label: "Description", type: "textarea", maxLength: 2000 },
    ],
  },
  "set-manufacturers": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: two fields, and the legacy ICM detail views likewise stack their
    // fields in a 40%-wide group with the rest of the row left as an empty filler.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/manufacturers",
    idField: "oid",
    formName: "Manufacturers",
    entityLabel: "Manufacturer",
    inlineRowEdit: false,
    fields: [
      {
        key: "manufacturerName",
        label: "Manufacturer",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Manufacturer must not be empty.",
      },
      { key: "description", label: "Description", type: "textarea", maxLength: 200 },
    ],
  },
  "set-package-units": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: four fields, and the legacy ICM detail views likewise stack their
    // fields in a 40%-wide group with the rest of the row left as an empty filler.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/package-units",
    idField: "oid",
    formName: "Package Units",
    entityLabel: "Package Unit",
    inlineRowEdit: false,
    fields: [
      {
        key: "option",
        label: "Name",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Name must not be empty.",
      },
      { key: "description", label: "Description", type: "textarea", maxLength: 2000 },
      // Sort + Comment removed from this form (owner request 2026-09-28) — Sort is
      // defaulted server-side; both are preserved (untouched) on update.
    ],
  },
  "set-icm-project": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: two fields, and the legacy ICM detail views likewise stack their
    // fields in a 40%-wide group with the rest of the row left as an empty filler.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/icm-projects",
    idField: "oid",
    formName: "ICM Project",
    entityLabel: "Project",
    inlineRowEdit: false,
    fields: [
      {
        key: "projectName",
        label: "Project",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "'Project' must not be empty.",
      },
      { key: "comment", label: "Comment", type: "textarea", maxLength: 2000 },
    ],
  },
  "set-shipping-options": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: four fields, and the legacy ICM detail views likewise stack their
    // fields in a 40%-wide group with the rest of the row left as an empty filler.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/shipping-options",
    idField: "oid",
    formName: "Shipping Options",
    entityLabel: "Shipping Option",
    inlineRowEdit: false,
    fields: [
      {
        key: "option",
        label: "Name",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Name must not be empty.",
      },
      { key: "description", label: "Description", type: "textarea", maxLength: 2000 },
      // Sort removed from this form (owner request 2026-09-28) — defaulted server-side,
      // preserved on update.
      { key: "comment", label: "Comment", type: "textarea", maxLength: 2000 },
    ],
  },
  "set-ship-via": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: four fields, and the legacy ICM detail views likewise stack their
    // fields in a 40%-wide group with the rest of the row left as an empty filler.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/ship-via",
    idField: "oid",
    formName: "Ship via",
    entityLabel: "Ship via",
    inlineRowEdit: false,
    fields: [
      {
        key: "option",
        label: "Name",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Name must not be empty.",
      },
      { key: "description", label: "Description", type: "textarea", maxLength: 2000 },
      // Sort removed from this form (owner request 2026-09-28) — defaulted server-side,
      // preserved on update.
      { key: "comment", label: "Comment", type: "textarea", maxLength: 2000 },
    ],
  },
  "set-icm-storage": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: four fields, and the legacy ICM detail views likewise stack their
    // fields in a 40%-wide group with the rest of the row left as an empty filler.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/icm-storages",
    idField: "oid",
    formName: "Storage",
    entityLabel: "Storage",
    inlineRowEdit: false,
    fields: [
      {
        key: "storage",
        label: "Storage",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Storage must not be empty.",
      },
      {
        key: "location",
        label: "Location",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Location must not be empty.",
      },
      { key: "comment", label: "Comment", type: "textarea", maxLength: 2000 },
      { key: "active", label: "Active", type: "checkbox" },
    ],
  },
  "set-vendor-evaluation-items": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: two fields, and the legacy ICM detail views likewise stack their
    // fields in a 40%-wide group with the rest of the row left as an empty filler.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/vendor-evaluation-items",
    idField: "oid",
    formName: "Vendor Evaluation Items",
    entityLabel: "Vendor Evaluation Item",
    inlineRowEdit: false,
    fields: [
      {
        key: "evaluationItem",
        label: "Name",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Name must not be empty.",
      },
      { key: "comment", label: "Comment", type: "textarea", maxLength: 200 },
    ],
  },

  // Settings ▸ Utility ▸ Inventory Management ▸ Items (legacy dbo.Items, 2026-09-21).
  "set-inventory-items": {
    basePath: "/api/v1/inventory-items",
    idField: "oid",
    formName: "Items",
    entityLabel: "Item",
    columnMajor: true,
    sideLabels: true,
    // Field set, ORDER and captions match the legacy Items_DetailView exactly
    // (user request 2026-09-23: "the columns which are in the G5 only needs, also
    // the order also"), verified against BTLIMS.Web\Model.xafml Items_col1/col2
    // and Items.cs — not eyeballed from the screenshot.
    //
    // ⚠️ The 8 fields legacy does not show are NOT deleted, they are
    // `hideInLayout` — InventoryItemsWriter.UpdateAsync does a full UPDATE … SET
    // over every column, so a field dropped from `fields` would stop being posted
    // and the next save would WIPE that column. Vendor Item Name, Vendor Lead Time
    // and Lab Lead Time all hold real data today. hideInLayout keeps them loaded
    // and posted (round-tripped untouched) while removing them from the layout.
    fields: [
      // ── LEFT column (legacy Items_col1, in its Index order) ──
      { key: "itemCode", label: "Item Code", type: "text", maxLength: 20 },
      // Legacy caption is "Item" (app-layer Model.xafml overrides the module
      // diff's "Item Name"), matching the BO's [XafDisplayName("Item")].
      {
        key: "items",
        label: "Item",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Item Name must not be empty.",
      },
      {
        key: "category",
        label: "Category",
        type: "select",
        optionsUrl: "/api/v1/inventory-items/categories",
        idKey: "categoryOid",
        noneLabel: "— None —",
      },
      {
        key: "grade",
        label: "Grade",
        type: "select",
        optionsUrl: "/api/v1/inventory-items/grades",
        idKey: "gradeOid",
        noneLabel: "— None —",
      },
      // "Description" is the SPECIFICATION column. Legacy labels dbo.Items.
      // Specification "Description" ([XafDisplayName("Description")] + the app
      // model's Caption) and removes the separate ItemDescription column from
      // this screen entirely — which is why ItemDescription is NULL on every
      // record. ItemDescription is kept below, hidden, so it is not wiped.
      { key: "specification", label: "Description", type: "textarea", maxLength: 2000 },
      { key: "vendorCatName", label: "Catalog#", type: "text", maxLength: 200 },
      {
        key: "manufacturer",
        label: "Manufacturer",
        type: "select",
        optionsUrl: "/api/v1/inventory-items/manufacturers",
        idKey: "manufacturerOid",
        noneLabel: "— None —",
      },
      {
        key: "vendor",
        label: "Vendor",
        type: "select",
        optionsUrl: "/api/v1/inventory-items/vendors",
        idKey: "vendorOid",
        required: true,
        requiredMessage: "Vendor must not be empty.",
      },
      {
        key: "unit",
        label: "Pack Unit",
        type: "select",
        optionsUrl: "/api/v1/inventory-items/pack-units",
        idKey: "unitOid",
        noneLabel: "— None —",
      },
      { key: "itemUnit", label: "Item/Unit", type: "text", maxLength: 200 },
      // 2 decimals, as legacy: Items.cs has [ModelDefault("DisplayFormat","{0:n2}")]
      // on UnitPrice, so a stored 0 shows "0.00" (user request 2026-09-23).
      { key: "unitPrice", label: "Unit Price", type: "number", step: 0.01, decimals: 2 },
      { key: "stockQty", label: "Stock Qty", type: "number" },
      // ── RIGHT column (legacy Items_col2, in its Index order) ──
      { key: "isFractional", label: "Is Fractional", type: "checkbox" },
      // Legacy shows Amount / Amount Units twice — a persistent pair and a
      // [NonPersistent] mirror, made mutually exclusive by ConditionalAppearance
      // on IsFractional, so the user only ever sees ONE of each. The new form
      // renders one and persists to the real columns, which is the same thing.
      /* TEXT, not number. Amount is a STRING all the way down -- legacy
         `public string Amount` (Items.cs :595), dbo.Items.Amount nvarchar,
         and InventoryItemInput.Amount is string?. Declaring it "number"
         made the browser post a JSON number into a string property, so
         System.Text.Json could not bind the body AT ALL and the API
         answered 400 "The input field is required." -- every save that
         touched Amount failed with "Save failed." and lost the whole form,
         not just that field (owner report 2026-10-08, creating an item
         with Amount 110). The five genuinely numeric fields on this form
         (Unit Price, Stock Qty, Alert Qty, Additional Qty, Reminder Days)
         have numeric DTO types and stay "number"; Amount was the only
         mismatch. It pairs with Amount Units, so "500" + "ml" is the
         legacy shape. */
      { key: "amount", label: "Amount", type: "text", maxLength: 100 },
      {
        key: "amountUnit",
        label: "Amount Units",
        type: "select",
        optionsUrl: "/api/v1/inventory-items/amount-units",
        idKey: "amountUnitOid",
        noneLabel: "— None —",
      },
      { key: "alertQty", label: "Alert Qty", type: "number" },
      { key: "comment", label: "Comment", type: "textarea", maxLength: 2000 },
      {
        key: "department",
        label: "Department",
        type: "select",
        optionsUrl: "/api/v1/inventory-items/departments",
        idKey: "departmentOid",
        noneLabel: "— None —",
      },
      { key: "reminderDaysInAdvance", label: "Reminder Days In Advance", type: "number" },
      { key: "retireDate", label: "Retire Date", type: "date" },
      { key: "size", label: "Size", type: "text", maxLength: 100 },
      { key: "additionalQty", label: "Additional Qty", type: "number" },
      // Pads the layout to 23 slots so columnMajor's ceil(n/2) split puts 12
      // fields in the left column and 10 in the right, as legacy does, instead
      // of an even 11/11. Renders as an empty row at the foot of the right column.
      { key: "__pad", label: "", type: "spacer" },
      // ── NOT on the legacy detail view — hidden, but still loaded and posted
      //    so a save round-trips them untouched (see the note above). ──
      { key: "itemDescription", label: "Item Description", type: "textarea", maxLength: 2000, hideInLayout: true },
      { key: "mfritemName", label: "Manufacturer Item Name", type: "text", maxLength: 200, hideInLayout: true },
      { key: "mfrcatNum", label: "Manufacturer Cat#", type: "text", maxLength: 200, hideInLayout: true },
      { key: "vendorItemName", label: "Vendor Item Name", type: "text", maxLength: 200, hideInLayout: true },
      { key: "isVendorLT", label: "Vendor Lead Time", type: "checkbox", hideInLayout: true },
      { key: "isLabLT", label: "Lab Lead Time", type: "checkbox", hideInLayout: true },
      { key: "isToxic", label: "Toxic", type: "checkbox", hideInLayout: true },
      { key: "bolResaleItems", label: "Resale Item", type: "checkbox", hideInLayout: true },
    ],
    // "Sub Item" tab (legacy Items_DetailView, ALPACA parity, user request 2026-09-23):
    // a many-to-many child, NOT the one-to-one Instrument Certificate/Attachment pattern
    // — a Sub Item can be linked to several different Items, so linkQueryParam is set
    // explicitly ("itemId") rather than reusing "labwareId", and Unlink needs it too.
    //
    // Rendered as a TAB, not a bare heading (user report 2026-09-23: "the border is
    // missing for this grid"). Legacy wraps it in a TabbedGroup — one "Sub Item" tab
    // — and that tab is what binds the grid to the form; without it the grid floats
    // under a plain <h3> and reads as unbounded.
    childGridsTabbed: true,
    childGrids: [
      {
        title: "Sub Item",
        listUrl: "/api/v1/sub-items/by-item/{id}",
        columns: [
          { key: "subItemsName", label: "SubItem" },
          { key: "concentration", label: "Concentration" },
          { key: "remark", label: "Remark" },
        ],
        childKey: "sub-items",
        parentField: "linkToItemId",
        linkable: true,
        linkListUrl: "/api/v1/sub-items/linkable/{id}",
        linkQueryParam: "itemId",
      },
    ],
  },

  // Settings ▸ Utility ▸ Inventory Management ▸ Sub Item — the child dialog behind Items'
  // "Sub Item" tab (see childGrids above). Standalone MODULE_CRUD entry only because New/
  // Edit need their own field set; there is no separate top-level nav entry for it.
  "sub-items": {
    basePath: "/api/v1/sub-items",
    idField: "oid",
    formName: "Sub Item",
    entityLabel: "Sub Item",
    fields: [
      {
        key: "subItemsName",
        label: "Sub Item",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Sub Item must not be empty.",
      },
      {
        key: "concentration",
        label: "Concentration",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Concentration must not be empty.",
      },
      { key: "remark", label: "Remark", type: "textarea", maxLength: 500 },
      // Hidden via the child grid's fixedValues (parentField "linkToItemId") — present
      // here only so that hiding mechanism has a real field to match against.
      { key: "linkToItemId", label: "Item", type: "readonly", placeholder: "" },
    ],
  },

  // Settings ▸ Utility ▸ Inventory Management ▸ Vendors (legacy dbo.Vendors, 2026-09-21).
  // ── Settings ▸ Analysis ▸ Daily QC Log Book Builder ─────────────────────────
  // Legacy DailyQCLogBookBuilder_DetailView. Field set, ORDER and captions follow
  // the legacy view; the 15 laid-out fields split 8 / 7 under columnMajor's
  // ceil(n/2), which is exactly the legacy two-column split, so no padding.
  //
  // ⚠️ The record spans TWO tables: dbo.Event (DevExpress base, XPO OwnTable
  // inheritance) + dbo.DailyQCLogBookBuilder. Start Date / End Date / Recurrence
  // are Event columns, which is why they are not on the builder table.
  "set-dailyqc-logbook": {
    basePath: "/api/v1/daily-qc-logbook",
    idField: "oid",
    formName: "Daily QC Log Book Builder",
    entityLabel: "Daily QC Log Book Template",
    // Without this the header falls back to fields[0] (the read-only, sometimes
    // blank Template ID) and the title reads "{BO} – {BO}". The template's NAME is
    // what identifies it to the user, in the header and the breadcrumb alike.
    titleField: "templateName",
    // 2026-09-28 (owner): New / Edit open the prototype page DailyQcBuilderPage
    // (ModuleListView routes this key to it); the list keeps its grid but loses the
    // List View / Detail View switch. The fields below now only drive the list's
    // New/Edit/Delete wiring — the detail page does not read them.
    hideViewToggle: true,
    columnMajor: true,
    sideLabels: true,
    fields: [
      // ── LEFT column ──
      // Legacy renders this AllowEdit="False" with NullText="Autogenerated".
      { key: "templateId", label: "Template ID", type: "readonly", placeholder: "Autogenerated" },
      {
        key: "templateName",
        label: "Template Name",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Template Name must not be empty.",
      },
      {
        key: "categoryOid",
        label: "Category",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/daily-qc-logbook/categories",
        noneLabel: "— None —",
        requiredMessage: "Category must not be empty.",
      },
      // Legacy's Test lookup displays TestName and its data source is TestMethod
      // grouped BY TestName — one entry per test, not one per test/method pair.
      {
        key: "testOid",
        label: "Test",
        type: "select",
        optionsUrl: "/api/v1/daily-qc-logbook/tests",
        noneLabel: "— None —",
      },
      // Method CASCADES off Test: legacy's MethodDataSource is the TestMethod rows
      // sharing the chosen test's name, displayed as MethodName.MethodNumber (a
      // two-hop walk — TestMethod.MethodName is the Method BO, not a string).
      {
        key: "methodOid",
        label: "Method",
        type: "select",
        dependsOn: "testOid",
        enabledWhen: ["testOid"],
        optionsUrl: "/api/v1/daily-qc-logbook/methods",
        noneLabel: "— None —",
      },
      // ⚠️ Two adjacent fields, two DIFFERENT Labware columns: the lookup shows
      // LabwareID (the IN#### code) per the legacy LookupProperty, while the
      // read-only name below shows LabwareName. On dbo.Labware, LabwareName is
      // captioned "Instrument Number" yet holds a descriptive name and differs
      // from AssignedName on 50 of 87 live rows — so the pairing is deliberate.
      {
        key: "instrumentOid",
        label: "Instrument ID",
        type: "select",
        optionsUrl: "/api/v1/daily-qc-logbook/instruments",
        noneLabel: "— None —",
      },
      { key: "instrumentName", label: "Instrument Name", type: "readonly", placeholder: "" },
      { key: "trending", label: "Trending", type: "checkbox" },
      // ── RIGHT column ──
      { key: "startOn", label: "Start Date", type: "date" },
      { key: "endOn", label: "End Date", type: "date" },
      // The legacy editor is a DevExpress scheduler popup with no equivalent here,
      // so the value is shown read-only and round-tripped verbatim rather than
      // offering an editor that would write XML we cannot faithfully produce.
      { key: "recurrenceInfoXml", label: "Recurrence", type: "readonly", placeholder: "" },
      { key: "notification", label: "Notification", type: "checkbox" },
      { key: "active", label: "Active", type: "checkbox" },
      { key: "retire", label: "Retire", type: "checkbox" },
      { key: "comment", label: "Comment", type: "textarea", maxLength: 2000 },
    ],
    // Dates display MM/DD/YYYY regardless of the viewer's browser locale (rule 3).
    usDateFormat: true,
    // The legacy "Field Configuration" dual list, full-width below the header.
    dailyQcFieldConfig: true,
  },

  "set-vendors": {
    basePath: "/api/v1/vendors-mgmt",
    idField: "oid",
    formName: "Vendors",
    entityLabel: "Vendor",
    columnMajor: true,
    sideLabels: true,
    // Field set, ORDER and captions match the legacy Vendors_DetailView exactly
    // (user request 2026-09-23: "columns and their orders need to be corrected"),
    // verified against BTLIMS.Web\Model.xafml Vendors_col1/col2 + Vendors.cs —
    // 11 fields per column, which is what columnMajor's ceil(n/2) produces from
    // the 22 laid-out fields, so no padding is needed here.
    //
    // ⚠️ Like Items, the field legacy does not show is `hideInLayout`, NOT deleted:
    // VendorsMgmtWriter.UpdateAsync does a full UPDATE … SET over every column, so
    // dropping a field from `fields` would stop it being posted and the next save
    // would null that column.
    fields: [
      // ── LEFT column (legacy Vendors_col1, in its Index order) ──
      { key: "vendorcode", label: "Vendor Code", type: "text", maxLength: 200 },
      {
        key: "vendor",
        label: "Vendor",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Vendor Name must not be empty.",
      },
      { key: "account", label: "Account", type: "text", maxLength: 200 },
      { key: "address1", label: "Address 1", type: "text", maxLength: 200 },
      { key: "address2", label: "Address 2", type: "text", maxLength: 200 },
      { key: "city", label: "City", type: "text", maxLength: 200 },
      // Legacy's visible "State" is the free-text column, not the CustomState
      // lookup — see the note on `stateCb` below.
      { key: "state", label: "State", type: "text", maxLength: 200 },
      { key: "zipCode", label: "Zip Code", type: "text", maxLength: 50 },
      {
        key: "country",
        label: "Country",
        type: "select",
        optionsUrl: "/api/v1/vendors-mgmt/countries",
        idKey: "countryOid",
        noneLabel: "— None —",
      },
      { key: "phone", label: "Phone", type: "text", maxLength: 200 },
      { key: "fax", label: "Fax", type: "text", maxLength: 200 },
      // ── RIGHT column (legacy Vendors_col2, in its Index order) ──
      { key: "contact", label: "Contact", type: "text", maxLength: 200 },
      { key: "email", label: "Email", type: "text", maxLength: 200 },
      { key: "qualification", label: "Qualification", type: "textarea", maxLength: 2000 },
      { key: "certificate", label: "Certificate", type: "text", maxLength: 200 },
      { key: "certExpDate", label: "Cert Exp Date", type: "date" },
      { key: "approvedDate", label: "Approved Date", type: "date" },
      {
        key: "approvedBy",
        label: "Approved By",
        type: "select",
        optionsUrl: "/api/v1/vendors-mgmt/employees",
        idKey: "approvedByOid",
        noneLabel: "— None —",
      },
      { key: "retiredDate", label: "Retired Date", type: "date" },
      {
        key: "retiredBy",
        label: "Retired By",
        type: "select",
        optionsUrl: "/api/v1/vendors-mgmt/employees",
        idKey: "retiredByOid",
        noneLabel: "— None —",
      },
      { key: "website", label: "Website", type: "text", maxLength: 200 },
      { key: "comment", label: "Comment", type: "textarea", maxLength: 2000 },
      // ── NOT shown by legacy — hidden, still loaded and posted ──
      // dbo.Vendors carries BOTH a free-text `State` and a `StateCB` FK→CustomState,
      // and legacy lays both out (col1 idx 6 and 7) behind a ConditionalAppearance
      // keyed on Country: the lookup shows only when Country.EnglishLongName equals
      // 'United States of America'. No such row exists — this database spells it
      // "United States" — so the condition can never hold and the lookup is
      // permanently hidden, which the data confirms: 0 vendors have StateCB set,
      // 10 use the free-text State. So legacy shows exactly one State field, the
      // textbox above. StateCB is kept here only so a save round-trips it.
      {
        key: "stateCb",
        label: "State (lookup)",
        type: "select",
        optionsUrl: "/api/v1/vendors-mgmt/states",
        idKey: "stateCbOid",
        noneLabel: "— None —",
        hideInLayout: true,
      },
    ],
    // Legacy Vendors_DetailView TabbedGroup → the two child grids below the form
    // (user report 2026-09-23: "seems to be the grid is missing" / "need that grid
    // below, like g5"). Ported 2026-09-23 after verifying both tables against the
    // live schema.
    //
    // Evaluation is a genuine MANY-TO-MANY (dbo.VendorEvaluation + the junction
    // dbo.VendorEvaluationlink), so it sets its own linkQueryParam ("vendorId")
    // rather than reusing the Instrument pair's historical "labwareId" — same as
    // the Items → Sub Item tab. Upload is one-to-many and vendor-OWNED (legacy
    // [Aggregated]), so it is NOT linkable: New / Edit / Delete only, matching XAF.
    //
    // ONE tabbed grid area, not two stacked grids — legacy renders these as a
    // TabbedGroup (user request 2026-09-23: "need a single grid G5").
    childGridsTabbed: true,
    childGrids: [
      {
        title: "Evaluation",
        listUrl: "/api/v1/vendor-evaluations/by-vendor/{id}",
        // ONE column, as G5 (user request 2026-09-23: "the columns are excess in
        // Evaluation Tab"). Legacy has no explicit ListView node for this grid —
        // XAF auto-generates it from the collection and shows the class's
        // [DefaultProperty] alone. Comment / CreatedDate / CreatedBy are still
        // returned by the endpoint and still edited in the New/Edit dialog; they
        // are simply not columns, exactly as legacy renders it.
        columns: [{ key: "evaluation", label: "Evaluation" }],
        childKey: "vendor-evaluation-row",
        parentField: "linkToVendorId",
        linkable: true,
        linkListUrl: "/api/v1/vendor-evaluations/linkable/{id}",
        linkQueryParam: "vendorId",
        // Toolbar = Link · Unlink · Edit, exactly as G5 (user request 2026-09-23:
        // "the actions also need to be similar, no other actions"). XAF drops New
        // and Delete on a non-aggregated M:N collection — Link replaces New, and
        // Unlink is the destructive action rather than deleting the shared row.
        hideNew: true,
        hideDelete: true,
        // Checkbox selection + select-all, as G5 (user request 2026-09-23) — and
        // Unlink then acts on every ticked row, which is the point of ticking several.
        multiSelect: true,
        // The pencil beside the checkbox, as G5: opens THIS row's edit popup
        // without ticking it first (user request 2026-09-23).
        rowEditIcon: true,
      },
      {
        title: "Upload",
        listUrl: "/api/v1/vendor-uploads/by-vendor/{id}",
        // NO width on the only data column — it must absorb the slack. The table
        // is width:100% with table-layout:auto, so a FIXED width here makes the
        // browser spread the leftover space across every column, blowing the
        // narrow checkbox/pencil/download cells out (user report 2026-09-23:
        // "the layout is incorrect for only the Upload tab" — only this tab,
        // because Evaluation's lone column already has no width).
        columns: [
          {
            key: "fileName",
            label: "File",
            type: "file",
            downloadUrlBase: "/api/v1/vendor-uploads/file",
            fileOidKey: "file",
          },
        ],
        childKey: "vendor-upload-row",
        parentField: "linkToVendorId",
        // Toolbar = New · Edit · Delete · Download, exactly as G5. No Link/Unlink:
        // the collection is aggregated, so an upload belongs to this vendor alone.
        downloadAction: { urlBase: "/api/v1/vendor-uploads/file", fileOidKey: "file" },
        multiSelect: true,
        rowEditIcon: true,
        // The per-row download glyph beside the pencil, as G5 (user report
        // 2026-09-23: "the download is missing") — in ADDITION to the toolbar
        // button, which is what legacy shows.
        rowDownloadIcon: true,
      },
    ],
  },

  // Vendors detail → "Evaluation" tab child dialog (legacy dbo.VendorEvaluation).
  // Standalone MODULE_CRUD entry only because New/Edit need their own field set;
  // there is no top-level nav entry for it. NOT the same table as the
  // "Vendor Evaluation Items" settings master (dbo.VendorEvaluationItem) — legacy
  // keeps them separate with no FK between them.
  "vendor-evaluation-row": {
    basePath: "/api/v1/vendor-evaluations",
    idField: "oid",
    formName: "Vendor Evaluation",
    entityLabel: "Evaluation",
    dialogEdit: true,
    singleColumn: true,
    fields: [
      {
        key: "evaluation",
        label: "Evaluation",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Evaluation must not be empty.",
      },
      { key: "comment", label: "Comment", type: "textarea", maxLength: 500 },
      // Hidden via the child grid's fixedValues (parentField "linkToVendorId") —
      // present only so that hiding mechanism has a real field to match against.
      { key: "linkToVendorId", label: "Vendor", type: "readonly", placeholder: "" },
    ],
  },

  // Vendors detail → "Upload" tab child dialog (legacy dbo.Vendorsupload). The row
  // holds nothing but the file itself — legacy's Vendorsupload has no title/name
  // column of its own; the filename comes from the dbo.FileData row it points at.
  "vendor-upload-row": {
    basePath: "/api/v1/vendor-uploads",
    idField: "oid",
    formName: "Vendors",
    entityLabel: "Upload",
    dialogEdit: true,
    singleColumn: true,
    fields: [
      {
        key: "file",
        label: "File",
        type: "file",
        required: true,
        requiredMessage: "A file is required.",
        fileNameKey: "fileName",
        uploadUrl: "/api/v1/vendor-uploads/file/upload",
        downloadUrlBase: "/api/v1/vendor-uploads/file",
      },
      { key: "linkToVendorId", label: "Vendor", type: "readonly", placeholder: "" },
    ],
  },

  // Settings ▸ Utility ▸ Inventory Management ▸ Assign Items To Test (read-only list;
  // items are assigned from the Item cell's double-click popup). The row-select
  // checkbox is the FIRST column, the app-wide grid standard (user request
  // 2026-09-22 — legacy TestMethod_ListView_Copy_ItemLink hides it); there is no
  // detail, so the Edit pencil and the List/Detail toggle stay hidden.
  "set-assign-items-to-test": {
    basePath: "/api/v1/inventory-items",
    idField: "oid",
    formName: "Assign Items To Test",
    entityLabel: "Assignment",
    readOnlyList: true,
    hideEditPencil: true,
    hideViewToggle: true,
    fields: [],
  },

  // Settings ▸ Utility ▸ Inventory Management ▸ Assign Tests To Item (legacy
  // Items_ListView_NN → Items_DetailView_NN). List: Edit only (AllowNew/AllowDelete
  // False). Detail: the read-only Item + the "Tests" tab (ItemTestsGrid), saved with
  // Save · Save and Close · Cancel — no Save and New.
  "set-assign-tests-to-item": {
    basePath: "/api/v1/assign-tests-to-item",
    idField: "oid",
    formName: "Assign Tests To Item",
    entityLabel: "Assign Tests To Item",
    inlineRowEdit: false,
    editOnlyList: true,
    detailHideSaveNew: true,
    hideSectionHeader: true,
    sideLabels: true,    // AI Standard side-label layout — label beside field (owner request 2026-10-02)
    singleColumn: true,  // one field cannot fill a 50/50 split
    titleField: "itemName",
    itemTestsGrid: true,
    fields: [{ key: "itemName", label: "Item", type: "readonly", required: true, placeholder: "" }],
  },

  // Settings ▸ Utility ▸ Maintenance ▸ Security ▸ Version Log ▸ Deployment Team (legacy DeploymentTeam).
  "set-deployment-team": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: a single field cannot fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/deployment-teams",
    idField: "oid",
    formName: "Deployment Team",
    entityLabel: "Deployment Team",
    inlineRowEdit: false,
    fields: [
      {
        key: "name",
        label: "Name",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Name must not be empty.",
      },
    ],
  },

  // Settings ▸ Utility ▸ Prep Options (legacy PrepOptions — single PrepName).
  "set-prep-options": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: a single field cannot fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/prep-options",
    idField: "oid",
    formName: "Prep Options",
    entityLabel: "Prep Option",
    inlineRowEdit: false,
    fields: [
      {
        key: "name",
        label: "Prep Name",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Prep Name must not be empty.",
      },
    ],
  },

  // Settings ▸ Turn Around Time (legacy TurnAroundTime — analytical turnaround
  // durations, e.g. "10 Days"). CRUD-only port (2026-09-17): the legacy TAT→Count
  // auto-parse and single-Default rule are out of scope, so Count/Default are plain
  // editable fields. TAT required + unique; Sort > -1.
  "set-turn-around-time": {
    basePath: "/api/v1/turn-around-times",
    idField: "oid",
    formName: "Turn Around Time",
    entityLabel: "Turn Around Time",
    sideLabels: true,   // AI Standard side-label layout (owner request 2026-10-02)
    singleColumn: true, // one control per row; caps field width (incl. Comment) to the column
    // Shared opt-in: square corners + plain white editable boxes, no yellow tint
    // (owner request 2026-10-13 "remove curve and yellow colour").
    squareControls: true,
    inlineRowEdit: false,
    // Row Edit pencil SHOWN again (owner request 2026-10-13 "missing edit column"),
    // superseding the 2026-09-17 request that hid it. The pencil opens the record
    // editable; a click on the row opens it read-only (shared ModuleListView).
    fields: [
      {
        key: "tat",
        label: "TAT",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "TAT must not be empty.",
      },
      { key: "labTat", label: "Lab TAT", type: "text", maxLength: 100 },
      { key: "count", label: "Count", type: "number", min: 0 },
      { key: "sort", label: "Sort", type: "number", min: 0 },
      { key: "default", label: "Default", type: "checkbox" },
      { key: "comment", label: "Comment", type: "textarea", maxLength: 1000 },
    ],
  },

  // Settings ▸ Technical Writer (legacy HelpCenter — the Help-Center / User's Manual
  // article editor). Core-CRUD port 2026-09-17: rich-text Article binds to the HTML
  // column ArticleHTML; Category dropdown from /technical-writer/categories. Legacy
  // list has a checkbox but no pencil, so hideEditPencil. Deferred: PDF upload,
  // Release workflow, attachments, Module→BO cascade, chatbot sync.
  "set-technical-writer": {
    basePath: "/api/v1/technical-writer",
    idField: "oid",
    formName: "Technical Writer",
    // Legacy DetailView caption is "Technical Writer" → header "Technical Writer – SUVA".
    entityLabel: "Technical Writer",
    sideLabels: true,  // AI Standard side-label layout (owner request 2026-10-02)
    // G5 HelpCenter_DetailView_FAQ_Articles layout (owner request 2026-10-13 "match
    // G5"): two stacks — Topic · Category · Module · Business Object on the left,
    // Style · Date Released · Author · Active on the right — the Article on its OWN
    // tab, and Attachments | Version Log as tabs under the fields.
    columnMajor: true,
    childGridsTabbed: true,
    // Shared opt-in: square corners + plain white editable boxes, no yellow tint
    // (owner request 2026-10-13 "remove curve and yellow colour").
    squareControls: true,
    fieldTabs: [{ title: "Article", fields: ["articleRtf"] }],
    // Opens editable on a row click and the Topic link, as the Method form does (owner request 2026-10-14).
    detailOpenInEdit: true,
    inlineRowEdit: false,
    // Row Edit pencil SHOWN (G5 parity, 2026-10-13): HelpCenter_ListView_FAQ_Articles
    // has the standard XAF edit column — the pencil opens the article EDITABLE. A click on
    // the row or the Topic link opens it editable too (detailOpenInEdit, owner request
    // 2026-10-14: the Category ＋ must show, as on the Method form).
    // G5 detail toolbar: New · Delete · Release · Refresh · Edit.
    detailShowNew: true,
    detailShowDelete: true,
    detailShowRefresh: true,
    recordActions: [
      {
        key: "release",
        label: "Release",
        icon: "✔",
        path: "release",
        confirmTitle: "Release",
        confirmMessage: "Do you want to release this article?",
        successMessage: "Released successfully.",
      },
    ],
    // G5 list toolbar: New · Edit · Delete · Release · Refresh · Export to.
    // crudFirst puts the CRUD group before Release (owner request 2026-10-13, "check buttons").
    // Search sits on its own row under the bar, like Turn Around Time (owner request 2026-10-14).
    crudFirst: true,
    listActions: [
      {
        key: "release",
        label: "Release",
        icon: "✔",
        path: "release",
        confirmTitle: "Release",
        confirmMessage: "Do you want to release the selected article(s)?",
        successMessage: "Released successfully.",
        noSelectionMessage: "Please select an item to proceed further.",
      },
    ],
    fields: [
      {
        key: "topic",
        label: "Topic",
        type: "text",
        required: true,
        maxLength: 500,
        requiredMessage: "Topic must not be empty.",
      },
      {
        key: "category",
        label: "Category",
        type: "select",
        optionsUrl: "/api/v1/technical-writer/categories",
        // G5 Category lookup has a "New" button → the shared "+" quick-add, welded to the
        // dropdown caret like the Method Category and Payment Terms controls (owner request 2026-10-14).
        quickAdd: {
          createUrl: "/api/v1/technical-writer/categories",
          welded: true,
          title: "Category",
          icon: "ti-category",
          nameKey: "category",
          fields: [
            { key: "category", label: "Category", type: "text", required: true, maxLength: 100, requiredMessage: "Category must not be empty." },
            { key: "comment", label: "Comment", type: "text", maxLength: 1000 },
          ],
        },
      },
      // G5 dropdowns (ModuleNamePropertyEditor / BusinessObjectPropertyEditor). The values are
      // stored as TEXT, so each option's id is its name. Business Object reloads — and clears —
      // when Module changes, as legacy re-fills its combo from SelectBusinessObject_Sp.
      { key: "module", label: "Module", type: "select", optionsUrl: "/api/v1/technical-writer/modules" },
      {
        key: "businessObject",
        label: "Business Object",
        type: "select",
        optionsUrl: "/api/v1/technical-writer/business-objects",
        dependsOn: "module",
      },
      // Legacy EnumStyle — the stored int is the member's position.
      {
        key: "style",
        label: "Style",
        type: "select",
        numericValue: true,
        defaultValue: "0",
        options: [
          "Normal", "No Spacing", "Heading 1", "Heading 2", "Title", "Subtitle", "Subtle Emphasis", "Emphasis",
          "Intense Emphasis", "Strong", "Quote", "Intense Quote", "Subtle Reference", "Intense Reference",
          "Book Title", "List Paragraph",
        ].map((name, i) => ({ id: String(i), name })),
      },
      { key: "dateReleased", label: "Date Released", type: "date" },
      // Set by the server to whoever created the article (legacy AfterConstruction).
      // G5 Author is an editable Employee lookup (by DisplayName); empty shows "N/A".
      // A new article left empty is stamped with the signed-in user server-side.
      { key: "author", label: "Author", type: "select", optionsUrl: "/api/v1/technical-writer/authors", noneLabel: "N/A" },
      { key: "active", label: "Active", type: "checkbox" },
      // Not on the G5 detail layout (HelpCenter_DetailView_FAQ_Articles has no Upload
      // LayoutItem), so it is hidden — but still posted, so the stored file is kept.
      {
        key: "upload",
        label: "Upload",
        type: "file",
        hideInLayout: true,
        fileNameKey: "uploadFileName",
        uploadUrl: "/api/v1/technical-writer/file/upload",
        downloadUrlBase: "/api/v1/technical-writer/file",
      },
      // The Article tab: G5's DevExpress ribbon editor over the stored RTF while editing
      // (articleRtf, posted as-is), the formatted HTML rendering while viewing (article).
      { key: "article", label: "Article", type: "readonly", hideInLayout: true },
      { key: "articleRtf", label: "Article", type: "richdoc", htmlKey: "article" },
    ],
    // Legacy HelpCenter_DetailView Attachments / Version Log tabs → child grids.
    childGrids: [
      {
        title: "Attachments",
        listUrl: "/api/v1/helpcenter-attachments/by-article/{id}",
        // G5 child bar: checkboxes · row pencil · New · Edit · Delete · Export to; File is a download link.
        exportButton: true,
        multiSelect: true,
        rowEditIcon: true,
        // Shared list-grid footer, as G5's nested grids (owner request 2026-10-14): Page X of N (items).
        paged: true,
        childKey: "hc-attachment-row",
        parentField: "topicId",
        columns: [
          {
            key: "fileName",
            label: "File",
            width: 260,
            type: "file",
            downloadUrlBase: "/api/v1/helpcenter-attachments/file",
            fileOidKey: "fileOid",
          },
          { key: "title", label: "Title", width: 220 },
          { key: "size", label: "Size", width: 100 },
          { key: "sort", label: "Sort", width: 80 },
          { key: "date", label: "Date", width: 160, type: "date" },
          { key: "authorName", label: "Author", width: 180 },
        ],
      },
      {
        title: "Version Log",
        listUrl: "/api/v1/helpcenter-versionlogs/by-article/{id}",
        exportButton: true,
        multiSelect: true,
        rowEditIcon: true,
        // Shared list-grid footer, as G5's nested grids (owner request 2026-10-14): Page X of N (items).
        paged: true,
        childKey: "hc-versionlog-row",
        parentField: "helpCenterId",
        columns: [
          { key: "versionId", label: "Version ID", width: 160 },
          { key: "date", label: "Date", width: 160, type: "date" },
          { key: "authorName", label: "Author", width: 180 },
          { key: "inUse", label: "In Use", width: 90, type: "checkbox" }, // G5 shows a checkbox
          { key: "dateRetire", label: "Date Retire", width: 160, type: "date" },
          { key: "comment", label: "Comment", width: 260 },
        ],
      },
    ],
  },

  // Technical Writer child collections — the Attachments + Version Log dialogs.
  // These drive ONLY the child New/Edit popups (dialogEdit); they are never a
  // standalone nav screen. parentField (topicId / helpCenterId) is injected by the
  // child grid, so it is NOT a rendered field here.
  "hc-attachment-row": {
    basePath: "/api/v1/helpcenter-attachments",
    idField: "oid",
    formName: "Technical Writer",
    entityLabel: "Attachment",
    inlineRowEdit: false,
    dialogEdit: true,
    singleColumn: true,
    fields: [
      {
        key: "title",
        label: "Title",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Title must not be empty.",
      },
      {
        key: "file",
        label: "File",
        type: "file",
        required: true,
        fileNameKey: "fileName",
        uploadUrl: "/api/v1/helpcenter-attachments/file/upload",
        downloadUrlBase: "/api/v1/helpcenter-attachments/file",
        requiredMessage: "A file is required.",
      },
      { key: "sort", label: "Sort", type: "number", min: 0 },
    ],
  },
  "hc-versionlog-row": {
    basePath: "/api/v1/helpcenter-versionlogs",
    idField: "oid",
    formName: "Technical Writer",
    entityLabel: "Version Log entry",
    inlineRowEdit: false,
    dialogEdit: true,
    singleColumn: true,
    fields: [
      {
        key: "versionId",
        label: "Version ID",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Version ID must not be empty.",
      },
      { key: "date", label: "Date", type: "date" },
      { key: "inUse", label: "In Use", type: "checkbox" },
      { key: "dateRetire", label: "Date Retire", type: "date" },
      { key: "comment", label: "Comment", type: "text", maxLength: 100 },
    ],
  },

  // Settings ▸ Utility ▸ QA/QC — PT Study Log + QC Category.
  "set-study-id": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: a single field cannot fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/study-ids",
    idField: "oid",
    formName: "Study ID",
    entityLabel: "Study ID",
    inlineRowEdit: false,
    fields: [
      {
        key: "name",
        label: "Study ID",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Study ID must not be empty.",
      },
    ],
  },
  "set-study-name": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: a single field cannot fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/study-names",
    idField: "oid",
    formName: "Study Name",
    entityLabel: "Study Name",
    inlineRowEdit: false,
    fields: [
      {
        key: "name",
        label: "Study Name",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Study Name must not be empty.",
      },
    ],
  },
  "set-source": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: a single field cannot fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/sources",
    idField: "oid",
    formName: "Source",
    entityLabel: "Source",
    inlineRowEdit: false,
    fields: [
      {
        key: "name",
        label: "Source Name",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Source Name must not be empty.",
      },
    ],
  },
  "set-qc-category": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: two fields cannot fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/qc-categories",
    idField: "oid",
    formName: "QC Category",
    entityLabel: "QC Category",
    inlineRowEdit: false,
    fields: [
      {
        key: "categoryName",
        label: "Category",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "QC Category must not be empty",
      },
      { key: "description", label: "Description", type: "textarea" },
    ],
  },

  // Settings ▸ Utility ▸ Reagent Prep — 6 masters. Detail-View standard (standing
  // rule 12, migrated 2026-09-26): singleColumn + sideLabels — label BESIDE its
  // field, single column, 26px controls.
  "set-calculation-approach": {
    basePath: "/api/v1/calculation-approaches",
    idField: "oid",
    formName: "Calculation Approach",
    entityLabel: "Calculation Approach",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "approach",
        label: "Approach",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Approach must not be empty.",
      },
      { key: "remark", label: "Remark", type: "textarea" },
    ],
  },
  "set-reagent-operator": {
    basePath: "/api/v1/reagent-operators",
    idField: "oid",
    formName: "Operator",
    entityLabel: "Operator",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "operator",
        label: "Operator",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Operator must not be empty.",
      },
      { key: "remark", label: "Remark", type: "textarea" },
    ],
  },
  "set-standard-name": {
    basePath: "/api/v1/standard-names",
    idField: "oid",
    formName: "Standard Name",
    entityLabel: "Standard Name",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "standardName",
        label: "Standard Name",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Standard Name must not be empty.",
      },
      {
        key: "prepType",
        label: "Prep Type",
        type: "select",
        optionsUrl: "/api/v1/standard-names/prep-types",
        noneLabel: "— None —",
      },
    ],
  },
  // Settings ▸ Reagent Prep ▸ Calculation Editor — dbo.RegentPrepCalculationEditor. CodeID
  // required + unique; Calculation Approach + the C1/V1/C2/V2/W1 unit slots are FK dropdowns;
  // Formula + Remark are free text (the formula is stored text, evaluation is deferred).
  "set-reagent-calceditor": {
    basePath: "/api/v1/reagent-calc-editors",
    idField: "oid",
    formName: "Calculation Editor",
    entityLabel: "Calculation Editor",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    squareControls: true,
    // Legacy detail field order: Code ID (auto, read-only) · Calculation Approach (required) ·
    // C2 · V2 · C1 · V1 · W1 Units · Formula · Remark. V1/W1 are nulled on save when the Approach
    // text lacks "V1"/"W1" (legacy HideV1Unit/HideW1Unit). The Formula field carries the legacy
    // "Formula" builder button (formulaBuilder) — it opens the "Result Correction Formula" popup;
    // the box on the detail is read-only, so the popup is the only way to write the formula.
    fields: [
      { key: "codeId", label: "Code ID", type: "readonly", defaultValue: "AutoGenerate" },
      { key: "calcApproach", label: "Calculation Approach", type: "select", comboList: true, required: true, optionsUrl: "/api/v1/reagent-calc-editors/approaches", noneLabel: "— None —", requiredMessage: "Calculation Approach must not be empty." },
      { key: "c2Units", label: "C2 Units", type: "select", comboList: true, optionsUrl: "/api/v1/reagent-calc-editors/units", noneLabel: "— None —" },
      { key: "v2Units", label: "V2 Units", type: "select", comboList: true, optionsUrl: "/api/v1/reagent-calc-editors/units", noneLabel: "— None —" },
      { key: "c1Units", label: "C1 Units", type: "select", comboList: true, optionsUrl: "/api/v1/reagent-calc-editors/units", noneLabel: "— None —" },
      { key: "v1Units", label: "V1 Units", type: "select", comboList: true, optionsUrl: "/api/v1/reagent-calc-editors/units", noneLabel: "— None —" },
      { key: "w1Units", label: "W1 Units", type: "select", comboList: true, optionsUrl: "/api/v1/reagent-calc-editors/units", noneLabel: "— None —" },
      {
        key: "formula", label: "Formula", type: "textarea",
        formulaBuilder: {
          approachKey: "calcApproach",
          units: [
            { variable: "C1", key: "c1Units" },
            { variable: "C2", key: "c2Units" },
            { variable: "V1", key: "v1Units" },
            { variable: "V2", key: "v2Units" },
            { variable: "W1", key: "w1Units" },
          ],
          operatorsUrl: "/api/v1/reagent-calc-editors/operators",
        },
      },
      { key: "remark", label: "Remark", type: "textarea" },
    ],
  },
  // Settings ▸ Test Pricing ▸ Priority (legacy Priority form). Priority (required) · Default
  // (bool) · Sort (unique int). CRUD over dbo.Priority.
  "set-test-priority": {
    basePath: "/api/v1/test-priorities",
    idField: "oid",
    formName: "Priority",
    entityLabel: "Priority",
    // Shared opt-in: square control corners + plain white editable boxes (no yellow tint).
    squareControls: true,
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      { key: "prioritys", label: "Priority", type: "text", required: true, maxLength: 100, requiredMessage: "Priority must not be empty." },
      { key: "isRegular", label: "Default", type: "checkbox" },
      { key: "sort", label: "Sort", type: "number", required: true, requiredMessage: "Sort must not be empty." },
    ],
  },
  // Settings ▸ Test Pricing ▸ Flat Test Pricing (legacy port). Test (required) · Component
  // (cascaded off Test) · Price · Invoice Code · Is Active · Remark. PriceCode auto "FTP-####"
  // (read-only). Unique (Test, Component); an active price must be >= 1.
  "set-flat-test-pricing": {
    basePath: "/api/v1/flat-test-pricings",
    idField: "oid",
    formName: "Flat Test Pricing",
    entityLabel: "Flat Test Pricing",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    // Shared opt-in: square control corners + plain white editable boxes (no yellow tint).
    squareControls: true,
    fields: [
      { key: "priceCode", label: "Price Code", type: "readonly", defaultValue: "AutoGenerate" },
      { key: "test", label: "Test", type: "select", required: true, optionsUrl: "/api/v1/flat-test-pricings/tests", requiredMessage: "Test must not be empty." },
      { key: "component", label: "Component", type: "select", dependsOn: "test", optionsUrl: "/api/v1/flat-test-pricings/components", noneLabel: "— None —" },
      { key: "price", label: "Price", type: "number", decimals: 2 },
      { key: "invoiceCode", label: "Invoice Code", type: "text", maxLength: 100 },
      { key: "active", label: "Is Active", type: "checkbox" },
      { key: "remark", label: "Remark", type: "textarea" },
    ],
  },
  // Settings ▸ Test Pricing ▸ Contract Lab Test Pricing (legacy port). TestMethod · TAT ·
  // Contract Lab (required FK dropdowns) · Unit Price · Comment. PriceCode + Change% read-only
  // (Change% + OldUnitPrice auto-computed on save). Unique (TestMethod, TAT, Contract Lab).
  // ⚠ Since 2026-10-14 the SCREEN is the bespoke G5 batch-edit page
  // features/settings/ContractLabTestPricingPage (Refresh · Add · Remove · Save), routed in
  // AppShell ahead of ModuleListView — this entry no longer renders the list or a detail view;
  // it is kept for the shared code that still reads the form's CRUD config by nav id.
  "set-contractlab-test-pricing": {
    basePath: "/api/v1/contractlab-test-pricings",
    idField: "oid",
    formName: "Contract Lab Test Pricing",
    entityLabel: "Contract Lab Test Pricing",
    // Double-click a cell to edit in place, as in G5's batch-edit list. Only TAT, Unit Price, Contract Lab
    // and Comment are editable (G5 AllowEdit); Price Code, Matrix, Test, Method, Parameter, LastUpdatedDate
    // and Change % are read-only.
    inlineRowEdit: true,
    // G5 has no per-row pencil on this list (batch inline edit only) — hide the pencil column.
    hideEditPencil: true,
    singleColumn: true,
    sideLabels: true,
    // Shared opt-in: square control corners + plain white editable boxes (no yellow tint).
    squareControls: true,
    fields: [
      { key: "priceCode", label: "Price Code", type: "readonly", defaultValue: "" },
      { key: "testMethod", label: "Test Method", type: "select", required: true, lockOnEdit: true, optionsUrl: "/api/v1/contractlab-test-pricings/test-methods", requiredMessage: "Test Method must not be empty." },
      // Order follows the legacy G5 default detail (BO property order): Test Method · Parameter · TAT ·
      // Unit Price · Contract Lab · Change Percent · Comment.
      // The linked test parameter names, comma-joined (G5 Parameter property). In the list's edit mode a
      // click opens the checkbox popup (G5): the chosen ids are saved to the Testparameter link table.
      { key: "parameters", label: "Parameter", type: "readonly", defaultValue: "", placeholder: "", inlineType: "multiselect", optionsUrl: "/api/v1/contractlab-test-pricings/{id}/parameter-options" },
      { key: "tat", label: "TAT", type: "select", required: true, inlineType: "select", optionsUrl: "/api/v1/contractlab-test-pricings/tats", requiredMessage: "TAT must not be empty." },
      { key: "unitPrice", label: "Unit Price", type: "number", decimals: 2, inlineType: "text" },
      { key: "contractLab", label: "Contract Lab", type: "select", required: true, inlineType: "select", optionsUrl: "/api/v1/contractlab-test-pricings/contract-labs", requiredMessage: "Contract Lab must not be empty." },
      { key: "changePercent", label: "Change %", type: "readonly", defaultValue: "" },
      { key: "comment", label: "Comment", type: "textarea" },
    ],
  },
  // Settings ▸ Test Pricing ▸ Default Pricing (legacy TestPriceSurcharge port). Matrix (required) →
  // Test (cascaded) → Method (cascaded) + Component. IsGroup marks a group row (Method/Component then
  // do not apply — nulled server-side). TAT is a required multi-select of TurnAroundTime names.
  // ChargeType (Test/Parameter) + Priority required. Surcharge(%) · Price · Invoice Code · Remark.
  // PriceCode is read-only, minted on save with the G5 rule. New does NOT open this form: it opens G5's
  // "New" list of unpriced tests (DefaultPricingNewPage, wired in ModuleListView, owner 2026-10-15);
  // these fields are the Edit form.
  "set-test-default-pricing": {
    basePath: "/api/v1/default-pricings",
    idField: "oid",
    formName: "Default Pricing",
    entityLabel: "Default Pricing",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    // Shared opt-in: square control corners + plain white editable boxes (no yellow tint).
    squareControls: true,
    fields: [
      { key: "priceCode", label: "Price Code", type: "readonly", defaultValue: "" },
      // Matrix / Test / Is Group / Method / Component / Charge Type / Priority are locked once the row
      // exists (legacy G5 Edit grid: not editable). Only TAT, Surcharge, Price, Remark and Invoice Code change.
      { key: "matrix", label: "Matrix", type: "select", required: true, lockOnEdit: true, optionsUrl: "/api/v1/default-pricings/matrices", requiredMessage: "Matrix must not be empty." },
      { key: "test", label: "Test", type: "select", dependsOn: "matrix", lockOnEdit: true, optionsUrl: "/api/v1/default-pricings/tests" },
      { key: "isGroup", label: "Is Group", type: "checkbox", lockOnEdit: true },
      { key: "method", label: "Method", type: "select", dependsOn: "test", lockOnEdit: true, optionsUrl: "/api/v1/default-pricings/methods", noneLabel: "— None —" },
      { key: "component", label: "Component", type: "select", lockOnEdit: true, optionsUrl: "/api/v1/default-pricings/components", noneLabel: "— None —" },
      { key: "tat", label: "TAT", type: "multiselect", required: true, optionsUrl: "/api/v1/default-pricings/tats", requiredMessage: "TAT must not be empty." },
      { key: "chargeType", label: "Charge Type", type: "select", required: true, lockOnEdit: true, options: [{ id: "0", name: "Test" }, { id: "1", name: "Parameter" }], requiredMessage: "Charge Type must not be empty." },
      { key: "priority", label: "Priority", type: "select", required: true, lockOnEdit: true, optionsUrl: "/api/v1/default-pricings/priorities", requiredMessage: "Priority must not be empty." },
      { key: "surcharge", label: "Surcharge (%)", type: "number", decimals: 0 },
      { key: "surchargePrice", label: "Price", type: "number", decimals: 2 },
      { key: "invoiceCode", label: "Invoice Code", type: "text", maxLength: 100 },
      { key: "remark", label: "Remark", type: "textarea" },
    ],
  },
  // Settings ▸ Test Pricing ▸ Item Charge (legacy port). ItemName required+unique · Category
  // (required FK) · Units (optional FK) · Unit Price · Description · Remark · Cancel. ItemCode auto
  // "101,102,…" (read-only). Ticking Cancel moves the row to the read-only Canceled Item Charge list
  // (set-itemcharge-canceled has no moduleCrud entry — it is a view-only archive).
  // Settings ▸ Test Pricing ▸ Item Charge ▸ Canceled Item Charge (legacy ItemChargePricing_ListView_Cancaled).
  // The rows come from the ModuleRegistry list (Cancel = True). Toolbar: Delete, Reactivate, Refresh,
  // Export — no New and no Edit, as in G5. Reactivate is a custom button in ModuleListView.
  "set-itemcharge-canceled": {
    basePath: "/api/v1/item-charges",
    idField: "oid",
    formName: "Item Charge",
    entityLabel: "Item Charge",
    plainList: true,
    hideNewButton: true,
    hideEditButton: true,
    // G5 shows a green Reactivate arrow per row in place of an Edit pencil.
    hideEditPencil: true,
    fields: [],
  },
  "set-itemcharge-pricing": {
    basePath: "/api/v1/item-charges",
    idField: "oid",
    formName: "Item Charge",
    entityLabel: "Item Charge",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    // Shared opt-in: square control corners + plain white editable boxes (no yellow tint).
    squareControls: true,
    fields: [
      { key: "itemCode", label: "Item Code", type: "readonly", defaultValue: "AutoGenerate" },
      { key: "itemName", label: "Item Name", type: "text", required: true, maxLength: 100, requiredMessage: "Item Name must not be empty." },
      { key: "category", label: "Category", type: "select", required: true, optionsUrl: "/api/v1/item-charges/categories", requiredMessage: "Category must not be empty." },
      { key: "units", label: "Units", type: "select", optionsUrl: "/api/v1/item-charges/units", noneLabel: "— None —" },
      { key: "unitPrice", label: "Unit Price", type: "number", decimals: 2, required: true, requiredMessage: "Unit Price must not be empty." },
      { key: "description", label: "Description", type: "textarea" },
      { key: "cancel", label: "Cancel", type: "checkbox" },
      { key: "remark", label: "Remark", type: "textarea" },
    ],
  },
  // Settings ▸ Parameter ▸ Parameter Library (legacy dbo.Parameter master). ParameterName required
  // + unique · ParameterCode auto "0001.." (read-only) · Synonym · Formula · CAS · MW · Equivalent ·
  // Boiling/Melting/Flash Point · Analyte Code · Comment · Retire Date · Limit. (MSDS file deferred.)
  "set-parameter-library": {
    basePath: "/api/v1/parameter-library",
    idField: "oid",
    formName: "Parameter Library",
    entityLabel: "Parameter",
    // Search sits on the grid card's own row (beside Export / Maximize), the list-view standard —
    // it stays visible when the grid is maximised. (Was on the action bar 2026-10-13; moved back
    // 2026-10-14, owner request.)
    // Legacy Import File / Template Download (SettingFormCriteriaViewController).
    listImport: {
      importUrl: "/api/v1/parameter-library/import",
      templateUrl: "/api/v1/parameter-library/import-template",
      templateName: "parameter.xlsx",
      dialogTitle: "Test File Upload",
    },
    // No in-row editor (owner request 2026-10-15, reversing the 2026-10-14 row editor): the row icon and the
    // toolbar Edit (one ticked row) open the full Detail View ready to edit; a row click opens it read-only.
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    // Detail-view standard: square, 24px, plain white controls (owner request 2026-10-14).
    squareControls: true,
    fields: [
      { key: "parameterName", label: "Parameter", type: "text", required: true, maxLength: 100, requiredMessage: "'Parameter' must not to be empty." },
      { key: "parameterCode", label: "Parameter Code", type: "readonly", defaultValue: "AutoGenerate" },
      { key: "synonym", label: "Synonym", type: "text", maxLength: 100 },
      { key: "formula", label: "Formula", type: "text", maxLength: 100 },
      { key: "cas", label: "CAS", type: "text", maxLength: 100 },
      { key: "mw", label: "MW", type: "text", maxLength: 100 },
      { key: "equivalent", label: "Equivalent", type: "text", maxLength: 100 },
      { key: "boilingPoint", label: "Boiling Point", type: "text", maxLength: 100 },
      { key: "meltingPoint", label: "Melting Point", type: "text", maxLength: 100 },
      { key: "flashPoint", label: "Flash Point", type: "text", maxLength: 100 },
      // MSDS file upload (legacy dbo.Parameter.MSDS → FileData), after Flash Point (owner request 2026-10-02).
      { key: "msds", label: "MSDS", type: "file", fileNameKey: "msdsFileName", uploadUrl: "/api/v1/parameter-library/file/upload", downloadUrlBase: "/api/v1/parameter-library/file" },
      // G5 Parameter_DetailView order and captions (owner request 2026-10-14, superseding the 2026-10-02
      // Retire Date / Comment swap): Analytecode · Comment · Retire Date. Legacy has no "Limit" control.
      { key: "analytecode", label: "Analytecode", type: "text", maxLength: 100 },
      { key: "comment", label: "Comment", type: "textarea" },
      { key: "retireDate", label: "Retire Date", type: "date" },
    ],
  },
  // Settings ▸ Master Data Initialization ▸ Initialization Templates (legacy port). New/Edit open the
  // BESPOKE MdiTemplatesDetail (ModuleListView routes this key to it) — a header (MDI Version · MDI
  // Name* · Attachment Browse/Load/Reset) + "Import Template Sheets" + an editable Templates grid.
  // hideViewToggle: the list keeps its grid but loses the List/Detail switch; these fields only drive
  // the list's New/Edit/Delete wiring (the detail page does not read them).
  "set-mdi-templates": {
    basePath: "/api/v1/mdi-templates",
    idField: "oid",
    formName: "Initialization Templates",
    entityLabel: "Initialization Template",
    titleField: "mdiName",
    // List/Detail toggle shown (owner request 2026-10-16) — a plain CRUD form (no
    // dialogEdit), so Detail opens the selected/first row in the full-page detail.
    fields: [
      { key: "mdiName", label: "MDI Name", type: "text", required: true, maxLength: 100, requiredMessage: "MDI Name must not be empty." },
      { key: "mdiVersion", label: "MDI Version", type: "text", maxLength: 100 },
    ],
  },
  // Settings ▸ Master Data Initialization ▸ Initialization Log. New opens the BESPOKE MdiLogDetail
  // popup (legacy InitializationLogDetail_DetailView — Date · MDI Name · Template Browse/Import/Reset
  // + the templates grid; Import execution deferred). ModuleListView routes this key to it. basePath
  // points at the log endpoints so the list's Delete soft-deletes the InitializationLog row. The pencil
  // is hidden; these fields only drive the New toolbar wiring (the popup does not read them).
  "set-mdi-log": {
    basePath: "/api/v1/mdi-templates/log",
    idField: "oid",
    formName: "Initialization Log",
    entityLabel: "Initialization Log",
    // dialogEdit keeps the LIST front and renders the popup OVER it (ModuleListView routes this key's
    // dialog to the bespoke MdiLogDetail) — without it the early-return full-page detail blanked the
    // list behind the modal (owner report 2026-10-01, image 308).
    dialogEdit: true,
    // List/Detail toggle HIDDEN (owner request 2026-10-16, reversing the same-day add): this log
    // has no real full-page detail — New/Edit open the MdiLogDetail popup — so the toggle was
    // misleading here. (Initialization Templates keeps its toggle; it IS a plain CRUD detail form.)
    hideViewToggle: true,
    hideEditPencil: true,
    fields: [
      { key: "mdiName", label: "MDI Name", type: "text" },
    ],
  },
  "set-reagent-units": {
    basePath: "/api/v1/reagent-units",
    idField: "oid",
    formName: "Units",
    entityLabel: "Units",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "units",
        label: "Units",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Units must not be empty.",
      },
      { key: "remark", label: "Remark", type: "textarea" },
    ],
  },
  "set-ws-prep-type": {
    basePath: "/api/v1/ws-prep-types",
    idField: "oid",
    formName: "WS Prep Type",
    entityLabel: "WS Prep Type",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "prepType",
        label: "WS Prep Type",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "WS Prep Type must not be empty.",
      },
    ],
  },
  "set-ws-storage-name": {
    basePath: "/api/v1/ws-storage-names",
    idField: "oid",
    formName: "WS Storage Name",
    entityLabel: "WS Storage Name",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "storageName",
        label: "Storage Name",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Storage Name must not be empty.",
      },
      { key: "comments", label: "Comments", type: "textarea" },
      { key: "retireDate", label: "Retire Date", type: "date" },
    ],
  },

  // Settings ▸ Utility ▸ Workflow Status Chain — legacy dbo.StatusDefinition
  // (captioned "Workflow Status Chain"; list filtered BO IS NOT NULL). Module +
  // BO are FK → dbo.DefaultSetting dropdowns (shared /options list). Status is
  // required; StatusID/Index/Progress free (2026-09-12).
  "set-workflow-status-chain": {
    basePath: "/api/v1/workflow-status-chains",
    idField: "oid",
    formName: "Workflow Status Chain",
    entityLabel: "Workflow Status",
    inlineRowEdit: false,
    // Detail-View sideLabels standard (standing rule 12) — label BESIDE its field,
    // 26px controls, tighter row gap; migrated on owner request 2026-09-26. Kept the
    // existing 2-column arrangement (no singleColumn).
    sideLabels: true,
    fields: [
      { key: "statusId", label: "Status ID", type: "text", maxLength: 200 },
      {
        key: "status",
        label: "Status",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Status must not be empty.",
      },
      {
        key: "module",
        label: "Module",
        type: "select",
        optionsUrl: "/api/v1/workflow-status-chains/options",
        noneLabel: "— None —",
      },
      {
        key: "bo",
        label: "BO",
        type: "select",
        optionsUrl: "/api/v1/workflow-status-chains/options",
        noneLabel: "— None —",
      },
      { key: "progress", label: "Progress", type: "number" },
      { key: "processMode", label: "Process Mode", type: "text", maxLength: 200 },
      { key: "process", label: "Process", type: "text", maxLength: 200 },
      { key: "action", label: "Action", type: "text", maxLength: 200 },
      { key: "case", label: "Case", type: "text", maxLength: 200 },
      { key: "remark", label: "Remark", type: "textarea", maxLength: 200, halfWidth: true },
      { key: "description", label: "Description", type: "textarea", maxLength: 200, halfWidth: true },
      // Index hidden (owner 2026-09-26): it is an auto-generated sort position, not
      // user-editable. Auto-assigned on insert, preserved on update (see writer).
    ],
  },

  // Settings ▸ Utility ▸ Sample Tracking ▸ Collectors — legacy dbo.Collector.
  // FullName = EmpFullName FK→Employee (required); ClientName = CustomerName
  // FK→Customer. FirstName required. Bools = checkboxes (2026-09-12).
  "set-collectors": {
    basePath: "/api/v1/collectors",
    idField: "oid",
    formName: "Collectors",
    entityLabel: "Collector",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      // Read-only display of the collector's name (legacy parity). Every live
      // collector has EmpFullName (Employee FK) NULL, so this shows First + Last;
      // it is NOT the editable Employee dropdown it used to be (owner 2026-09-26).
      { key: "fullName", label: "Full Name", type: "readonly" },
      {
        key: "firstName",
        label: "First Name",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "First Name must not be empty.",
      },
      { key: "lastName", label: "Last Name", type: "text", maxLength: 200 },
      {
        key: "customerName",
        label: "Client Name",
        type: "lookup",
        optionsUrl: "/api/v1/collectors/customers",
        lookupColumns: ["Client", "Address", "Phone"],
        noneLabel: "— None —",
      },
      { key: "contactPhone", label: "Contact Phone", type: "text", maxLength: 200 },
      { key: "retiredDate", label: "Retired Date", type: "date" },
      { key: "licenseNumber", label: "License Number", type: "text", maxLength: 128 },
      { key: "comment", label: "Comment", type: "textarea" },
      // Checkbox at the end (owner request 2026-09-26, legacy layout). Is Contractor /
      // Is Default / Don't Show were removed from THIS form; their dbo.Collector
      // columns are PRESERVED on save (the writer no longer overwrites them).
      { key: "isFieldUser", label: "Is Field User", type: "checkbox" },
    ],
  },

  // Settings ▸ Utility ▸ Sample Tracking ▸ Sample Registration — the 3 simple
  // masters (Check Points Setup · Payment Status · Job Inactivation). The other
  // 4 nodes (Sample Matrix Field Setup, Sample Field Configuration, eNotification
  // Content Template, QC Category) are their own forms (2026-09-12).
  "set-check-points-setup": {
    basePath: "/api/v1/check-points",
    idField: "oid",
    formName: "Check Points Setup",
    entityLabel: "Check Point",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "checkPoint",
        label: "Check Point",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Check Point must not be empty.",
      },
      {
        key: "sampleMatrices",
        label: "Sample Matrix",
        type: "multiselect",
        optionsUrl: "/api/v1/check-points/matrices",
        summarySeparator: "; ",
      },
      {
        key: "pickAnswer",
        label: "Pick Answer",
        type: "select",
        options: [
          { id: "0", name: "Yes" },
          { id: "1", name: "No" },
          { id: "2", name: "N/A" },
        ],
      },
      { key: "sort", label: "Sort", type: "number" },
    ],
  },
  // Settings ▸ SDMS ▸ SDMS Formulas — legacy SpreadSheetBuilder_Formula_ListView / _DetailView:
  // New · Edit (pencil) · Delete and a full-page detail (2026-10-09). The SAME table and API as
  // Formula Library (`formulalib`, /api/v1/formula-library) — a second face, not a second writer —
  // so its saves are audited as "Formula Library". Legacy field set and order, Kind as the legacy
  // radio pair, no helper text; detail-view standard (side labels, single column, square boxes).
  "set-sdms-formulas": {
    basePath: "/api/v1/formula-library",
    idField: "oid",
    formName: "Formula Library",
    entityLabel: "SDMS Formulas",
    titleField: "formulaName",
    // Search on its own row above the grid with Export / Maximize — the list-view standard (2026-10-14).
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    squareControls: true,
    fields: [
      { key: "formulaID", label: "Formula ID", type: "readonly" },
      { key: "formulaName", label: "Formula Name", type: "text", required: true, maxLength: 100 },
      { key: "kind", label: "Kind", type: "radio", required: true, defaultValue: "0", optionsUrl: "/api/v1/formula-library/kinds" },
      { key: "apiFunction", label: "Api Function", type: "text" },
      { key: "targetField", label: "Target Field", type: "text", required: true },
      { key: "series", label: "Series", type: "text", required: true, defaultValue: "0" },
      { key: "caption", label: "Caption", type: "text" },
      { key: "isActive", label: "Is Active", type: "checkbox", defaultValue: true },
      { key: "script", label: "Script", type: "textarea", textRows: 22 },
      { key: "description", label: "Description", type: "textarea", textRows: 3 },
    ],
  },
  // Settings ▸ SDMS ▸ Template Types — legacy SpreadSheetBuilder_TemplateType_ListView / _DetailView:
  // tick column, New · Edit · Delete, and a detail with Name + Description (2026-10-08).
  "set-sdms-template-types": {
    basePath: "/api/v1/sdms-template-types",
    idField: "oid",
    formName: "Template Types",
    entityLabel: "Template Types",
    // Search on its own row above the grid with Export / Maximize — the list-view standard (2026-10-14).
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    squareControls: true,
    fields: [
      { key: "name", label: "Name", type: "text", required: true, maxLength: 50, requiredMessage: "Name must not be empty." },
      { key: "description", label: "Description", type: "textarea", maxLength: 1000 },
    ],
  },
  "set-payment-status": {
    basePath: "/api/v1/payment-statuses",
    idField: "oid",
    formName: "Payment Status",
    entityLabel: "Payment Status",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "status",
        label: "Status",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Status must not be empty.",
      },
      { key: "isInvoiceQueue", label: "Is Invoice Queue", type: "checkbox" },
      { key: "default", label: "Default", type: "checkbox" },
    ],
  },
  "set-job-inactivation": {
    basePath: "/api/v1/job-inactivations",
    idField: "oid",
    formName: "Job Inactivation",
    entityLabel: "Job Inactivation",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "action",
        label: "Action",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Action must not be empty.",
      },
      {
        key: "inactivationReason",
        label: "Inactivation Reason",
        type: "textarea",
        required: true,
        requiredMessage: "Inactivation Reason must not be empty.",
      },
    ],
  },
  // eNotification Content Template — legacy is INLINE-editable in the LIST
  // (Subject text + SampleCheckin/Reporting/ContentType/Invoice dropdowns);
  // the DETAIL (New / Edit pencil) additionally carries the rich-text Body.
  // inlineRowEdit:true + inlineType:'select' on the FK/enum fields turns those
  // grid cells into dropdowns (double-click a cell to edit in place), matching
  // legacy. Field keys match the ModuleRegistry list column keys so inline edit
  // binds; the companion *Id columns prefill each select from the FK Oid / enum.
  "set-enotification-template": {
    basePath: "/api/v1/enotification-templates",
    idField: "oid",
    formName: "eNotification Content Template",
    entityLabel: "eNotification Content Template",
    inlineRowEdit: true,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "subject",
        label: "Subject",
        type: "text",
        required: true,
        maxLength: 500,
        requiredMessage: "Subject must not be empty.",
      },
      {
        key: "sampleCheckin",
        label: "SampleCheckin",
        type: "select",
        inlineType: "select",
        optionsUrl: "/api/v1/enotification-templates/samplecheckins",
        noneLabel: "N/A",
      },
      {
        key: "reporting",
        label: "Reporting",
        type: "select",
        inlineType: "select",
        optionsUrl: "/api/v1/enotification-templates/reportings",
        noneLabel: "N/A",
      },
      {
        key: "contentType",
        label: "ContentType",
        type: "select",
        inlineType: "select",
        hideBlankOption: true,
        options: [
          { id: "0", name: "None" },
          { id: "1", name: "SampleCheckin" },
          { id: "2", name: "Report" },
          { id: "3", name: "Invoice" },
        ],
      },
      {
        key: "invoice",
        label: "Invoice",
        type: "select",
        inlineType: "select",
        optionsUrl: "/api/v1/enotification-templates/invoices",
        noneLabel: "N/A",
      },
      { key: "body", label: "Body", type: "richtext" },
    ],
  },

  // Settings ▸ Utility ▸ Sample Sites lookup group (legacy Utility Index 27) —
  // 5 lookups. PWS ID/Name/System/Water are single required+unique fields;
  // Sample Type adds Comment + VisualMatrix (multiselect of dbo.VisualMatrix).
  "set-pws-id": {
    basePath: "/api/v1/pws-ids",
    idField: "oid",
    formName: "PWS ID",
    entityLabel: "PWS ID",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "pwsId",
        label: "PWSID",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "PWS ID must not be empty.",
      },
    ],
  },
  "set-pws-name": {
    basePath: "/api/v1/pws-names",
    idField: "oid",
    formName: "PWS Name",
    entityLabel: "PWS Name",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "pwsName",
        label: "PWSName",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "PWS Name must not be empty.",
      },
    ],
  },
  "set-sample-type": {
    basePath: "/api/v1/sample-types",
    idField: "oid",
    formName: "Sample Type",
    entityLabel: "Sample Type",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "sampleTypeName",
        label: "Sample Type Name",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Sample Type must not be empty",
      },
      {
        key: "visualMatrix",
        label: "Sample Matrix",
        type: "multiselect",
        required: true,
        requiredMessage: "Sample Matrix must not be empty.",
        optionsUrl: "/api/v1/sample-types/matrices",
        summarySeparator: "; ",
      },
      { key: "comment", label: "Comment", type: "textarea", maxLength: 1000 },
    ],
  },
  "set-system-type": {
    basePath: "/api/v1/system-types",
    idField: "oid",
    formName: "System Type",
    entityLabel: "System Type",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "systemType",
        label: "System Type",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "System Type must not be empty.",
      },
    ],
  },
  "set-water-type": {
    basePath: "/api/v1/water-types",
    idField: "oid",
    formName: "Water Type",
    entityLabel: "Water Type",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "waterType",
        label: "Water Type",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Water Type must not be empty.",
      },
    ],
  },

  // Settings ▸ Utility ▸ Sample Login lookup group (legacy Utility Index 28) — 14
  // masters. 10 single-field; Rejection Criteria + Site Source add a Matrix
  // checked-list (multiselect of dbo.VisualMatrix); Sample Status adds Hold /
  // Sampling Hold / Description / Color; User Defined Dropdowns adds Active.
  "set-sl-bare": {
    basePath: "/api/v1/sl-bare",
    idField: "oid",
    formName: "Bare",
    entityLabel: "Bare",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "bares",
        label: "Bare",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Bare must not be empty.",
      },
    ],
  },
  "set-sl-code": {
    basePath: "/api/v1/sl-codes",
    idField: "oid",
    formName: "Code",
    entityLabel: "Code",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "code",
        label: "Code",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Code must not be empty.",
      },
    ],
  },
  "set-sl-component": {
    basePath: "/api/v1/sl-components",
    idField: "oid",
    formName: "Component",
    entityLabel: "Component",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "components",
        label: "Component",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Component must not be empty.",
      },
    ],
  },
  "set-sl-kind": {
    basePath: "/api/v1/sl-kinds",
    idField: "oid",
    formName: "Kind",
    entityLabel: "Kind",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "kind",
        label: "Kind",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Kind must not be empty.",
      },
    ],
  },
  "set-rejection-criteria": {
    basePath: "/api/v1/rejection-criteria",
    idField: "oid",
    formName: "Rejection Criteria",
    entityLabel: "Rejection Criteria",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "rejectionCode",
        label: "Rejection Code",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Rejection Code must not be empty.",
      },
      { key: "description", label: "Description", type: "textarea", maxLength: 1000 },
      {
        key: "matrix",
        label: "Sample Matrix",
        type: "multiselect",
        required: true,
        requiredMessage: "Sample Matrix must not be empty.",
        optionsUrl: "/api/v1/sample-types/matrices",
        summarySeparator: "; ",
      },
    ],
  },
  "set-repeat-location": {
    basePath: "/api/v1/repeat-locations",
    idField: "oid",
    formName: "Repeat Location",
    entityLabel: "Repeat Location",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "repeatLocationName",
        label: "Repeat Location",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Repeat Location must not be empty.",
      },
    ],
  },
  "set-sample-status": {
    basePath: "/api/v1/sample-statuses",
    idField: "oid",
    formName: "Sample Status",
    entityLabel: "Sample Status",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "samplestatus",
        label: "Sample Status",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Sample status must not be empty.",
      },
      { key: "hold", label: "Hold", type: "checkbox" },
      { key: "samplinghold", label: "Sampling Hold", type: "checkbox" },
      { key: "description", label: "Description", type: "textarea" },
      // Color is the legacy `colors` enum stored as a smallint (0..9). The detail
      // showed the raw number; render it as the NAME dropdown (legacy XAF parity),
      // with option ids = the stored int and labels IDENTICAL to the list CASE in
      // ModuleRegistry (None · Blue · … · Yellow). numericValue posts the picked int
      // as a JSON number so it binds to the int? column. hideBlankOption + a None(0)
      // default match the legacy enum editor (no blank; None is 0).
      {
        key: "color",
        label: "Color",
        type: "select",
        numericValue: true,
        hideBlankOption: true,
        defaultValue: "0",
        options: [
          { id: "0", name: "None" },
          { id: "1", name: "Blue" },
          { id: "2", name: "Cyan" },
          { id: "3", name: "Green" },
          { id: "4", name: "Light Blue" },
          { id: "5", name: "Light Green" },
          { id: "6", name: "Light Yellow" },
          { id: "7", name: "Red" },
          { id: "8", name: "Skyblue" },
          { id: "9", name: "Yellow" },
        ],
      },
    ],
  },
  "set-sl-sanitarian": {
    basePath: "/api/v1/sl-sanitarians",
    idField: "oid",
    formName: "Sanitarian",
    entityLabel: "Sanitarian",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "sanitarian",
        label: "Sanitarian",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Sanitarian must not be empty.",
      },
    ],
  },
  "set-sanitarian-temperature": {
    basePath: "/api/v1/sanitarian-temperatures",
    idField: "oid",
    formName: "Sanitarian Temperature",
    entityLabel: "Sanitarian Temperature",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "sanitarianTemp",
        label: "Sanitarian Temp",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Sanitarian Temperature must not be empty.",
      },
    ],
  },
  "set-site-source": {
    basePath: "/api/v1/site-sources",
    idField: "oid",
    formName: "Site Source",
    entityLabel: "Site Source",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "sourceName",
        label: "Source Name",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Source Name must not be empty.",
      },
      {
        key: "matrix",
        label: "Sample Matrix",
        type: "multiselect",
        required: true,
        requiredMessage: "Sample Matrix must not be empty.",
        optionsUrl: "/api/v1/sample-types/matrices",
        summarySeparator: "; ",
      },
    ],
  },
  "set-sl-size": {
    basePath: "/api/v1/sl-sizes",
    idField: "oid",
    formName: "Size",
    entityLabel: "Size",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "size",
        label: "Size",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Size must not be empty.",
      },
    ],
  },
  "set-user-defined-dropdown": {
    basePath: "/api/v1/user-defined-dropdowns",
    idField: "oid",
    formName: "User Defined Dropdowns",
    entityLabel: "User Defined Dropdown",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "udDropdown1",
        label: "User Defined Dropdown",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "User Defined Dropdown must not be empty.",
      },
      { key: "active", label: "Active", type: "checkbox" },
    ],
  },
  "set-sl-air-volume": {
    basePath: "/api/v1/sl-air-volumes",
    idField: "oid",
    formName: "Air Volume",
    entityLabel: "Air Volume",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "airVolume",
        label: "Air Volume",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Air Volume must not be empty.",
      },
    ],
  },
  "set-sl-total-surface-area": {
    basePath: "/api/v1/sl-total-surface-areas",
    idField: "oid",
    formName: "Total Surface Area",
    entityLabel: "Total Surface Area",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "totalSurfaceArea",
        label: "Total Surface Area",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Total Surface Area must not be empty.",
      },
    ],
  },

  // Settings ▸ Utility ▸ Topic (legacy dbo.Topic; Topics is [Size(Unlimited)] → textarea).
  "set-topic": {
    basePath: "/api/v1/topics",
    idField: "oid",
    formName: "Topic",
    entityLabel: "Topic",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "topics",
        label: "Topic",
        type: "textarea",
        required: true,
        requiredMessage: "'Topic' must not be empty.",
      },
    ],
  },

  // Settings ▸ Utility ▸ Reporting — CoverPage NELAP / MSR Category / MSR Water System.
  "set-coverpage-nelap": {
    basePath: "/api/v1/coverpage-nelaps",
    idField: "oid",
    formName: "CoverPage NELAP",
    entityLabel: "CoverPage NELAP",
    inlineRowEdit: false,
    // Detail-View standard (standing rule 12) — label BESIDE its field in a
    // fixed-width, left-aligned column, 26px controls (migrated 2026-09-26).
    // singleColumn so the lone NELAP field is a tidy ≤560px block, not stretched
    // full-page (owner request 2026-09-26: "reduce the input field control").
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "nelap",
        label: "NELAP",
        type: "textarea",
        required: true,
        maxLength: 500,
        requiredMessage: "NELAP must not be empty.",
      },
    ],
  },
  "set-msr-category": {
    basePath: "/api/v1/msr-categories",
    idField: "oid",
    formName: "MSR Category",
    entityLabel: "MSR Category",
    inlineRowEdit: false,
    fields: [
      { key: "categoryID", label: "Category ID", type: "readonly" },
      {
        key: "categoryName",
        label: "Category Name",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Category Name must not be empty.",
      },
      { key: "description", label: "Description", type: "textarea" },
    ],
  },
  // MSR Water System = existing 'msr-water-system' moduleCrud (merged 2026-07-02) — reused.

  // Settings ▸ Utility ▸ Instrument ▸ Instrument Category (legacy LabwareCategory
  // — single CategoryName; only LastUpdated* audit cols).
  "set-instrument-category": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: a single field cannot fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/instrument-categories",
    idField: "oid",
    formName: "Instrument Category",
    entityLabel: "Instrument Category",
    inlineRowEdit: false,
    fields: [
      {
        key: "categoryName",
        label: "Instrument Category",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Instrument Category must not be empty.",
      },
    ],
  },

  // Settings ▸ Utility ▸ Sample Sites (legacy SampleSites — core field set).
  // JSON keys follow the app camelCase policy (first char only): SiteID→siteID,
  // PWSSystemName→pWSSystemName, PWSID→pWSID.
  "set-sample-sites": {
    basePath: "/api/v1/sample-sites",
    idField: "oid",
    formName: "Sample Sites",
    entityLabel: "Sample Site",
    inlineRowEdit: false,
    // Detail-View standard (standing rule 12) — label beside field, 2 columns.
    // columnMajor so the fields fill DOWN the left column then the right, matching
    // the legacy ALPACA Sample Sites detail exactly: ceil(29/2)=15 puts Site ID →
    // Sample Point Type on the LEFT and Site Name Archived → Blended on the RIGHT
    // (the field order below is already that column-major order). Owner request
    // 2026-09-26 ("arrange the controls like this" — legacy screenshot).
    columnMajor: true,
    sideLabels: true,
    // Full legacy detail field set, in the legacy left-then-right column order.
    fields: [
      { key: "siteID", label: "Site ID", type: "readonly" },
      { key: "siteCode", label: "Site Code", type: "text", maxLength: 100 },
      {
        key: "client",
        label: "Client",
        type: "lookup",
        optionsUrl: "/api/v1/sample-sites/clients",
        lookupColumns: ["Client", "Address", "Phone"],
        lookupFill: { address: 1 },
        noneLabel: "N/A",
      },
      { key: "address", label: "Address", type: "textarea", maxLength: 2000 },
      {
        key: "siteName",
        label: "Site Name",
        type: "text",
        required: true,
        maxLength: 4000,
        requiredMessage: "Site Name must not be empty.",
      },
      { key: "projectName", label: "Project Name", type: "text", maxLength: 200 },
      { key: "samplePointID", label: "Sample Point ID", type: "text", maxLength: 100 },
      {
        key: "pwsSystemName",
        label: "PWS System Name",
        type: "select",
        optionsUrl: "/api/v1/sample-sites/pws-names",
        noneLabel: "— None —",
      },
      { key: "samplingAddress", label: "Sampling Address", type: "text", maxLength: 100 },
      {
        key: "systemType",
        label: "System Type",
        type: "select",
        optionsUrl: "/api/v1/sample-sites/system-types",
        noneLabel: "— None —",
      },
      {
        key: "pwsid",
        label: "PWS ID",
        type: "select",
        optionsUrl: "/api/v1/sample-sites/pws-ids",
        noneLabel: "— None —",
      },
      { key: "keyMap", label: "Key Map", type: "text", maxLength: 100 },
      { key: "serviceArea", label: "Service Area", type: "text", maxLength: 100 },
      {
        key: "sampleType",
        label: "Sample Type",
        type: "select",
        optionsUrl: "/api/v1/sample-sites/sample-types",
        noneLabel: "— None —",
      },
      { key: "samplePointType", label: "Sample Point Type", type: "text", maxLength: 100 },
      { key: "siteNameArchived", label: "Site Name Archived", type: "text", maxLength: 100 },
      { key: "monitoringRequirement", label: "Monitoring Requirements", type: "text", maxLength: 100 },
      { key: "facilityID", label: "Facility ID", type: "text", maxLength: 100 },
      { key: "facilityName", label: "Facility Name", type: "text", maxLength: 100 },
      { key: "facilityType", label: "Facility Type", type: "text", maxLength: 100 },
      {
        key: "waterType",
        label: "Water Type",
        type: "select",
        optionsUrl: "/api/v1/sample-sites/water-types",
        noneLabel: "— None —",
      },
      { key: "city", label: "City", type: "text", maxLength: 100 },
      { key: "state", label: "State", type: "text", maxLength: 100 },
      { key: "zipCode", label: "Zip Code", type: "text", maxLength: 100 },
      { key: "latitude", label: "Latitude", type: "text", maxLength: 100 },
      { key: "longitude", label: "Longitude", type: "text", maxLength: 100 },
      { key: "description", label: "Description", type: "textarea", maxLength: 100 },
      { key: "isActive", label: "Is Active", type: "checkbox" },
      { key: "blended", label: "Blended", type: "checkbox" },
    ],
  },

  // ── Settings ▸ Instrument Management (legacy XAF "LabwareSettings", 2026-09-18) ──
  // Labware ("Instrument", legacy dbo.Labware) — LabwareID auto ("IN0001"); the
  // detail field set mirrors Labware_DetailView. Category → LabwareCategory dropdown;
  // Department = Department checked-list. The file Attachment + Uncertainty image are
  // deferred (bespoke upload/HTML UI).
  "set-labware": {
    basePath: "/api/v1/labware",
    idField: "oid",
    formName: "Instrument",
    entityLabel: "Instrument",
    // Legacy XAF row editor: the Edit pencil turns the whole row into editors
    // (InstrumentName / InstrumentNumber / Category / ModelNumber / SerialNumber)
    // with a Save / Cancel pair — matching the editable Labware_ListView. inlineRowEdit
    // keeps the cell editors resolving; pencilRowEdit routes the pencil to the row editor.
    // The wider field set (Department, dates, retire, remarks, …) is reached via
    // check-row → toolbar Edit → full detail.
    inlineRowEdit: true,
    pencilRowEdit: true,
    // G5 Labware_ListView has TWO icon columns: the row editor (pencil) and a second
    // icon that opens the full detail in edit mode (owner request 2026-10-14).
    detailIcon: true,
    detailIconColumn: true,
    // G5 toolbar: New · Edit · Delete · Refresh · PrintLabwareBarcodeBig · PrintLabwareBarcode ·
    // Export to (owner request 2026-10-14); Search on its own row below the bar (owner request 2026-10-16,
    // was on the bar — searchInToolbar removed). The two print buttons render the lab's
    // PrintLabwareBarcode(Big) / PrintLabwareBarcode layouts for the ticked rows and
    // download the PDF (LabwareMaintenanceViewController.LabwareBarcode*_Execute).
    listActions: [
      {
        key: "barcode-big",
        label: "PrintLabwareBarcodeBig",
        icon: "🏷",
        path: "barcode",
        query: "big=true",
        download: "LabwareBarcodeBig.pdf",
        placement: "beforeExport",
        successMessage: "Barcode labels downloaded successfully.",
        noSelectionMessage: "Select at least one CheckBox",
      },
      {
        key: "barcode",
        label: "PrintLabwareBarcode",
        icon: "🏷",
        path: "barcode",
        query: "big=false",
        download: "LabwareBarcode.pdf",
        placement: "beforeExport",
        successMessage: "Barcode labels downloaded successfully.",
        noSelectionMessage: "Select at least one CheckBox",
      },
    ],
    // Column-major 2-column detail matching the legacy Labware_DetailView: the
    // LEFT column runs InstrumentID→Department, the RIGHT column Purchase Date→
    // Remarks. The field order below IS that top-to-bottom, left-then-right order.
    columnMajor: true,
    sideLabels: true,       // AI Standard side-label layout — label beside field, matching legacy (owner request 2026-10-02)
    childGridsTabbed: true, // Certificates + Attachments in ONE tabbed panel, matching legacy (owner request 2026-10-02)
    // De-yellow the editable controls (plain white) + square corners (owner request
    // 2026-10-13, this form only). Read-only grey + required-empty yellow cues kept.
    squareControls: true,
    fields: [
      // ── LEFT column (legacy detail order) ──
      { key: "labwareID", label: "Instrument ID", type: "readonly" },
      {
        key: "assignedName",
        label: "Instrument Name",
        type: "text",
        required: true,
        maxLength: 128,
        requiredMessage: "Instrument name must not to be empty.",
      },
      {
        key: "labwareName",
        label: "Instrument Number",
        type: "text",
        required: true,
        maxLength: 128,
        requiredMessage: "'Instrument Number' must not to be empty.",
      },
      { key: "assetsNumber", label: "Assets Number", type: "text", maxLength: 64 },
      { key: "specification", label: "Specification", type: "text", maxLength: 32 },
      {
        key: "category",
        label: "Category",
        type: "select",
        inlineType: "select",
        optionsUrl: "/api/v1/labware/categories",
        noneLabel: "N/A",
      },
      { key: "manufacturer", label: "Manufacturer", type: "text", maxLength: 64 },
      { key: "factoryNumber", label: "Model Number", type: "text", maxLength: 256 },
      { key: "serialNumber", label: "Serial Number", type: "text", maxLength: 256 },
      { key: "measuringRange", label: "Measuring Range", type: "text", maxLength: 32 },
      { key: "uncertainty", label: "Uncertainty", type: "text", maxLength: 128 },
      {
        key: "department",
        label: "Department",
        type: "multiselect",
        optionsUrl: "/api/v1/labware/departments",
        noneLabel: "Select departments…",
      },
      // ── RIGHT column (legacy detail order) ──
      { key: "purchasingDate", label: "Purchase Date", type: "text", maxLength: 200 },
      { key: "acceptanceDate", label: "Acceptance Date", type: "text", maxLength: 200 },
      { key: "startDate", label: "Start Date", type: "text", maxLength: 400 },
      { key: "location", label: "Location", type: "text", maxLength: 200 },
      { key: "price", label: "Price", type: "text", maxLength: 200 },
      { key: "unitOfMeasure", label: "Unit Of Measure", type: "text", maxLength: 64 },
      // Legacy shows a read-only computed "Active" here; AI persists it as the
      // Labware.IsValid flag (checked = active), defaulted on for a new instrument.
      { key: "isValid", label: "Active", type: "checkbox", defaultValue: true },
      { key: "maintenanceFrequency", label: "Maintenance Frequency", type: "text", maxLength: 200 },
      { key: "retiredDate", label: "Retired Date", type: "date" },
      { key: "retireReason", label: "Retire Reason", type: "textarea", maxLength: 2048 },
      { key: "remarks", label: "Remarks", type: "textarea" },
    ],
    // Certificates child collection (legacy Labware_DetailView "Certificates" tab).
    // New/Edit reuse the set-labware-certificate dialog; the owning instrument is
    // stamped via parentField "labware". New works before the instrument is saved
    // (rows buffer, then POST once the parent id exists). The Attachments tab is a
    // file-upload collection (dbo.Attachment) — a separate follow-up.
    childGrids: [
      {
        title: "Certificates",
        listUrl: "/api/v1/labware-certificates/by-instrument/{id}",
        childKey: "set-labware-certificate",
        parentField: "labware",
        listReturnsFullRecord: true,
        linkable: true,
        linkListUrl: "/api/v1/labware-certificates/linkable/{id}",
        exportable: true,
        // Legacy LabwareCertificate list column order.
        columns: [
          { key: "title", label: "Title", width: 160 },
          { key: "iD", label: "ID", width: 90 },
          { key: "number", label: "Number", width: 120 },
          { key: "categoryName", label: "Category", width: 120 },
          // Two-word captions joined, G5 style (owner request 2026-10-16).
          { key: "dateIssued", label: "DateIssued", width: 115, type: "date" },
          { key: "issuingAgency", label: "IssuingAgency", width: 140 },
          { key: "expirationDate", label: "ExpirationDate", width: 135, type: "date" },
          {
            key: "attachmentFileName",
            label: "Attachment",
            width: 160,
            type: "file",
            downloadUrlBase: "/api/v1/labware-certificates/file",
            fileOidKey: "attachment",
          },
          { key: "active", label: "Active", width: 70 },
          { key: "comment", label: "Comment", width: 160 },
        ],
      },
      {
        title: "Attachments",
        listUrl: "/api/v1/labware-attachments/by-instrument/{id}",
        childKey: "set-labware-attachment",
        parentField: "labware",
        listReturnsFullRecord: true,
        linkable: true,
        linkListUrl: "/api/v1/labware-attachments/linkable/{id}",
        exportable: true,
        columns: [
          { key: "name", label: "Name", width: 200 },
          { key: "categoryName", label: "Category", width: 130 },
          {
            key: "attachmentFileName",
            label: "Attachment",
            width: 180,
            type: "file",
            downloadUrlBase: "/api/v1/labware-attachments/file",
            fileOidKey: "attachment",
          },
          { key: "date", label: "Date", width: 120, type: "date" },
          { key: "operator", label: "Operator", width: 150 },
          { key: "comment", label: "Comment", width: 180 },
        ],
      },
    ],
  },

  // Instrument Certificate (legacy dbo.LabwareCertificate) — belongs to a Labware;
  // Title required+unique, Certificate Number unique. File Attachment deferred.
  "set-labware-certificate": {
    basePath: "/api/v1/labware-certificates",
    idField: "oid",
    formName: "Instrument Certificate",
    entityLabel: "Instrument Certificate",
    inlineRowEdit: false,
    // The standalone Instrument Certificate list is a read-only VIEW (legacy parity):
    // no New/Edit/Delete — certificates are created/edited from the Instrument detail's
    // Certificates tab. Row-click still opens the read-only detail; the child dialog
    // (New/Edit on the Instrument detail) still uses these fields.
    readOnlyList: true,
    // fitAllColumns removed (owner report 2026-10-16): squeezing 14 columns into the screen cut the headers off;
    // the columns now take their own widths and the grid scrolls sideways.
    // 2-column popup matching the legacy Instrument Certificate detail. Opened as a
    // child of the Instrument detail, the owning Instrument field is hidden (stamped
    // via the child grid's parentField "labware"); opened standalone it shows.
    columnMajor: true,
    // AI Standard detail layout (Items reference form, standing rule 12): label BESIDE its field, full-width
    // 50/50 split, 26px controls — owner request 2026-10-16 ("design standards").
    sideLabels: true,
    // Shared opt-in: square control corners + plain white editable boxes (no yellow tint) — owner request 2026-10-16.
    squareControls: true,
    fields: [
      {
        key: "labware",
        label: "Instrument",
        type: "select",
        optionsUrl: "/api/v1/labware-certificates/instruments",
        noneLabel: "— None —",
      },
      {
        key: "title",
        label: "Title",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Title must not be empty.",
      },
      { key: "iD", label: "ID", type: "text", maxLength: 200 },
      { key: "number", label: "CertificateNumber", type: "text", maxLength: 200 },
      {
        key: "attachment",
        label: "Attachment",
        type: "file",
        fileNameKey: "attachmentFileName",
        uploadUrl: "/api/v1/labware-certificates/file/upload",
        downloadUrlBase: "/api/v1/labware-certificates/file",
      },
      { key: "issuingAgency", label: "Issuing Agency", type: "text", maxLength: 200 },
      { key: "dateIssued", label: "Date Issued", type: "date" },
      { key: "expirationDate", label: "Expiration Date", type: "date" },
      { key: "comment", label: "Comment", type: "textarea", maxLength: 200 },
      { key: "active", label: "Active", type: "checkbox" },
    ],
  },

  // Instrument Attachment (legacy shared dbo.Attachment, Labware-Attachments) — the
  // Instrument detail's "Attachments" tab. Child of the Instrument detail; the owning
  // Instrument is stamped via the child grid's parentField "labware" (hidden in the
  // dialog). Name + Category + the file are required. Category is the legacy enum.
  "set-labware-attachment": {
    basePath: "/api/v1/labware-attachments",
    idField: "oid",
    formName: "Instrument Attachment",
    entityLabel: "Attachment",
    inlineRowEdit: false,
    // Legacy Attachment popup is SINGLE column (no columnMajor), field order
    // Attachment · Name · Category · Received Date · Date(ro) · Comment · Operator(ro).
    // The Instrument field is hidden (stamped via the child grid's parentField).
    // Design standard of the detail view: square corners, plain white boxes, no yellow (owner request 2026-10-16).
    squareControls: true,
    fields: [
      {
        key: "labware",
        label: "Instrument",
        type: "select",
        optionsUrl: "/api/v1/labware-certificates/instruments",
        noneLabel: "— None —",
      },
      {
        key: "attachment",
        label: "Attachment",
        type: "file",
        fileNameKey: "attachmentFileName",
        required: true,
        uploadUrl: "/api/v1/labware-attachments/file/upload",
        downloadUrlBase: "/api/v1/labware-attachments/file",
      },
      {
        key: "name",
        label: "Name",
        type: "text",
        required: true,
        maxLength: 400,
        requiredMessage: "'Name must not be empty'.",
      },
      {
        key: "category",
        label: "Category",
        type: "select",
        required: true,
        hideBlankOption: true,
        defaultValue: "0",
        options: [
          { id: "0", name: "COC" },
          { id: "1", name: "Image" },
          { id: "2", name: "Pdf Report" },
          { id: "3", name: "Result EDD" },
          { id: "4", name: "Subout" },
          { id: "5", name: "Other" },
        ],
      },
      { key: "receivedDate", label: "Received Date", type: "date" },
      { key: "date", label: "Date", type: "readonly" },
      { key: "comment", label: "Comment", type: "textarea", maxLength: 400 },
      { key: "operator", label: "Operator", type: "readonly" },
    ],
  },

  // Maintenance Setup (legacy dbo.MaintenanceSetup) — the per-instrument plan.
  // Instrument + Department required, one setup per instrument; Assign To = roles
  // checked-list. The scheduling engine (task generation/queue/rollback) is deferred.
  "set-maintenance-setup": {
    basePath: "/api/v1/maintenance-setups",
    idField: "oid",
    formName: "Maintenance Setup",
    entityLabel: "Maintenance Setup",
    inlineRowEdit: false,
    // 2-column detail matching legacy MaintenanceSetup_DetailView exactly.
    // Column-major over 10 slots (--sd-rows = 5): LEFT = Instrument ID* ·
    // Instrument Name (read-only) · Department* · Assign To · (blank spacer);
    // RIGHT = Retire Date · Retire By (read-only) · Remark · Active · Alert.
    // The trailing spacer keeps the last left cell empty so Alert lands opposite
    // it in the right column, mirroring the legacy layout. The Task Checklist
    // scheduling grid (MaintenanceTaskCheckList Link/Unlink) renders below.
    columnMajor: true,
    sideLabels: true,   // AI Standard side-label layout — label beside field (owner request 2026-10-02)
    taskChecklistGrid: true,
    // Shared opt-in: square control corners + plain white editable boxes (no yellow tint) — owner request 2026-10-16.
    squareControls: true,
    // Header/breadcrumb caption = the Instrument Name; the first field (Instrument ID) is a lookup, so the header
    // showed the setup's raw Oid (owner screenshot 2026-10-16).
    titleField: "instrumentName",
    fields: [
      // Legacy multi-column grid-lookup (InstrumentID · InstrumentNumber ·
      // InstrumentName); picking one auto-fills the read-only Instrument Name.
      {
        key: "instrumentID",
        label: "Instrument ID",
        type: "lookup",
        required: true,
        optionsUrl: "/api/v1/maintenance-setups/instruments-lookup",
        lookupColumns: ["InstrumentID", "InstrumentNumber", "InstrumentName"],
        lookupFill: { instrumentName: 2 },
        noneLabel: "— Select instrument —",
        requiredMessage: "Instrument is required.",
      },
      { key: "instrumentName", label: "Instrument Name", type: "readonly" },
      {
        key: "department",
        label: "Department",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/maintenance-setups/departments",
        noneLabel: "— Select department —",
        requiredMessage: "Department is required.",
      },
      {
        key: "assignTo",
        label: "Assign To",
        type: "multiselect",
        optionsUrl: "/api/v1/maintenance-setups/roles",
        noneLabel: "Select roles…",
      },
      { key: "__spacerLeft", label: "", type: "spacer" },
      { key: "retireDate", label: "Retire Date", type: "date" },
      { key: "retireBy", label: "Retire By", type: "readonly" },
      { key: "remark", label: "Remark", type: "text", maxLength: 200 },
      { key: "active", label: "Active", type: "checkbox" },
      { key: "alert", label: "Alert", type: "checkbox" },
    ],
  },

  // Settings ▸ SDMS ▸ Sequence Setup and Data Center ▸ SDMS Sequence Setup have NO
  // entry here: both are the bespoke master-detail SdmsSequenceSetupPage
  // (ADR-0015), routed in AppShell — Data Center in its view-only mode.

  // Settings ▸ Utility ▸ Routes (legacy Routes — RouteID auto).
  "set-routes": {
    basePath: "/api/v1/routes",
    idField: "oid",
    formName: "Routes",
    entityLabel: "Route",
    inlineRowEdit: false,
    // Detail-View standard (standing rule 12) — label beside field, single column
    // (small master, migrated 2026-09-26).
    singleColumn: true,
    sideLabels: true,
    fields: [
      { key: "routeId", label: "Route ID", type: "readonly" },
      {
        key: "routeName",
        label: "Route Name",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Route Name must not be empty.",
      },
      { key: "description", label: "Description", type: "textarea" },
      { key: "active", label: "Active", type: "checkbox" },
    ],
  },

  // Document Management → Qualifier — legacy dbo.Qualifiers (2026-09-22). Full
  // detail matching the ALPACA Qualifiers_DetailView: Definition + Sample / QC /
  // Surrogate Automation. Single-column layout (legacy stacks every field); the
  // Matrix / Test / Method / Parameter (+ QC Type) fields are cascading
  // multi-selects (Matrix → Test → Method → Parameter). Stored "; "-joined Oids.
  qualifier: {
    basePath: "/api/v1/qualifiers",
    idField: "oid",
    formName: "Qualifier",
    entityLabel: "Qualifier",
    inlineRowEdit: false,
    // Single-column layout (the original / legacy XAF shape): every field stacks
    // top-to-bottom, full-width, left-aligned, with the section headings in order
    // Definition → Sample → QC → Surrogate Automation (user request 2026-09-22).
    singleColumn: true,
    // Let the single column fill the full page width (cover the empty right space)
    // so the controls are wide, not a narrow left block (user request 2026-09-22).
    wideLayout: true,
    // Label to the LEFT of the control (same row), matching the legacy XAF detail
    // (user request 2026-09-22).
    horizontalLabels: true,
    // The auto "General Information" caption is hidden — the "Definition" section
    // field below is the first heading, matching the legacy XAF detail.
    hideSectionHeader: true,
    // Legacy XAF plain-list chrome: breadcrumb (not the List/Detail toggle), no
    // bare row-click → detail, no orange nav focus ring (user request 2026-09-22).
    plainList: true,
    fields: [
      // ── Definition ──
      { key: "__sec_def", label: "Definition", type: "section" },
      { key: "qualifierId", label: "Qualifier ID", type: "readonly", placeholder: "Autogenerated" },
      {
        key: "symbol",
        label: "Symbol",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Symbol must not be empty.",
      },
      {
        key: "definition",
        label: "Definition",
        type: "textarea",
        required: true,
        requiredMessage: "Definition must not be empty.",
      },
      {
        key: "category",
        label: "Definition Category",
        type: "select",
        optionsUrl: "/api/v1/qualifiers/categories",
        noneLabel: "— Select —",
      },
      { key: "comment", label: "Comment", type: "textarea", maxLength: 1000 },

      // ── Sample Automation ──
      { key: "__sec_sample", label: "Sample Automation", type: "section" },
      { key: "sampleFormula", label: "Sample Formula", type: "text" },
      {
        key: "sampleMatrices",
        label: "Sample Matrix",
        type: "multiselect",
        optionsUrl: "/api/v1/qualifiers/matrices",
        summarySeparator: "; ",
        pageSize: 10,
      },
      {
        key: "sampleTest",
        label: "Sample Test",
        type: "multiselect",
        optionsUrl: "/api/v1/qualifiers/sample-tests",
        dependsOn: "sampleMatrices",
        summarySeparator: "; ",
        pageSize: 10,
      },
      {
        key: "sampleMethod",
        label: "Sample Method",
        type: "multiselect",
        optionsUrl: "/api/v1/qualifiers/sample-methods",
        dependsOn: "sampleTest",
        summarySeparator: "; ",
        pageSize: 10,
      },
      {
        key: "sampleParameter",
        label: "Sample Parameter",
        type: "multiselect",
        optionsUrl: "/api/v1/qualifiers/sample-parameters",
        dependsOn: "sampleMethod",
        summarySeparator: "; ",
        pageSize: 10,
      },

      // ── QC Automation ──
      { key: "__sec_qc", label: "QC Automation", type: "section" },
      {
        key: "qcType",
        label: "QC Type",
        type: "multiselect",
        optionsUrl: "/api/v1/qualifiers/qc-types",
        summarySeparator: "; ",
        pageSize: 10,
      },
      { key: "qcFormula", label: "QC Formula", type: "text" },
      {
        key: "qcMatrix",
        label: "QC Matrix",
        type: "multiselect",
        optionsUrl: "/api/v1/qualifiers/matrices",
        summarySeparator: "; ",
        pageSize: 10,
      },
      {
        key: "qcTest",
        label: "QC Test",
        type: "multiselect",
        optionsUrl: "/api/v1/qualifiers/qc-tests",
        dependsOn: "qcMatrix",
        summarySeparator: "; ",
        pageSize: 10,
      },
      {
        key: "qcMethod",
        label: "QC Method",
        type: "multiselect",
        optionsUrl: "/api/v1/qualifiers/qc-methods",
        dependsOn: "qcTest",
        summarySeparator: "; ",
        pageSize: 10,
      },
      {
        key: "qcParameter",
        label: "QC Parameter",
        type: "multiselect",
        optionsUrl: "/api/v1/qualifiers/qc-parameters",
        dependsOn: "qcMethod",
        summarySeparator: "; ",
        pageSize: 10,
      },

      // ── Surrogate Automation ──
      { key: "__sec_sur", label: "Surrogate Automation", type: "section" },
      { key: "surFormula", label: "Sur Formula", type: "text" },
      {
        key: "surMatrices",
        label: "Sur Matrix",
        type: "multiselect",
        optionsUrl: "/api/v1/qualifiers/sur-matrices",
        summarySeparator: "; ",
        pageSize: 10,
      },
      {
        key: "surTest",
        label: "Sur Test",
        type: "multiselect",
        optionsUrl: "/api/v1/qualifiers/sur-tests",
        dependsOn: "surMatrices",
        summarySeparator: "; ",
        pageSize: 10,
      },
      {
        key: "surMethod",
        label: "Sur Method",
        type: "multiselect",
        optionsUrl: "/api/v1/qualifiers/sur-methods",
        dependsOn: "surTest",
        summarySeparator: "; ",
        pageSize: 10,
      },
      {
        key: "surParameter",
        label: "Sur Parameter",
        type: "multiselect",
        optionsUrl: "/api/v1/qualifiers/sur-parameters",
        dependsOn: "surMethod",
        summarySeparator: "; ",
        pageSize: 10,
      },
    ],
  },

  // Settings ▸ Utility ▸ Instrument ▸ Maintenance Setup — six masters.
  "set-maintenance-category": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: two fields, and the legacy Maintenance Setup detail views
    // likewise stack theirs in a 40%-wide group with the rest of the row left empty.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/maintenance-categories",
    idField: "oid",
    formName: "Maintenance Category",
    entityLabel: "Category",
    inlineRowEdit: false,
    fields: [
      {
        key: "categoryName",
        label: "Category Name",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Category Name must not be empty.",
      },
      { key: "description", label: "Description", type: "textarea", maxLength: 100 },
    ],
  },
  "set-freq-code": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: three fields, and the legacy Maintenance Setup detail views
    // likewise stack theirs in a 40%-wide group with the rest of the row left empty.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/freq-codes",
    idField: "oid",
    formName: "Freq Code",
    entityLabel: "Freq Code",
    inlineRowEdit: false,
    fields: [
      {
        key: "frequency",
        label: "Frequency",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Frequency must not be empty.",
      },
      {
        key: "code",
        label: "Code",
        type: "text",
        required: true,
        maxLength: 1,
        requiredMessage: "Code must not be empty.",
      },
      {
        key: "freqId",
        label: "Freq ID",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Freq ID must not be empty.",
      },
    ],
  },
  "set-service-company": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: a single field, and the legacy Maintenance Setup detail views
    // likewise stack theirs in a 40%-wide group with the rest of the row left empty.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/service-companies",
    idField: "oid",
    formName: "Service Company",
    entityLabel: "Service Company",
    inlineRowEdit: false,
    fields: [
      {
        key: "serviceCompanyName",
        label: "Service Company",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Service Company must not be empty.",
      },
    ],
  },
  "set-skip-reason": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: two fields, and the legacy Maintenance Setup detail views
    // likewise stack theirs in a 40%-wide group with the rest of the row left empty.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/skip-reasons",
    idField: "oid",
    formName: "Skip Reason",
    entityLabel: "Skip Reason",
    inlineRowEdit: false,
    fields: [
      {
        key: "reason",
        label: "Reason",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "'Reason' must not be empty.",
      },
      { key: "description", label: "Description", type: "textarea" },
    ],
  },
  "set-task-ownership": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: a single field, and the legacy Maintenance Setup detail views
    // likewise stack theirs in a 40%-wide group with the rest of the row left empty.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/task-ownerships",
    idField: "oid",
    formName: "Task Ownership",
    entityLabel: "Task Ownership",
    inlineRowEdit: false,
    fields: [
      {
        key: "tasksOwnership",
        label: "Task Ownership",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Task Ownership must not be empty.",
      },
    ],
  },
  "set-task-checklist": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: five fields, and the legacy Maintenance Setup detail views
    // likewise stack theirs in a 40%-wide group with the rest of the row left empty.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/task-checklists",
    idField: "oid",
    formName: "Task Checklist",
    entityLabel: "Task Checklist",
    inlineRowEdit: false,
    fields: [
      { key: "taskId", label: "Task ID", type: "readonly" },
      {
        key: "category",
        label: "Category",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/task-checklists/categories",
        noneLabel: "— Select —",
        requiredMessage: "'Category' must not be empty.",
        // Legacy XAF grid-lookup "New" button parity: add a Maintenance Category
        // without leaving this form. POSTs to the Category master's own endpoint.
        quickAdd: {
          createUrl: "/api/v1/maintenance-categories",
          title: "Category",
          icon: "ti-category",
          nameKey: "categoryName",
          fields: [
            {
              key: "categoryName",
              label: "Category Name",
              type: "text",
              required: true,
              maxLength: 100,
              requiredMessage: "Category Name must not be empty.",
            },
            { key: "description", label: "Description", type: "text", maxLength: 100 },
          ],
        },
      },
      { key: "taskDescription", label: "Task/Description", type: "textarea" },
      {
        key: "department",
        label: "Department",
        type: "select",
        optionsUrl: "/api/v1/task-checklists/departments",
        noneLabel: "— None —",
      },
      {
        key: "taskOwnership",
        label: "Ownership",
        type: "select",
        optionsUrl: "/api/v1/task-checklists/ownerships",
        noneLabel: "— None —",
        // Legacy XAF grid-lookup "New" button parity: add a Task Ownership inline.
        quickAdd: {
          createUrl: "/api/v1/task-ownerships",
          title: "Task Ownership",
          icon: "ti-user-cog",
          nameKey: "tasksOwnership",
          fields: [
            {
              key: "tasksOwnership",
              label: "Task Ownership",
              type: "text",
              required: true,
              maxLength: 100,
              requiredMessage: "Task Ownership must not be empty.",
            },
          ],
        },
      },
    ],
  },

  // Settings ▸ Utility ▸ Project Category (legacy ProjectCategory). Detail =
  // Project Category + Description + Non Commercial (enum None/Yes); list shows
  // only Project Category.
  "set-project-category": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: three fields are too few to fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/project-categories",
    idField: "oid",
    formName: "Project Category",
    entityLabel: "Project Category",
    inlineRowEdit: false,
    fields: [
      {
        key: "categoryName",
        label: "Project Category",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Project Category must not be empty.",
      },
      { key: "description", label: "Description", type: "textarea", maxLength: 100 },
      {
        key: "nonCommercial",
        label: "Non Commercial",
        type: "select",
        options: [
          { id: "None", name: "None" },
          { id: "Yes", name: "Yes" },
        ],
        defaultValue: "None",
      },
    ],
  },

  // Settings ▸ Utility ▸ Inspection ▸ Inspection Category (legacy InspectCategory).
  "set-inspection-category": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: legacy InspectCategory_DetailView stacks Name / Last Time /
    // Next Time in a 40%-wide group and fills the rest of the row with an empty
    // Item1 placeholder.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/inspection-categories",
    idField: "oid",
    formName: "Inspection Category",
    entityLabel: "Inspection Category",
    inlineRowEdit: false,
    fields: [
      {
        key: "name",
        label: "Name",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "Name must not be empty.",
      },
      { key: "lastTime", label: "Last Time", type: "date" },
      { key: "nextTime", label: "Next Time", type: "date" },
    ],
  },

  // Settings ▸ Utility ▸ Document Management ▸ Document ▸ Category (legacy DocumentCategory).
  "set-documentcategory": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: two fields are too few to fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/document-categories",
    idField: "oid",
    formName: "Document Category",
    entityLabel: "Document Category",
    inlineRowEdit: false,
    fields: [
      {
        key: "name",
        label: "Name",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Name must not be empty.",
      },
      { key: "description", label: "Description", type: "textarea", maxLength: 100 },
    ],
  },

  // Settings ▸ Utility ▸ e-Data Transfer ▸ Parser Setup (legacy InstrumentSoftware).
  "set-parsersetup": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column even at 9 fields — legacy InstrumentSoftware_DetailView puts every
    // item in InstrumentSoftware_col1 (RelativeSize 40) and leaves col2 (60) holding
    // nothing but an empty Item1 filler, so G5 is a single stack too.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/parser-setups",
    idField: "oid",
    formName: "Parser Setup",
    entityLabel: "Parser Setup",
    inlineRowEdit: false,
    // Field order + set match the legacy InstrumentSoftware_DetailView: Parser ID
    // (autogenerate, read-only) · Parser Name · Software Name* · Software Version ·
    // Instrument · Remark · Retire. OrganizationID / ModelNo / DateRetired are
    // hidden in legacy (Removed in the layout) so they are omitted here; the
    // Raw Data File / Parser Code file-upload artifacts are not yet supported.
    fields: [
      { key: "parserId", label: "Parser ID", type: "readonly" },
      { key: "parserName", label: "Parser Name", type: "text", maxLength: 100 },
      {
        key: "software",
        label: "Software Name",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Software Name must not be empty.",
      },
      { key: "softwareVersion", label: "Software Version", type: "text", maxLength: 100 },
      {
        key: "linkInstruments",
        label: "Instrument",
        type: "multiselect",
        optionsUrl: "/api/v1/parser-setups/instruments",
        summarySeparator: "; ",
      },
      { key: "description", label: "Remark", type: "textarea", maxLength: 100 },
      {
        key: "instrumentFile",
        label: "Raw Data File",
        type: "file",
        fileNameKey: "instrumentFileName",
        uploadUrl: "/api/v1/parser-setups/file/upload",
        downloadUrlBase: "/api/v1/parser-setups/file",
      },
      {
        key: "parserCode",
        label: "Parser Code",
        type: "file",
        fileNameKey: "parserCodeName",
        uploadUrl: "/api/v1/parser-setups/file/upload",
        downloadUrlBase: "/api/v1/parser-setups/file",
      },
      { key: "retire", label: "Retire", type: "checkbox" },
    ],
  },

  // Settings ▸ Utility ▸ e-Data Transfer ▸ Parameter Match (legacy ParameterMatch).
  "set-parametermatch": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: legacy ParameterMatch_DetailView stacks all four items in one
    // group and fills the rest of the row with an ExtraSpace placeholder.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/parameter-matches",
    idField: "oid",
    formName: "Parameter Match",
    entityLabel: "Parameter Match",
    inlineRowEdit: false,
    fields: [
      {
        key: "parameter",
        label: "Parameter",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/parameter-matches/parameters",
        noneLabel: "— Select —",
        requiredMessage: "'Parameter' must not be empty.",
      },
      { key: "matchingParameter", label: "Matching Parameter", type: "text", maxLength: 100 },
      {
        key: "software",
        label: "Software",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/parameter-matches/softwares",
        noneLabel: "— Select —",
        requiredMessage: "'Software' must not be empty.",
      },
      { key: "surrogate", label: "Surrogate", type: "checkbox" },
    ],
  },

  // Settings ▸ Utility ▸ Subout Tracking ▸ Sub Lab Parameter Match (legacy SubLabParameterMatch).
  "set-sublabparammatch": {
    basePath: "/api/v1/sub-lab-parameter-matches",
    idField: "oid",
    formName: "Sub Lab Parameter Match",
    entityLabel: "Sub Lab Parameter Match",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "subContractLab",
        label: "Sub Contract Lab",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/sub-lab-parameter-matches/contract-labs",
        noneLabel: "— Select —",
        requiredMessage: "'Sub Contract Lab' must not be empty.",
      },
      {
        key: "parameter",
        label: "Parameter",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/sub-lab-parameter-matches/parameters",
        noneLabel: "— Select —",
        requiredMessage: "'Parameter' must not be empty.",
      },
      { key: "subLabParameter", label: "Sub Lab Parameter", type: "text", maxLength: 100 },
    ],
  },

  // Settings ▸ Utility ▸ Subout Tracking ▸ Sub Lab Qc Parameter Match (legacy SubLapQCParamMatch).
  "set-sublabqcparammatch": {
    basePath: "/api/v1/sub-lab-qc-parameter-matches",
    idField: "oid",
    formName: "Sub Lab Qc Parameter Match",
    entityLabel: "Sub Lab Qc Parameter Match",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "subContractLab",
        label: "Sub Contract Lab",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/sub-lab-qc-parameter-matches/contract-labs",
        noneLabel: "— Select —",
        requiredMessage: "'Sub Contract Lab' must not be empty.",
      },
      {
        key: "qcType",
        label: "QC Type",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/sub-lab-qc-parameter-matches/qc-types",
        noneLabel: "— Select —",
        requiredMessage: "'QC Type' must not be empty.",
      },
      {
        key: "parameter",
        label: "Parameter",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/sub-lab-qc-parameter-matches/parameters",
        noneLabel: "— Select —",
        requiredMessage: "'Parameter' must not be empty.",
      },
      { key: "subLabQcParameter", label: "Sub Lab Qc Parameter", type: "text", maxLength: 100 },
    ],
  },

  // Settings ▸ Utility ▸ Test Pricing ▸ Item Charge Pricing ▸ Category (legacy ItemChargePricingCategory).
  "set-itemchargepricing-category": {
    basePath: "/api/v1/item-charge-pricing-categories",
    idField: "oid",
    formName: "Item Charge Pricing Category",
    entityLabel: "Item Charge Pricing Category",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "categoryName",
        label: "Category",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "'Category' must not be empty.",
      },
      { key: "description", label: "Description", type: "textarea", maxLength: 100 },
    ],
  },

  // ── Settings ▸ Utility ▸ Test — simple/mild lookup masters (2026-09-17). ──
  "set-accreditation": {
    basePath: "/api/v1/accreditations",
    idField: "oid",
    formName: "Accreditation",
    entityLabel: "Accreditation",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "accreditation",
        label: "Accreditation",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Accrediation must not be empty.",
      },
      { key: "certificateNumber", label: "Certificate Number", type: "text", maxLength: 250 },
      { key: "defaultAccreditation", label: "Default Accreditation", type: "checkbox" },
      {
        key: "statement",
        label: "Statement",
        type: "textarea",
        required: true,
        requiredMessage: "Statement must not be empty.",
      },
    ],
  },
  "set-broughtby": {
    basePath: "/api/v1/brought-by",
    idField: "oid",
    formName: "Brought By",
    entityLabel: "Relinquished By",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "broughtByName",
        label: "Relinquished By",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "'Relinquished By must not be empty'",
      },
      { key: "broughtByCode", label: "Relinquished By Code", type: "text", maxLength: 100 },
      { key: "retiredDate", label: "Retired Date", type: "date" },
      { key: "comment", label: "Comment", type: "textarea", maxLength: 1000 },
    ],
  },
  "set-holdingtime": {
    basePath: "/api/v1/holding-times",
    idField: "oid",
    formName: "Holding Time",
    entityLabel: "Holding Time",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "holdingTime",
        label: "Holding Time",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Holding time must not be empty",
      },
      { key: "hourValueConversion", label: "Hour Value Conversion", type: "number" },
    ],
  },
  "set-matrix": {
    basePath: "/api/v1/matrices",
    idField: "oid",
    formName: "Matrix",
    entityLabel: "Matrix",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "matrixName",
        label: "Matrix",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Matrix must not be empty",
      },
      { key: "matrixCode", label: "Matrix Code", type: "readonly", hint: "Auto-generated on save" },
      { key: "comment", label: "Comment", type: "textarea", maxLength: 1000 },
    ],
  },
  "set-preptypes": {
    basePath: "/api/v1/prep-types",
    idField: "oid",
    formName: "Prep Types",
    entityLabel: "Prep Type",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "samplePrepType",
        label: "Sample Prep Type",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "'Sample Prep Type' must not be empty.",
      },
      { key: "tier", label: "Tier", type: "number" },
      { key: "comment", label: "Comment", type: "textarea", maxLength: 1000 },
    ],
  },
  "set-preservecondition": {
    basePath: "/api/v1/preserve-conditions",
    idField: "oid",
    formName: "Preserve Condition",
    entityLabel: "Preserve Condition",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      { key: "id", label: "ID", type: "text", readOnly: true },
      {
        key: "preserveConditionName",
        label: "Preserve Condition",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Preserve Condition must not be empty.",
      },
      { key: "comment", label: "Comment", type: "textarea", maxLength: 1000 },
    ],
  },
  "set-sample-matrix": {
    basePath: "/api/v1/sample-matrices",
    idField: "oid",
    formName: "Sample Matrix",
    entityLabel: "Sample Matrix",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "visualMatrixName",
        label: "Sample Matrix",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Sample Matrix must not be empty",
      },
      // `readOnly` is not a CrudField property; the read-only field TYPE is what
      // makes a value non-editable (as Method Code etc. already do). Corrected
      // 2026-09-24 — it did not compile.
      { key: "visualMatrixCode", label: "Sample Matrix Code", type: "readonly" },
      {
        key: "matrixName",
        label: "Matrix",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/sample-matrices/matrices",
        noneLabel: "— Select —",
        requiredMessage: "Matrix must not be empty",
      },
      {
        key: "daysSampleKeeping",
        label: "Days Sample Keeping",
        type: "number",
        required: true,
        requiredMessage: "Days Sample Keeping should be greater than 0",
      },
      { key: "isRetired", label: "Is Retired", type: "checkbox" },
      { key: "comment", label: "Comment", type: "textarea", maxLength: 1000 },
    ],
  },
  "set-tabcontrols": {
    basePath: "/api/v1/tab-controls",
    idField: "oid",
    formName: "Tab Controls",
    entityLabel: "Tab Control",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      {
        key: "tabName",
        label: "Tab Control",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "Tab Control  must not be empty",
      },
      { key: "isVisible", label: "Is Visible", type: "checkbox" },
    ],
  },

  // Settings ▸ Utility ▸ UnFollow Settings (legacy singleton dbo.UnFollowSettings).
  "set-unfollow-settings": {
    basePath: "/api/v1/unfollow-settings",
    idField: "oid",
    formName: "UnFollow Settings",
    entityLabel: "UnFollow Settings",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    fields: [
      { key: "unfollowedClient", label: "Unfollowed Client", type: "number" },
      { key: "unfollowedProspect", label: "Unfollowed Prospect", type: "number" },
    ],
  },

  // Settings ▸ Utility ▸ e-Data Transfer ▸ QC Type Match (legacy QCTypeMatch).
  "set-qctypematch": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: two fields, and legacy QCTypeMatch_DetailView likewise pairs its
    // single group with an ExtraSpace placeholder.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/qc-type-matches",
    idField: "oid",
    formName: "QC Type Match",
    entityLabel: "QC Type Match",
    inlineRowEdit: false,
    fields: [
      {
        key: "qcType",
        label: "QC Type",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/qc-type-matches/qc-types",
        noneLabel: "— Select —",
        requiredMessage: "'QC Type' must not be empty.",
      },
      { key: "matchingQcType", label: "Matching QC Type", type: "text", maxLength: 100 },
    ],
  },

  // Settings ▸ Utility ▸ Custom Report Builder ▸ Report Category.
  "set-report-category": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: two fields are too few to fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/report-categories",
    idField: "oid",
    formName: "Report Category",
    entityLabel: "Report Category",
    inlineRowEdit: false,
    fields: [
      { key: "category", label: "Category", type: "text", required: true, maxLength: 200 },
      { key: "description", label: "Description", type: "textarea", maxLength: 1000 },
    ],
  },

  // Settings ▸ Utility ▸ Custom Report Builder ▸ Report Type.
  // DTO key `reportType` maps to the real dbo.ReportType.ReportTypes column.
  "set-report-type": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: two fields are too few to fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/report-types",
    idField: "oid",
    formName: "Report Type",
    entityLabel: "Report Type",
    inlineRowEdit: false,
    fields: [
      { key: "reportType", label: "ReportType", type: "text", required: true, maxLength: 200 },
      { key: "description", label: "Description", type: "textarea", maxLength: 1000 },
    ],
  },

  // Settings ▸ Utility ▸ CRM ▸ Category (legacy CRMNotesCategory).
  "set-crm-category": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: a single field cannot fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/crm-categories",
    idField: "oid",
    formName: "Category",
    entityLabel: "Category",
    inlineRowEdit: false,
    fields: [{ key: "name", label: "Name", type: "text", required: true, maxLength: 100 }],
  },

  // Settings ▸ Utility ▸ Client Request ▸ Request Category (name + Is Test Needed).
  "set-request-category": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: too few fields to fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/request-categories",
    idField: "oid",
    formName: "Request Category",
    entityLabel: "Request Category",
    inlineRowEdit: false,
    fields: [
      { key: "requestCategory", label: "Request Category", type: "text", required: true, maxLength: 100 },
      { key: "isTestNeeded", label: "Is Test Needed", type: "checkbox" },
    ],
  },

  // Settings ▸ Client Request ▸ Client Request Priority.
  // Backed by dbo.AI_ClientRequestPriority through /api/v1/client-request-priorities
  // (stored procedures only). The values the Request Registration Priority dropdown
  // offers. Full-page detail (no dialogEdit — standing rule 12).
  "set-request-priority": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    sideLabels: true,
    basePath: "/api/v1/client-request-priorities",
    idField: "oid",
    formName: "Client Request Priority",
    entityLabel: "Priority",
    inlineRowEdit: false,
    singleColumn: true,
    titleField: "priorityName",
    detailShowDelete: true,
    fields: [
      // 20 = AI_ClientRequest.Priority NVARCHAR(20), which stores the chosen TEXT.
      {
        key: "priorityName",
        label: "Priority",
        type: "text",
        required: true,
        maxLength: 20,
        requiredMessage: "'Priority' must not be empty.",
      },
      { key: "description", label: "Description", type: "text" },
      { key: "sortOrder", label: "Sort Order", type: "number", min: 0 },
    ],
  },

  // Settings ▸ Utility ▸ Client Request ▸ Report Template.
  // Backed by dbo.AI_ClientRequestReportTemplate through
  // /api/v1/client-request-report-templates (stored procedures only). The Service
  // Task "Report template" dropdown lists, one per Settings → Service Type row. A
  // template saves ONLY serviceTypeId (2026-09-19); Service Category is a filter
  // for the type dropdown (prefilled from the type on edit), not a saved value.
  // Full-page detail (no dialogEdit — standing rule 12).
  "set-request-report-template": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    sideLabels: true,
    basePath: "/api/v1/client-request-report-templates",
    idField: "oid",
    formName: "Client Request Report Template",
    entityLabel: "Report Template",
    inlineRowEdit: false,
    singleColumn: true,
    titleField: "reportTemplateName",
    detailShowDelete: true,
    fields: [
      {
        key: "serviceCategoryId",
        label: "Service Category",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/client-request-report-templates/lookups/categories",
        noneLabel: "— Select —",
        requiredMessage: "'Service category' must not be empty.",
      },
      // Cascades from the category: its live Service Type rows. The saved value.
      {
        key: "serviceTypeId",
        label: "Service Type",
        type: "select",
        required: true,
        dependsOn: "serviceCategoryId",
        optionsUrl: "/api/v1/client-request-report-templates/lookups/service-types",
        noneLabel: "— Select —",
        requiredMessage: "'Service type' must not be empty.",
      },
      // 120 = the column size (NVARCHAR(120)).
      {
        key: "reportTemplateName",
        label: "Report Template",
        type: "text",
        required: true,
        maxLength: 120,
        requiredMessage: "'Report template' must not be empty.",
      },
      { key: "description", label: "Description", type: "text" },
      { key: "sortOrder", label: "Sort Order", type: "number", min: 0 },
    ],
  },

  // Settings ▸ Utility ▸ Client Request ▸ Service Category.
  // Backed by dbo.AI_ClientRequestServiceCategory through
  // /api/v1/client-request-service-categories (stored procedures only). A standalone
  // name list: the category code was removed from the form and the table (owner
  // decision 2026-09-17). Full-page detail (no dialogEdit — standing rule 12).
  "set-request-service-category": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    sideLabels: true,
    basePath: "/api/v1/client-request-service-categories",
    idField: "oid",
    formName: "Client Request Service Category",
    entityLabel: "Service Category",
    inlineRowEdit: false,
    singleColumn: true,
    titleField: "categoryName",
    detailShowDelete: true,
    fields: [
      {
        key: "categoryName",
        label: "Service Category",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "'Category name' must not be empty.",
      },
      { key: "description", label: "Description", type: "text" },
      { key: "sortOrder", label: "Sort Order", type: "number", min: 0 },
    ],
  },

  // Settings ▸ Client Request ▸ Client Request Service Type.
  // Backed by dbo.AI_ClientRequestServiceType through
  // /api/v1/client-request-service-types (stored procedures only). A type belongs to
  // a service category; the Category dropdown is served from the C# switchboard
  // (ServiceDefinitions.All) by the controller's lookups/categories endpoint, and
  // the server validates the code against that same list. Full-page detail.
  "set-request-service-type": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    sideLabels: true,
    basePath: "/api/v1/client-request-service-types",
    idField: "oid",
    formName: "Client Request Service Type",
    entityLabel: "Service Type",
    inlineRowEdit: false,
    singleColumn: true,
    titleField: "serviceTypeName",
    detailShowDelete: true,
    fields: [
      // The Settings → Service Category row's Oid (2026-09-19; was the switchboard
      // code) — the options are that form's live rows.
      {
        key: "serviceCategoryId",
        label: "Service Category",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/client-request-service-types/lookups/categories",
        noneLabel: "— Select —",
        requiredMessage: "'Service category' must not be empty.",
      },
      // 120 = the column size; a service stores the type's Oid (AI_ServiceTask.ServiceTypeId).
      {
        key: "serviceTypeName",
        label: "Service Type",
        type: "text",
        required: true,
        maxLength: 120,
        requiredMessage: "'Service type' must not be empty.",
      },
      { key: "description", label: "Description", type: "text" },
      // Position WITHIN the category — the dropdown's order, not alphabetical.
      { key: "sortOrder", label: "Sort Order", type: "number", min: 0 },
    ],
  },

  // Settings ▸ Utility ▸ Client Request ▸ Vehicle.
  // Backed by dbo.AI_ClientRequestVehicle through /api/v1/client-request-vehicles
  // (stored procedures only). The vehicles the Route Tracking "Vehicle" dropdown
  // offers (VAN-01 · VAN-02 · TRK-03). Full-page detail (no dialogEdit — standing rule 12).
  "set-request-vehicle": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    sideLabels: true,
    basePath: "/api/v1/client-request-vehicles",
    idField: "oid",
    formName: "Client Request Vehicle",
    entityLabel: "Vehicle",
    inlineRowEdit: false,
    singleColumn: true,
    titleField: "vehicleName",
    detailShowDelete: true,
    fields: [
      // 30 = AI_Route.VehicleCode NVARCHAR(30), where the chosen vehicle is saved.
      {
        key: "vehicleName",
        label: "Vehicle",
        type: "text",
        required: true,
        maxLength: 30,
        requiredMessage: "'Vehicle' must not be empty.",
      },
      { key: "description", label: "Description", type: "text" },
      { key: "sortOrder", label: "Sort Order", type: "number", min: 0 },
    ],
  },

  // Settings ▸ Utility ▸ Client Request ▸ Proof Type.
  // Backed by dbo.AI_ClientRequestProofType through /api/v1/client-request-proof-types
  // (stored procedures only). The options the Delivery "Proof required" dropdown
  // offers (Signature · Photo · Signature + Photo). Full-page detail (no dialogEdit —
  // standing rule 12).
  "set-request-proof-type": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    sideLabels: true,
    basePath: "/api/v1/client-request-proof-types",
    idField: "oid",
    formName: "Client Request Proof Type",
    entityLabel: "Proof Type",
    inlineRowEdit: false,
    singleColumn: true,
    titleField: "proofTypeName",
    detailShowDelete: true,
    fields: [
      // 200 fits AI_ServiceTaskField.Value NVARCHAR(400), where the chosen value is saved.
      {
        key: "proofTypeName",
        label: "Proof Type",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "'Proof type' must not be empty.",
      },
      { key: "description", label: "Description", type: "text" },
      { key: "sortOrder", label: "Sort Order", type: "number", min: 0 },
    ],
  },

  // Settings ▸ Utility ▸ Client Request ▸ Fulfilment Method.
  // Backed by dbo.AI_ClientRequestFulfilmentMethod through /api/v1/client-request-fulfilment-methods
  // (stored procedures only). The options the Bottle Order "Fulfilment method"
  // dropdown offers (Company Van · Technician Pickup · Client Pickup · FedEx · UPS ·
  // Other Carrier). Full-page detail (no dialogEdit — standing rule 12).
  "set-request-fulfilment-method": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    sideLabels: true,
    basePath: "/api/v1/client-request-fulfilment-methods",
    idField: "oid",
    formName: "Client Request Fulfilment Method",
    entityLabel: "Fulfilment Method",
    inlineRowEdit: false,
    singleColumn: true,
    titleField: "fulfilmentMethodName",
    detailShowDelete: true,
    fields: [
      // 200 fits AI_ServiceTaskField.Value NVARCHAR(400), where the chosen value is saved.
      {
        key: "fulfilmentMethodName",
        label: "Fulfilment Method",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "'Fulfilment method' must not be empty.",
      },
      { key: "description", label: "Description", type: "text" },
      { key: "sortOrder", label: "Sort Order", type: "number", min: 0 },
    ],
  },

  // Settings ▸ Utility ▸ Client Request ▸ Document Type.
  // Backed by dbo.AI_ClientRequestDocumentType through /api/v1/client-request-document-types
  // (stored procedures only). The options the Pickup ▸ Document Pickup "Document
  // type" dropdown offers (Signed COC · Site records · Permit / plans · Other).
  // Full-page detail (no dialogEdit — standing rule 12).
  "set-request-document-type": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    sideLabels: true,
    basePath: "/api/v1/client-request-document-types",
    idField: "oid",
    formName: "Client Request Document Type",
    entityLabel: "Document Type",
    inlineRowEdit: false,
    singleColumn: true,
    titleField: "documentTypeName",
    detailShowDelete: true,
    fields: [
      // 200 fits AI_ServiceTaskField.Value NVARCHAR(400), where the chosen value is saved.
      {
        key: "documentTypeName",
        label: "Document Type",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "'Document type' must not be empty.",
      },
      { key: "description", label: "Description", type: "text" },
      { key: "sortOrder", label: "Sort Order", type: "number", min: 0 },
    ],
  },

  // Settings ▸ Utility ▸ Client Request ▸ Sample Collection.
  // Backed by dbo.AI_ClientRequestSampleCollection through
  // /api/v1/client-request-sample-collections (stored procedures only). The options
  // the Survey "Sample collection" dropdown offers (None · Planned · Determined in
  // field). Full-page detail (no dialogEdit — standing rule 12).
  "set-request-sample-collection": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    sideLabels: true,
    basePath: "/api/v1/client-request-sample-collections",
    idField: "oid",
    formName: "Client Request Sample Collection",
    entityLabel: "Sample Collection",
    inlineRowEdit: false,
    singleColumn: true,
    titleField: "sampleCollectionName",
    detailShowDelete: true,
    fields: [
      // 200 fits AI_ServiceTaskField.Value NVARCHAR(400), where the chosen value is saved.
      {
        key: "sampleCollectionName",
        label: "Sample Collection",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "'Sample collection' must not be empty.",
      },
      { key: "description", label: "Description", type: "text" },
      { key: "sortOrder", label: "Sort Order", type: "number", min: 0 },
    ],
  },

  // Settings ▸ Utility ▸ Client Request ▸ Client Request Survey Template.
  // Backed by dbo.AI_ClientRequestSurveyTemplate through
  // /api/v1/client-request-survey-templates (stored procedures only). The options
  // the Survey "Survey template" dropdown offers. Full-page detail (no dialogEdit —
  // standing rule 12).
  "set-request-survey-template": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    sideLabels: true,
    basePath: "/api/v1/client-request-survey-templates",
    idField: "oid",
    formName: "Client Request Survey Template",
    entityLabel: "Survey Template",
    inlineRowEdit: false,
    singleColumn: true,
    titleField: "surveyTemplateName",
    detailShowDelete: true,
    fields: [
      // 200 fits AI_ServiceTaskField.Value NVARCHAR(400), where the chosen template is saved.
      {
        key: "surveyTemplateName",
        label: "Survey Template",
        type: "text",
        required: true,
        maxLength: 200,
        requiredMessage: "'Survey template' must not be empty.",
      },
      { key: "description", label: "Description", type: "text" },
      { key: "sortOrder", label: "Sort Order", type: "number", min: 0 },
    ],
  },

  // Settings ▸ Utility ▸ Client Request ▸ Turnaround.
  // Backed by dbo.AI_ClientRequestTurnAround through
  // /api/v1/client-request-turnarounds. These are the values the Request
  // Registration Turnaround dropdown offers — until this form landed they were a
  // hardcoded C# constant (ServiceDefinitions.Turnarounds).
  //
  // No `dialogEdit`: standing rule 12 makes the full-page SettingsDetailPage the
  // default, and the popup is an explicit per-form opt-out that needs the form
  // owner's sign-off. This form has none, so it takes the standard detail page.
  "set-request-turnaround": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    sideLabels: true,
    basePath: "/api/v1/client-request-turnarounds",
    idField: "oid",
    formName: "Client Request Turnaround",
    entityLabel: "Turnaround",
    inlineRowEdit: false,
    singleColumn: true,
    titleField: "turnAroundName",
    detailShowDelete: true,
    fields: [
      // maxLength 20 is not arbitrary: AI_ClientRequest.Priority stores the
      // CHOSEN value as NVARCHAR(20) text (not an FK), so a longer name could
      // never round-trip onto a request. The column and the API agree.
      {
        key: "turnAroundName",
        label: "Turnaround",
        type: "text",
        required: true,
        maxLength: 20,
        requiredMessage: "'Turnaround' must not be empty.",
      },
      { key: "description", label: "Description", type: "text" },
      // The dropdown is ordered by this, not alphabetically — the list reads
      // fastest-to-slowest, the way it is said on the telephone.
      { key: "sortOrder", label: "Sort Order", type: "number", min: 0 },
    ],
  },

  // Settings ▸ Utility ▸ CMDP ▸ CMDP Sample Category.
  "set-cmdp-sample-category": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-25), which the rule requires over a silent side-effect.
    // ONE column: two fields are too few to fill a 50/50 split.
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/cmdp-sample-categories",
    idField: "oid",
    formName: "CMDP Sample Category",
    entityLabel: "CMDP Sample Category",
    inlineRowEdit: false,
    fields: [
      { key: "sampleCategory", label: "Sample Category", type: "text", required: true, maxLength: 100 },
      { key: "description", label: "Description", type: "textarea", maxLength: 500 },
    ],
  },

  // Settings ▸ Utility ▸ CMDP ▸ CMDP Method and Param Match.  Mirrors ALPACA
  // CMDPMethodParamMatch_DetailView: a Sample Category, a Test picked from the
  // multi-column TestMethod_LookupListView_CMDP grid lookup, the Test's derived
  // (read-only) Method Number, and the Test's parameters as a checked list —
  // plus the free-text CMDP method code/name and Is Active.
  //
  // The list had NO entry here, which is why it showed only Refresh · Export to:
  // MODULE_CRUD membership is what gives a Settings list its selection checkbox,
  // row Edit pencil and New/Edit/Delete (owner request 2026-09-25, "add checkbox,
  // edit and actions like G5").
  "set-cmdp-method-param-match": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap.
    // TWO columns: columnMajor's ceil(7/2) split puts Sample Category · Test ·
    // Method Number · Parameter on the left and CMDP Method Code · CMDP Method
    // Name · Is Active on the right — exactly the legacy col1/col2 grouping.
    columnMajor: true,
    sideLabels: true,
    basePath: "/api/v1/cmdp-method-param-matches",
    idField: "oid",
    formName: "CMDP Method and Param Match",
    entityLabel: "CMDP Method and Param Match",
    inlineRowEdit: false,
    fields: [
      {
        key: "cmdpSampleCategoryOid",
        label: "CMDP Sample Category",
        type: "select",
        required: true,
        requiredMessage: "Sample Category is required.",
        optionsUrl: "/api/v1/cmdp-method-param-matches/lookups/sample-categories",
        noneLabel: "— Select sample category —",
      },
      {
        // Legacy ASPxGridLookup over TestMethod_LookupListView_CMDP (Matrix · Test ·
        // Method).  The display column comes first, as in every other AI grid
        // lookup, and the picked row fills the read-only Method Number beside it
        // (cells = [name, ...cols], so Method is index 2).
        key: "testOid",
        label: "Test",
        type: "lookup",
        required: true,
        requiredMessage: "Test is required.",
        optionsUrl: "/api/v1/cmdp-method-param-matches/lookups/tests",
        lookupColumns: ["Test", "Matrix", "Method"],
        lookupFill: { methodNumber: 2 },
        noneLabel: "— Select test —",
      },
      // Derived from the Test (legacy [NonPersistent] + Appearance Enabled=false).
      // Blank, not the read-only default "(auto)": nothing mints a Method Number —
      // it is simply the chosen Test's, so before a Test is picked it is empty, as
      // the legacy detail shows it.
      { key: "methodNumber", label: "Method Number", type: "readonly", placeholder: "" },
      {
        // Legacy EditorAliases.CheckedListBoxEditor over the Test's parameters.
        // Stored as the "; "-joined Parameter-Oid string; the controller folds
        // this id array into it, so the column keeps its legacy shape.
        key: "parameterIds",
        label: "Parameter",
        type: "multiselect",
        required: true,
        requiredMessage: "Parameter is required.",
        optionsUrl: "/api/v1/cmdp-method-param-matches/lookups/parameters",
        dependsOn: "testOid",
        enabledWhen: ["testOid"],
        noneLabel: "Select parameters…",
      },
      { key: "cmdpMethodCode", label: "CMDP Method Code", type: "text", maxLength: 100 },
      { key: "cmdpMethodName", label: "CMDP Method Name", type: "text", maxLength: 250 },
      { key: "isActive", label: "Is Active", type: "checkbox", defaultValue: true },
    ],
  },

  // Settings ▸ Utility ▸ Analysis ▸ Asbestos ▸ Asbestos Fibers.
  "set-asbestos-fibers": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-24), which the rule requires over a silent side-effect.
    // ONE column: too few fields to fill a 50/50 split (owner, 2026-09-24).
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/asbestos-fibers",
    idField: "oid",
    formName: "Asbestos Fibers",
    entityLabel: "Asbestos Fiber",
    inlineRowEdit: false,
    fields: [
      { key: "asbestosFibersName", label: "Asbestos Fibers Name", type: "text", required: true, maxLength: 100 },
    ],
  },

  // Settings ▸ Utility ▸ Analysis ▸ Asbestos ▸ Fiber Type.
  "set-fiber-types": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-24), which the rule requires over a silent side-effect.
    // ONE column: too few fields to fill a 50/50 split (owner, 2026-09-24).
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/fiber-types",
    idField: "oid",
    formName: "Fiber Type",
    entityLabel: "Fiber Type",
    inlineRowEdit: false,
    fields: [{ key: "fiberType", label: "Fiber Type", type: "text", required: true, maxLength: 100 }],
  },

  // Settings ▸ Utility ▸ Analysis ▸ Asbestos ▸ Fiber Type Values (14 optical props).
  "set-fiber-type-values": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-24), which the rule requires over a silent side-effect.
    // 14 fields, so the full 50/50 split is used rather than one column.
    columnMajor: true,
    sideLabels: true,
    basePath: "/api/v1/fiber-type-values",
    idField: "oid",
    formName: "Fiber Type Values",
    entityLabel: "Fiber Type Values",
    inlineRowEdit: false,
    fields: [
      { key: "fiberType", label: "Fiber Type", type: "text", required: true },
      { key: "riAlpha", label: "RIAlpha", type: "text" },
      { key: "gammaPrep", label: "Gamma Prep", type: "text" },
      { key: "riGamma", label: "RIGamma", type: "text" },
      { key: "gammaPara", label: "Gamma Para", type: "text" },
      { key: "pleochroism", label: "Pleochroism", type: "text" },
      { key: "birefringence", label: "Birefringence", type: "text" },
      { key: "extinction", label: "Extinction", type: "text" },
      { key: "elognation", label: "Elognation", type: "text" },
      { key: "parallel", label: "Parallel", type: "text" },
      { key: "perpenducular", label: "Perpenducular", type: "text" },
      { key: "morphology", label: "Morphology", type: "text" },
      { key: "isotropic", label: "Isotropic", type: "text" },
      { key: "ri", label: "RI", type: "text" },
    ],
  },

  // Settings ▸ Utility ▸ Analysis ▸ Asbestos ▸ Materials.
  "set-materials": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-24), which the rule requires over a silent side-effect.
    // ONE column: too few fields to fill a 50/50 split (owner, 2026-09-24).
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/materials",
    idField: "oid",
    formName: "Materials",
    entityLabel: "Material",
    inlineRowEdit: false,
    fields: [{ key: "materialName", label: "Material Name", type: "text", required: true }],
  },

  // Settings ▸ Utility ▸ Analysis ▸ Asbestos ▸ Non-Asbestos Fibers.
  "set-non-asbestos-fibers": {
    // "Non-Asbestos Fiber Name" needs ~150px, exactly the default column width,
    // so it was being ellipsised. Widen the label column for THIS form only.
    horizontalLabelWidth: 190,
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-24), which the rule requires over a silent side-effect.
    // ONE column: too few fields to fill a 50/50 split (owner, 2026-09-24).
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/non-asbestos-fibers",
    idField: "oid",
    formName: "Non-Asbestos Fibers",
    entityLabel: "Non-Asbestos Fiber",
    inlineRowEdit: false,
    fields: [{ key: "nonAsbestosFiberName", label: "Non-Asbestos Fiber Name", type: "text", required: true }],
  },

  // Settings ▸ Utility ▸ Analysis ▸ Asbestos ▸ Non-Fibrous Components.
  // Field key keeps the legacy column spelling (nonFibrousComponenetName).
  "set-non-fibrous-components": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-24), which the rule requires over a silent side-effect.
    // ONE column: too few fields to fill a 50/50 split (owner, 2026-09-24).
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/non-fibrous-components",
    idField: "oid",
    formName: "Non-Fibrous Components",
    entityLabel: "Non-Fibrous Component",
    inlineRowEdit: false,
    fields: [{ key: "nonFibrousComponenetName", label: "Name", type: "text", required: true }],
  },

  // Settings ▸ Utility ▸ Analysis ▸ Asbestos ▸ Other Components.
  "set-other-components": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-24), which the rule requires over a silent side-effect.
    // ONE column: too few fields to fill a 50/50 split (owner, 2026-09-24).
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/other-components",
    idField: "oid",
    formName: "Other Components",
    entityLabel: "Other Component",
    inlineRowEdit: false,
    fields: [{ key: "otherComponentName", label: "Other Component Name", type: "text", required: true }],
  },

  // Settings ▸ Utility ▸ Analysis ▸ Asbestos ▸ Result Range.
  "set-result-range": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-24), which the rule requires over a silent side-effect.
    // ONE column: too few fields to fill a 50/50 split (owner, 2026-09-24).
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/result-range",
    idField: "oid",
    formName: "Result Range",
    entityLabel: "Result Range",
    inlineRowEdit: false,
    fields: [
      { key: "resultOne", label: "Result One", type: "text", required: true },
      { key: "resultTwo", label: "Result Two", type: "text", required: true },
      { key: "deleteDate", label: "Delete Date", type: "date" },
    ],
  },

  // Settings ▸ Utility ▸ Analysis ▸ Asbestos ▸ Unconventional Analysis
  // (legacy DataEntry — FK caption dropdown + Comment + Test).
  "set-unconventional-analysis": {
    // Detail-view field layout standard (standing rule 12, 2026-09-23): label
    // BESIDE its field, fixed-width LEFT-aligned label column, uniform 26px
    // controls, 6px row gap. Migrated as its own reviewed change (owner request
    // 2026-09-24), which the rule requires over a silent side-effect.
    // ONE column: too few fields to fill a 50/50 split (owner, 2026-09-24).
    singleColumn: true,
    sideLabels: true,
    basePath: "/api/v1/unconventional-analysis",
    idField: "oid",
    formName: "Unconventional Analysis",
    entityLabel: "Unconventional Analysis",
    inlineRowEdit: false,
    fields: [
      {
        key: "sourceCaption",
        label: "BO Source Code Caption",
        type: "select",
        optionsUrl: "/api/v1/unconventional-analysis/captions",
        noneLabel: "— None —",
      },
      { key: "comment", label: "Comment", type: "textarea" },
      { key: "test", label: "Test", type: "text" },
    ],
  },

  // ── Settings ▸ Permit Library New ▸ Limit Type ──
  // Backed by dbo.AI_LimitType through /api/v1/limit-types. Same popup shape as
  // its sibling Permit Type New: two fields on New, Active added on Edit.
  "pln-limit-type": {
    basePath: "/api/v1/limit-types",
    idField: "oid",
    formName: "Limit Type",
    entityLabel: "Limit Type",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    titleField: "limitTypeName",
    // dialogEdit removed 2026-09-28 (owner request) — New/Edit now open the shared
    // full-page Detail View (standing rule 12), not a popup.
    detailShowDelete: true,
    fields: [
      {
        key: "limitTypeName",
        label: "Limit Type",
        type: "text",
        required: true,
        maxLength: 100,
        requiredMessage: "'Limit Type' must not be empty.",
      },
      { key: "description", label: "Description", type: "text" },
      // editOnly - a new row is Active by the column default (DF_AI_LimitType_Active),
      // so the New dialog stays two fields, exactly like Permit Type New.
      { key: "active", label: "Active", type: "checkbox", defaultValue: true, editOnly: true },
    ],
  },

  // ── External Data Import — Permit Libraries + Sheet Mapping settings CRUD ──
  "edi-permit-types": {
    basePath: "/api/v1/permit-types",
    idField: "oid",
    formName: "Permit Types",
    entityLabel: "Permit Type",
    fields: [
      { key: "permitType", label: "Permit Type", type: "text", required: true, maxLength: 100 },
      { key: "definition", label: "Definition", type: "textarea" },
      { key: "mcl", label: "MCL", type: "checkbox", hint: "Maximum Contaminant Level" },
      { key: "scl", label: "SCL", type: "checkbox", hint: "Secondary Contaminant Level" },
      { key: "alert", label: "Alert", type: "checkbox", hint: "Custom alert threshold" },
      { key: "mrl", label: "MRL", type: "checkbox", hint: "Maximum Residue Level" },
    ],
  },

  // ── Settings ▸ Permit Library New ▸ Permit Type (Lifecycle v5 rebuild) ──
  // Reuses the EXISTING /api/v1/permit-types controller + dbo.PermitTypes — no
  // second backend (CLAUDE.md standing rule 8). Only the presentation is new.
  //
  // Two editors, matching ALPACA's PermitTypes_DetailView and the prototype's
  // "New Permit Type" dialog. The entity's four limit-class flags (MCL / SCL /
  // Alert / MRL) are [VisibleInDetailView(false)] in ALPACA, so they are absent
  // here too; the API treats an unposted flag as "leave unchanged", so omitting
  // them cannot reset stored values.
  "pln-permit-type": {
    basePath: "/api/v1/permit-types",
    idField: "oid",
    // Audit FormName stays "Permit Types" — the name registered in CLAUDE.md
    // §14b.2 and already present on rows in dbo.AuditData.
    formName: "Permit Types",
    entityLabel: "Permit Type",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    squareControls: true,
    titleField: "permitType",
    detailShowDelete: true,
    // dialogEdit removed 2026-09-28 (owner request) — New/Edit now open the shared
    // full-page Detail View (standing rule 12), reversing the 2026-08-27 popup choice.
    fields: [
      {
        key: "permitType",
        label: "Permit Type",
        type: "text",
        required: true,
        maxLength: 100,
        // ALPACA PermitTypes.cs:44 — [RuleRequiredField] on PermitType.
        requiredMessage: "'Permit Type' must not be empty.",
      },
      // Payload key stays `definition` (API contract / DB column); only the
      // caption is "Description", per ALPACA's ListView and the prototype.
      // Single-line input (not a textarea) to match the prototype's dialog.
      // LEGACY DETAIL (owner request 2026-10-13, PermitTypes_DetailView): the caption is
      // "Definition" and the box is multi-line. The LIST column keeps the legacy list caption
      // "Description".
      { key: "definition", label: "Definition", type: "textarea", textRows: 2 },
      // Real dbo.PermitTypes.Active column (AI_PermitTypes_Active_Column.sql).
      // Independent of the soft-delete flag: an inactive permit type stays in
      // the list and reads "Inactive"; Delete still hides the row entirely.
      // editOnly — the prototype's "New Permit Type" dialog shows only Permit
      // Type + Description; Active appears on the EDIT dialog. A new row is
      // Active anyway (DF_PermitTypes_Active defaults to 1).
      // Not drawn since 2026-10-13 (legacy shows Permit Type + Definition only). hideInLayout keeps
      // it in the record and the payload, so a save never changes a type's Active value.
      { key: "active", label: "Active", type: "checkbox", defaultValue: true, editOnly: true, hideInLayout: true },
    ],
  },

  // ── Settings ▸ Utility ▸ Permit ▸ Operators — editable master ──
  // Real CRUD over dbo.AI_Operator (owner request 2026-09-28: New/Edit/Delete on the
  // Operators screen — it was a static VALUES reference list before). The table is the
  // SINGLE SOURCE OF TRUTH for this list AND the Permit Limit operator dropdown
  // (OperatorsController + PermitSetupController.ListOptionsAsync).
  //   · StoredValue is LOAD-BEARING — written verbatim to dbo.Permitsetup.Operator and
  //     matched by the limit-check engine (the legacy misspelling "GreaterThen" kept,
  //     §5.2). An operator IN USE by a Permit Limit cannot be deleted (API returns 409).
  //   · Kind is a fixed vocabulary (Numeric / Range / Qualitative) — a static select.
  //   · inlineRowEdit:false + singleColumn + sideLabels = the same detail shape as its
  //     sibling masters (Permit Type / Limit Type). basePath is a real endpoint now, so
  //     the shared REST load/save + the Audit chip come back automatically (the
  //     read-only staticListAdapter in ModuleListView fires only for a no-basePath form).
  "pln-operators": {
    basePath: "/api/v1/operators",
    idField: "oid",
    formName: "Operators",
    entityLabel: "Operator",
    inlineRowEdit: false,
    singleColumn: true,
    sideLabels: true,
    titleField: "operatorName",
    detailShowDelete: true,
    fields: [
      {
        key: "operatorName", label: "Operator", type: "text", required: true, maxLength: 100,
        requiredMessage: "'Operator' must not be empty.",
      },
      {
        key: "display", label: "Display", type: "text", required: true, maxLength: 50,
        requiredMessage: "'Display' must not be empty.",
      },
      {
        key: "kind", label: "Kind", type: "select", required: true,
        options: [
          { id: "Numeric", name: "Numeric" },
          { id: "Range", name: "Range" },
          { id: "Qualitative", name: "Qualitative" },
        ],
        hideBlankOption: true,
        defaultValue: "Numeric",
        requiredMessage: "'Kind' must not be empty.",
      },
      {
        key: "storedValue", label: "Stored value", type: "text", required: true, maxLength: 100,
        hint: "Written verbatim to Permit Limits and matched by the limit-check engine — change with care.",
        requiredMessage: "'Stored value' must not be empty.",
      },
      { key: "purpose", label: "What it is for", type: "textarea" },
    ],
  },

  // ── Settings ▸ Permit Library New ▸ Permit Library New ──
  // The SAME field list and the SAME /api/v1/permit-libraries controller as
  // 'edi-permit-library' below — no second backend (standing rule 8). Only the
  // presentation differs: New/Edit open in the shared popup, matching its
  // siblings inside the rebuilt group.
  "pln-permit-library": {
    basePath: "/api/v1/permit-libraries",
    idField: "oid",
    // PRIME THE CHILD DIALOG'S LISTS WHILE THE PERMIT IS BEING READ.
    // This permit form has no cascading select of its own; the prime is here
    // for the Permit Limits dialog below, which otherwise fetched every list
    // from cold on EVERY Edit click - a wave of round trips to a remote
    // database while the user waits and looks at empty dropdowns. Firing it on
    // page load spends that time when the user is reading the permit instead,
    // and cascadeFetch's shared cache carries the answer into the dialog.
    cachePrimeUrl: "/api/v1/permit-setup/cascade-prime",
    cachePrimeDependsOn: ["sampleMatrixId"],
    // Audit FormName stays "Permit Library" — the name already on this table's
    // rows in dbo.AuditData.
    formName: "Permit Library",
    entityLabel: "Permit Library",
    inlineRowEdit: false,
    titleField: "permitName",
    detailShowDelete: true,
    // No 'General Information' caption above the field grid (owner request
    // 2026-08-28) — the record title already names the section.
    hideSectionHeader: true,
    // Two independent stacks of fields, not label/value pairs (owner layout
    // 2026-08-28) — see the field list below.
    columnMajor: true,
    // Label to the LEFT of its control, same row — compact layout (owner
    // request 2026-09-23), replacing the earlier label-above-control stack.
    horizontalLabels: true,
    // The two columns fill the page width 50/50 (owner request 2026-09-23)
    // instead of being capped at the default 940px with dead space beside them.
    wideLayout: true,
    // Detail-view standard (owner request 2026-10-13): square 24px controls, no
    // colour at rest — the same look as the other standard detail forms.
    squareControls: true,
    // LEGACY FIELD SET AND ORDER (owner decision 2026-10-13, "exactly G5, hide extras"): the detail
    // shows legacy Permit Library's ten fields — LEFT Permit ID · Permit Name · Permit Type · Permit
    // Number · Sample Matrix · Effective Date; RIGHT Remark · Alert Subscribe · Email To · Active —
    // so the left column is SIX rows, not the even split. The renewal fields are NOT removed: they
    // carry `hideInLayout`, which keeps them loaded and posted, because this form's save sends only
    // declared fields and a dropped one would arrive NULL and blank its column. To bring the renewal
    // screen back, flip PERMIT_RENEWAL_UI at the top of this file.
    columnRows: 6,
    recordActions: [
      {
        key: "renew",
        label: "Renew Permit",
        icon: "\u21bb",
        path: "renew",
        primary: true,
        confirmTitle: "Renew Permit",
        confirmMessage:
          "This creates a successor permit with a new Permit ID, carries this permit\u2019s " +
          "details and all of its limits forward, and marks this one Renewed. The successor " +
          "opens with no term dates - set them on it, then Activate Renewal. " +
          "Do you want to continue?",
        successMessage: "Renewal {id} started. Set its dates, then Activate Renewal.",
        navigateToField: "oid",
        // The successor is COPIED from the stored row, so unsaved edits here would
        // be carried forward stale. Same reason as Activate Renewal below.
        saveFirst: true,
        // A permit that already points at a successor cannot be renewed again - the
        // server rejects it with 409, and hiding the button says so up front.
        // Reads renewedToName, NOT renewedTo: the detail page only keeps keys that
        // are DECLARED fields, and renewedTo is not one - the predicate would have
        // silently read undefined and shown the button on an already-renewed permit.
        visibleWhen: (v) => PERMIT_RENEWAL_UI && !String(v.renewedToName ?? "").trim(),
      },
      {
        key: "activate-renewal",
        label: "Activate Renewal",
        icon: "\u2713",
        path: "activate-renewal",
        confirmTitle: "Activate Renewal",
        confirmMessage:
          "This completes the renewal: this permit leaves the renewal workflow and " +
          "takes effect on its Effective Date (the permit it was renewed from is " +
          "already Renewed). Do you want to continue?",
        successMessage: "Renewal activated.",
        // The server checks the STORED Effective Date, so the date the user just
        // typed has to be committed first - otherwise activation is refused on a
        // record that visibly has one.
        saveFirst: true,
        // Only on a DRAFT successor: it must be mid-renewal AND actually be the
        // renewal (renewedFrom set). The source now reads 'Renewed' from Stage 1
        // so it already fails the first test; the renewedFrom test is kept because
        // it is the one that still holds for a renewal STARTED under the older
        // behaviour, where the source sat on 'Renewal In Progress' too.
        visibleWhen: (v) => PERMIT_RENEWAL_UI && v.displayStatus === "Renewal In Progress" && !!String(v.renewedFrom ?? "").trim(),
      },
    ],
    childGrids: [
      {
        title: "Permits",
        listUrl: "/api/v1/permit-setup/by-permit/{id}",
        childKey: "pln-permit-limit-row",
        parentField: "permitLibraryId",
        // by-permit and GET permit-setup/{id} both return PermitSetupDetail, so
        // Edit can open from the row this grid already holds.
        listReturnsFullRecord: true,
        // Lets the limit dialog's Units cascade see the permit's Sample Matrix.
        parentContext: ["sampleMatrixId"],
        // Limit Type comes from AI_Permitsetup_LimitType_Columns.sql; Limit3 and
        // the effective range from AI_Permitsetup_Limit3_Effective_Columns.sql.
        // Rows that predate either show blank - honest, since they never recorded
        // a class or a date range.
        // LEGACY COLUMNS AND ORDER (owner decision 2026-10-13): Parameter · Operator · Permit · Units ·
        // Permit2 · Permit2Units, as the legacy "Permits" grid. Synonym, Limit Type, the effective
        // range and Status are no longer columns here; the rows still carry them (the list returns the
        // full record), so nothing stored changes.
        columns: [
          { key: "parameterName", label: "Parameter", width: 260 },
          { key: "operatorDisplay", label: "Operator", width: 140 },
          { key: "permit", label: "Permit", width: 130 },
          { key: "unitsName", label: "Units", width: 130 },
          { key: "permit2", label: "Permit2", width: 130 },
          { key: "permit2UnitsName", label: "Permit2Units", width: 140 },
        ],
        note: "Configured in Permit Library Detail View",
        // The shared list-grid footer instead of the plain record count (owner
        // request 2026-09-28) — a permit's limits run to dozens of rows, and
        // this is the one bar the rest of the app pages with.
        paged: true,
        // Tick any number of rows (owner request 2026-09-23).
        multiSelect: true,
        // Edit opens the checked rows' own CELLS, not a popup.
        pencilRowEdit: true,
      },
    ],
    // NO dialogEdit and NO singleColumn: New/Edit open the shared FULL-PAGE
    // <SettingsDetailPage> in its standard TWO-COLUMN layout (owner request
    // 2026-08-28 - "don't show the popup"), which is also what standing rule 12
    // mandates. Its siblings Permit Type New / Limit Type keep the popup because
    // they are two-field lookups; this form has twelve fields and a detail page
    // suits it far better.
    //
    // Captions follow the Lifecycle v5 prototype's detail view, and the field
    // ORDER is chosen so the two-column grid - which fills left, right, left,
    // right - pairs them the way the prototype does (Permit ID | Remark,
    // Permit Name | Alert Enabled, and so on).
    //
    // FIELDS IN THE PROTOTYPE THAT DO NOT EXIST YET, so are absent here:
    // Renewal Due Days, Expired Permit Action, Renewal Workflow State, Renewed
    // From, Renewed To, Display Status - dbo.PermitLibrary has no such columns
    // (verified: Oid, PermitID, PermitName, PermitType, PermitNumber,
    // SampleMatrix, EffectiveDate, EndDate, Retire, Remark, Active,
    // AlertSubscribe, EmailTo, Subject, BodyOfText). They describe a permit
    // RENEWAL WORKFLOW that needs a schema decision first, along with the
    // "Renew Permit" action and the Permit Limits child grid.
    fields: [
      // COLUMN-MAJOR (see columnMajor above): the first half of this list is the
      // LEFT column top-to-bottom, the second half the RIGHT column.
      //
      // Order is the owner's layout of 2026-09-16: the LEFT column is what the
      // permit IS (identity, classification, notes, status), the RIGHT column is
      // its TERM and renewal in the order those actually happen — effective,
      // expires, what to do once expired, how far ahead renewal is due, where the
      // renewal got to, and what it came from / went to.
      //
      // SIXTEEN fields, so the halfway split lands exactly 8 / 8. Adding a field
      // to one column means adding one to the other, or the split silently moves
      // the last left-hand field across to the right.
      //
      // LEFT column
      // NO PRE-FILLED VALUES ON A NEW PERMIT (owner request 2026-10-13): the form opens blank, as
      // legacy. Permit ID is minted race-safely by the insert and appears once the permit is saved.
      { key: "permitId", label: "Permit ID", type: "readonly", placeholder: "Autogenerate" },
      {
        key: "permitName",
        label: "Permit Name",
        type: "text",
        required: true,
        maxLength: 100,
      },
      // Required; a new permit opens with none chosen (2026-10-13), so the user picks one.
      {
        key: "permitTypeId",
        label: "Permit Type",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/permit-types/options",
      },
      // Notification Recipients (dbo.PermitLibrary.EmailTo) was REMOVED from this
      // form on 2026-09-16 (owner request). Recipients are managed in the
      // Notification Rules, which is what the field's own placeholder said — it
      // showed a caption, never a value anyone edited here.
      //
      // The COLUMN is untouched and still holds its value: AI_PermitLibraryNew_SP
      // now COALESCEs EmailTo, because buildPayload sends only the fields this
      // config declares, so an omitted one arrives as NULL. Without that the
      // removal would have blanked EmailTo on every save — the same trap already
      // recorded for Retire (2026-08-31) and the renewal columns.
      { key: "permitNumber", label: "Permit Number", type: "text" },
      // A new permit opens with no matrix chosen (2026-10-13).
      {
        key: "sampleMatrixId",
        label: "Sample Matrix",
        type: "select",
        optionsUrl: "/api/v1/permit-libraries/matrices",
      },
      // DERIVED, never stored: the COMPUTED column dbo.PermitLibrary.DisplayStatus
      // resolves the precedence on every read, so it is read-only by nature.
      { key: "displayStatus", label: "Display Status", type: "readonly", defaultValue: "Current", hideInLayout: !PERMIT_RENEWAL_UI },
      { key: "effectiveDate", label: "Effective Date", type: "date" },
      //
      // RIGHT column — legacy order
      { key: "remark", label: "Remark", type: "textarea" },
      // dbo.PermitLibrary.AlertSubscribe — legacy caption.
      { key: "alertSubscribe", label: "Alert Subscribe", type: "checkbox" },
      // dbo.PermitLibrary.EmailTo — back on the form with the legacy field set. The save procedure
      // keeps the stored value when this arrives empty, so it can be changed here but not blanked.
      { key: "emailTo", label: "Email To", type: "text", maxLength: 500 },
      { key: "active", label: "Active", type: "checkbox", defaultValue: true },
      //
      // NOT SHOWN (hideInLayout) — the renewal fields, kept in the record so a save preserves them.
      // dbo.PermitLibrary.EndDate - the prototype calls it Expiration Date, and
      // the grid's derived Permit Status keys off it.
      { key: "endDate", label: "Expiration Date", type: "date", hideInLayout: !PERMIT_RENEWAL_UI },
      // No blank option: Block / Warn / Allow are the only valid values and the
      // column defaults to 'Block', so an empty choice would mean nothing.
      {
        key: "expiredPermitAction",
        label: "Expired Permit Action",
        type: "select",
        options: [
          { id: "Block", name: "Block" },
          { id: "Warn", name: "Warn" },
          { id: "Allow", name: "Allow" },
        ],
        defaultValue: "Block",
        hideBlankOption: true,
        hideInLayout: !PERMIT_RENEWAL_UI,
      },
      // Renewal workflow (AI_PermitLibrary_Renewal_Columns.sql).
      // 'number', not 'text': dbo.PermitLibrary.RenewalDueDays is an INT, and a
      // text field posts "90" as a JSON STRING - which will not bind to the int?
      // on PermitLibraryInput, because the API does not enable
      // JsonNumberHandling.AllowReadingFromString. The number type also gives the
      // stepper the design asks for.
      { key: "renewalDueDays", label: "Renewal Due Days", type: "number", min: 0, step: 1, defaultValue: "90", hideInLayout: !PERMIT_RENEWAL_UI },
      // 'None' is a REAL stored value here, not "nothing selected" - so the blank
      // option is suppressed; otherwise the list showed None twice.
      {
        key: "renewalWorkflowState",
        label: "Renewal Workflow State",
        type: "select",
        // THREE STATES, and only three (owner 2026-09-03). This field says where
        // the renewal WORK got to, so it runs None -> In Progress -> Completed.
        // What the permit IS while that happens - 'Renewal In Progress', then
        // 'Renewed' - is Display Status, the computed field in the left column,
        // which maps one vocabulary to the other.
        // 'Renewal Due' was REMOVED from this list (2026-09-03): DisplayStatus
        // now derives it from EndDate - RenewalDueDays, so it is a fact about
        // the calendar, not a decision anyone makes. Offering it here let a user
        // hand-set a state the system computes, which could only ever disagree
        // with the dates. The computed column still HONOURS a stored
        // 'Renewal Due' on an older row - it just can no longer be chosen.
        options: [
          { id: "None", name: "None" },
          { id: "In Progress", name: "In Progress" },
          { id: "Completed", name: "Completed" },
        ],
        defaultValue: "None",
        hideBlankOption: true,
        hideInLayout: !PERMIT_RENEWAL_UI,
      },
      {
        key: "renewedFrom",
        label: "Renewed From",
        type: "select",
        optionsUrl: "/api/v1/permit-libraries/options",
        noneLabel: "N/A",
        hideInLayout: !PERMIT_RENEWAL_UI,
      },
      // Set by the Renew Permit action, so read-only here.
      { key: "renewedToName", label: "Renewed To", type: "readonly", placeholder: "", hideInLayout: !PERMIT_RENEWAL_UI },
    ],
  },

  // ── Permit Limit (child of Permit Library New) ──
  // Drives the New/Edit dialog of the Permit Limits child grid. Reuses the
  // EXISTING /api/v1/permit-setup controller over legacy dbo.Permitsetup — no
  // second backend (standing rule 8).
  //
  // permitLibraryId is NOT a field here: the child grid passes it as the
  // dialog's `fixedValues`, so the row is always stamped with the permit it was
  // created under and cannot be re-parented from its own form.
  "pln-permit-limit-row": {
    basePath: "/api/v1/permit-setup",
    idField: "oid",
    formName: "Permit Setup",
    entityLabel: "Permit",
    // Every Method and Unit list this matrix can need, in ONE request on open
    // (410 rows for the busiest matrix), so choosing a Parameter binds Method
    // and Units from memory rather than waiting on two serial round trips.
    cachePrimeUrl: "/api/v1/permit-setup/cascade-prime",
    cachePrimeDependsOn: ["sampleMatrixId"],
    inlineRowEdit: false,
    // Two columns of three, as the legacy popup.
    columnMajor: true,
    columnRows: 3,
    dialogEdit: true,
    // The legacy window: title bar "Permits", heading "Permit Setup", captions with a colon, an
    // OK button (owner request 2026-10-13).
    dialogTitle: "Permits",
    dialogHeading: "Permit Setup",
    dialogSaveLabel: "OK",
    dialogStandard: true,
    // Popup standard: orange title bar + round close button, OK · Cancel (owner request 2026-10-15).
    dialogAccent: true,
    // Field order follows the prototype's "New Permit Limit" dialog.
    // Parameter and Units stay SELECTS rather than the prototype's free-text
    // boxes: both are real FKs (dbo.Parameter, dbo.Unit), and typing a name that
    // matches no row would silently store nothing.
    // LEGACY "Permit Setup" POPUP (owner decision 2026-10-13, "exactly G5, hide extras"): two
    // columns filled top to bottom — LEFT Parameter · Operator · Permit; RIGHT Units · Permit 2 ·
    // Permit 2 Units. Method, Limit Type, Limit3 and the effective range are NOT removed: they carry
    // `hideInLayout`, so they stay in the record and the payload; Method still auto-selects the first
    // one for the chosen Parameter behind the scenes. PERMIT_RENEWAL_UI brings them all back.
    fields: [
      // LEFT column
      {
        key: "parameterId",
        label: "Parameter",
        type: "select",
        // NOT mandatory (owner decision 2026-10-13): the legacy BO requires only Permit, and the
        // server check was dropped with it. Operator stays required, unmarked — it opens on "<".
        optionsUrl: "/api/v1/permit-setup/parameters",
        dependsOn: "sampleMatrixId",
        noneLabel: "N/A",
        // 260-odd analytes: a short, searchable list instead of a native one that covers the popup.
        comboList: true,
      },
      {
        key: "operator",
        label: "Operator",
        type: "select",
        required: true,
        hideRequiredMark: true,
        optionsUrl: "/api/v1/permit-setup/operators",
        noneLabel: "N/A",
        // Legacy opens a new row on "<", the first operator.
        defaultFirstOption: true,
        comboList: true,
      },
      { key: "permit", label: "Permit", type: "text", required: true, maxLength: 100 },
      // RIGHT column
      {
        key: "unitsId",
        label: "Units",
        type: "select",
        // INDEPENDENT of the other fields (owner request 2026-10-13): the whole Units list, usable
        // before or after Parameter, and never filled or cleared by it — as the legacy popup.
        optionsUrl: "/api/v1/permit-setup/units",
        noneLabel: "N/A",
        comboList: true,
      },
      { key: "permit2", label: "Permit 2", type: "text" },
      // dbo.Permitsetup.Permit2Units — the same whole Units list, also independent.
      {
        key: "permit2UnitsId",
        label: "Permit 2 Units",
        type: "select",
        optionsUrl: "/api/v1/permit-setup/units",
        noneLabel: "N/A",
        comboList: true,
      },
      // NOT SHOWN (hideInLayout) — kept in the record so a save preserves them.
      {
        key: "methodId",
        label: "Method",
        type: "select",
        optionsUrl: "/api/v1/permit-setup/methods",
        dependsOn: ["parameterId", "sampleMatrixId"],
        autoSelectFirst: true,
        autoSelectRequires: ["parameterId"],
        enabledWhen: ["parameterId"],
        noneLabel: "— None —",
        hideInLayout: !PERMIT_RENEWAL_UI,
      },
      {
        key: "limitTypeId",
        label: "Limit Type",
        type: "select",
        optionsUrl: "/api/v1/permit-setup/limit-types",
        noneLabel: "— Select —",
        hideInLayout: !PERMIT_RENEWAL_UI,
      },
      {
        key: "limit3",
        label: "Limit3 — warn at",
        type: "text",
        maxLength: 100,
        placeholder: "optional early-warning threshold",
        hideInLayout: !PERMIT_RENEWAL_UI,
      },
      { key: "effectiveFrom", label: "Effective From", type: "date", hideInLayout: !PERMIT_RENEWAL_UI },
      { key: "effectiveTo", label: "Effective To", type: "date", hideInLayout: !PERMIT_RENEWAL_UI },
    ],
  },

  "edi-permit-library": {
    basePath: "/api/v1/permit-libraries",
    idField: "oid",
    formName: "Permit Library",
    entityLabel: "Permit Library",
    fields: [
      { key: "permitId", label: "Permit ID", type: "readonly", hint: "Auto-generated on save" },
      { key: "permitName", label: "Permit Name", type: "text", required: true, maxLength: 100 },
      {
        key: "permitTypeId",
        label: "Permit Type",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/permit-types/options",
        noneLabel: "— Select —",
      },
      { key: "permitNumber", label: "Permit Number", type: "text" },
      {
        key: "sampleMatrixId",
        label: "Sample Matrix",
        type: "select",
        optionsUrl: "/api/v1/permit-libraries/matrices",
        noneLabel: "— None —",
      },
      { key: "effectiveDate", label: "Effective Date", type: "date" },
      { key: "endDate", label: "End Date", type: "date" },
      { key: "alertSubscribe", label: "Alert Subscribe", type: "checkbox" },
      { key: "emailTo", label: "Email To", type: "text", hint: "Alert recipient(s)" },
      { key: "remark", label: "Remark", type: "textarea" },
      { key: "active", label: "Active", type: "checkbox" },
      { key: "retire", label: "Retire", type: "checkbox", hint: "Retiring clears Active" },
    ],
  },

  "edi-permit-setup": {
    basePath: "/api/v1/permit-setup",
    idField: "oid",
    formName: "Permit Setup",
    entityLabel: "Permit Limit",
    fields: [
      {
        key: "permitLibraryId",
        label: "Permit Library",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/permit-libraries/options",
        noneLabel: "— Select —",
      },
      {
        key: "parameterId",
        label: "Parameter",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/permit-setup/parameters",
        noneLabel: "— Select —",
      },
      {
        key: "operator",
        label: "Operator",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/permit-setup/operators",
        noneLabel: "— Select —",
      },
      { key: "permit", label: "Limit Value", type: "text", required: true, maxLength: 100 },
      {
        key: "unitsId",
        label: "Units",
        type: "select",
        optionsUrl: "/api/v1/permit-setup/units",
        noneLabel: "— None —",
      },
      { key: "permit2", label: "2nd Value", type: "text", hint: "Only for the Inbetween operator" },
      {
        key: "permit2UnitsId",
        label: "2nd Units",
        type: "select",
        optionsUrl: "/api/v1/permit-setup/units",
        noneLabel: "— None —",
      },
    ],
  },

  "edi-sheet-mapping": {
    basePath: "/api/v1/edi-sheet-mapping",
    idField: "oid",
    formName: "EDI Sheet Mapping",
    entityLabel: "Sheet Mapping",
    sideLabels: true,    // AI Standard Form Layout — label beside field (migrated 2026-10-02)
    singleColumn: true,  // one control per row (owner request 2026-10-02)
    // De-yellow + square-corner the detail inputs (owner request 2026-10-12, this form only).
    squareControls: true,
    fields: [
      {
        key: "sheetName",
        label: "Worksheet Name",
        type: "text",
        required: true,
        maxLength: 200,
      },
      {
        key: "fileType",
        label: "File Type",
        type: "select",
        required: true,
        optionsUrl: "/api/v1/edi-sheet-mapping/file-types",
        noneLabel: "— Select —",
      },
      { key: "category", label: "Category", type: "text" },
      { key: "isActive", label: "Active", type: "checkbox" },
    ],
  },
};

// Nav entries that open ANOTHER entry's form (same list, detail, actions) under their own nav caption and
// breadcrumb — mirrors ModuleRegistry.WithAliases on the server. Instrument Management ▸ Instruments is G5
// Labware_ListView, the same list as Settings ▸ Instrument Management ▸ Instrument (owner report 2026-10-16).
MODULE_CRUD["instm-labware"] = MODULE_CRUD["set-labware"]!;
